import { defaultLocale, type Locale } from "./i18n";
import { SEO_PLANS, rm, SEO_PLANS_SGD_MONTHLY, sgd } from "./seo-pricing";
import { agkPreviewDocs } from "@/content/ai-governance/kit";
import { careerRoles } from "@/content/careers";

export interface LocalizedMetaConfig {
  title?: string;
  description?: string;
  keywords?: string;
}

export interface MetaConfig {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogImageType?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  tags?: string[];
  localized?: Partial<Record<Locale, LocalizedMetaConfig>>;
}

export interface MetaConfigs {
  [key: string]: MetaConfig;
}

export const metaConfigs: MetaConfigs = {
  "enterprise/rtx-spark-laptops-malaysia": {
    title: "RTX Spark Laptop Malaysia: Prices & Specs Compared",
    description: "Compare 14 RTX Spark laptops from ASUS, Dell, HP, Lenovo, Microsoft and MSI: Malaysian prices from RM12,687, memory, SSD, display, weight and release dates.",
    keywords: "RTX Spark laptop, NVIDIA RTX Spark laptop Malaysia, RTX Spark laptop price Malaysia, RTX Spark laptop comparison, RTX Spark laptop Singapore, Dell XPS 16 Creator Edition Malaysia price, Lenovo Yoga Pro 9n Malaysia price, HP OmniBook Ultra 16 RTX Spark, Surface Laptop Ultra Malaysia, RTX Spark release date, RTX Spark laptop preorder Malaysia, MSI RTX Spark laptop Malaysia, MSI Prestige N16 Flip AI+ Malaysia, RTX Spark vs DGX Spark, RTX Spark vs Mac Studio, RTX Spark laptop benchmarks, ASUS ProArt P14 vs MSI Prestige N16, RTX Spark N1X Malaysia, ASUS ProArt P14 RTX Spark, ASUS ProArt P16 RTX Spark, 128GB AI laptop Malaysia",
    ogImage: "/images/enterprise/rtx-spark/proart-p14-p16.webp",
    ogImageType: "image/webp",
    ogImageWidth: 1600,
    ogImageHeight: 900,
  },
  // Home page
  home: {
    title: "AI Agents & Agentic AI Development in Malaysia | Anchor Sprint",
    description:
      "Claude-focused agentic AI development in Malaysia & Singapore — enablement, advisory, build, run and HRD Corp claimable training, with Claude Certified Architects and Associates in house.",
    keywords:
      "AI agents Malaysia, agentic AI Malaysia, AI agent development Malaysia, custom AI agents, AI agents Singapore, Claude agents Malaysia, intelligent automation Malaysia, Claude Certified Architect Malaysia",
    localized: {
      ms: {
        title: "Anchor Sprint | Pembangunan Perisian & Penyelesaian AI di Selangor & KL | Disahkan MDEC",
        description:
          "Syarikat pembangunan perisian dan AI disahkan MDEC di Selangor. Aplikasi khusus, LLM peribadi, seni bina awan dan transformasi digital untuk organisasi Malaysia.",
        keywords:
          "ejen AI Malaysia, AI agentik Malaysia, penyelesaian AI khusus Malaysia, LLM peribadi Malaysia, automasi pintar Malaysia, syarikat pembangunan perisian Malaysia, transformasi digital Malaysia, pembangunan perisian Selangor",
      },
      zh: {
        title: "安克极速 | 雪兰莪与吉隆坡软件开发和 AI 解决方案 | MDEC 认证",
        description:
          "安克极速是位于雪兰莪并获得 MDEC 认证的软件开发与 AI 公司，提供定制应用、私有 LLM、云架构和数字化转型服务。",
        keywords:
          "马来西亚 AI 智能体, 马来西亚 AI 代理开发, 定制 AI 解决方案, 私有 LLM 马来西亚, 智能自动化马来西亚, 马来西亚软件开发公司, 马来西亚数字化转型, 雪兰莪软件开发",
      },
    },
    ogImage: "/images/og-default.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
  },


  // Service listing page
  service: {
    title: "Our Services | Software Development, AI & Cloud Solutions Malaysia",
    description:
      "Custom software development, AI solutions, cloud architecture, system integration and digital transformation services for Malaysian businesses.",
    keywords:
      "IT services Malaysia, software development Selangor, AI solutions KL, cloud architecture Malaysia, digital transformation, system integration",
    localized: {
      ms: {
        title: "Perkhidmatan Kami | Pembangunan Perisian, AI & Awan Malaysia",
        description:
          "Terokai rangkaian perkhidmatan Anchor Sprint untuk pembangunan perisian khusus, penyelesaian AI, seni bina awan, integrasi sistem dan transformasi digital.",
        keywords:
          "perkhidmatan IT Malaysia, pembangunan perisian Selangor, penyelesaian AI KL, seni bina awan Malaysia, transformasi digital, integrasi sistem",
      },
      zh: {
        title: "我们的服务 | 马来西亚软件开发、AI 与云解决方案",
        description:
          "了解安克极速的技术服务，包括定制软件开发、AI 解决方案、云架构、系统集成和数字化转型。",
        keywords:
          "马来西亚IT服务, 雪兰莪软件开发, 吉隆坡AI解决方案, 马来西亚云架构, 数字化转型, 系统集成",
      },
    },
    ogImage: "/images/services/abstract-agentic-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "service-details": {
    title: "Service Details | Enterprise Solutions Malaysia",
    description:
      "Explore Anchor Sprint service delivery areas: software development, system integration, digital transformation, cloud architecture, AI & smart manufacturing.",
    keywords:
      "Anchor Sprint services, enterprise solutions Malaysia, AI solutions Shah Alam, digital transformation Selangor, software development Kuala Lumpur",
    ogImage: "/images/services/abstract-agentic-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Butiran Perkhidmatan | Penyelesaian Perusahaan Malaysia",
        description:
          "Bidang perkhidmatan Anchor Sprint: penyelesaian dan penyepaduan AI, pembangunan perisian, penyepaduan sistem, transformasi digital, seni bina awan dan pembuatan pintar.",
        keywords:
          "perkhidmatan Anchor Sprint, penyelesaian perusahaan Malaysia, penyelesaian AI Shah Alam, transformasi digital Selangor, pembangunan perisian Kuala Lumpur",
      },
      zh: {
        title: "服务详情 | 马来西亚企业解决方案",
        description:
          "安克极速的服务领域：AI 解决方案与集成、软件开发、系统集成、数字化转型、云架构与智能制造。",
        keywords:
          "安克极速服务, 马来西亚企业解决方案, 莎阿南 AI 解决方案, 雪兰莪数字化转型, 吉隆坡软件开发",
      },
    },
  },

  "solutions/agent-use-cases": {
    title: "AI Agent Use Cases for Malaysia & Singapore",
    description:
      "A directory of AI agent use cases — the manual workflow, the agentic redesign, how we build it on Claude, and how we prove it works. Certified in the Claude Partner Network.",
    keywords:
      "AI agent use cases, AI agent use cases Malaysia, AI agent for finance, AI agent for F&B Malaysia, AI agent for manufacturing Malaysia, AI agent for retail Malaysia, AI agent for insurance Malaysia, AI agent solutions by industry, WhatsApp order processing agent, product enquiry chatbot Malaysia, AI automation use cases Malaysia, AI agent use cases Singapore, AI agent for F&B Singapore, AI agent for manufacturing Singapore, AI agent for retail Singapore, AI agent for insurance Singapore, product enquiry chatbot Singapore, AI automation use cases Singapore",
    ogImage: "/images/agent-use-cases/credit-evaluation-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Kes Penggunaan Ejen AI untuk Malaysia & Singapura",
        description:
          "Direktori kes penggunaan ejen AI — aliran kerja manual, reka bentuk semula agentik, cara kami membinanya atas Claude, dan cara kami membuktikannya. Diperakui dalam Claude Partner Network.",
        keywords:
          "kes penggunaan ejen AI, kes penggunaan ejen AI Malaysia, ejen AI kewangan, ejen AI F&B Malaysia, ejen AI pembuatan Malaysia, ejen AI runcit Malaysia, ejen AI insurans, penyelesaian ejen AI mengikut industri, ejen pemprosesan pesanan WhatsApp, kes automasi AI Malaysia",
      },
      zh: {
        title: "面向马来西亚与新加坡企业的 AI 智能体应用场景",
        description:
          "AI 智能体应用场景目录——人工流程、智能体化重新设计、如何在 Claude 上构建，以及如何验证。Claude Partner Network 认证合作伙伴。",
        keywords:
          "AI 智能体应用场景, 马来西亚 AI 智能体应用场景, 金融 AI 智能体, 餐饮 F&B AI 智能体, 制造业 AI 智能体, 零售 AI 智能体, 保险 AI 智能体, 按行业的 AI 智能体方案, WhatsApp 订单处理智能体, 马来西亚 AI 自动化案例",
      },
    },
  },
  "solutions/agent-use-cases/credit-evaluation": {
    title: "Credit Evaluation Agent: Automate Loan Document Review",
    description:
      "We design and build a credit evaluation agent that reads and cross-checks loan documents and flags discrepancies — human-in-the-loop, BNM/PDPA-aware. Talk to our team.",
    keywords:
      "credit evaluation agent, credit evaluation automation Malaysia, loan document review AI, AI underwriting document check, automated credit document verification Malaysia, AI agent for lenders, credit evaluation automation Singapore, automated credit document verification Singapore",
    ogImage: "/images/agent-use-cases/credit-evaluation-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen Penilaian Kredit: Automasi Semakan Dokumen Pinjaman",
        description:
          "Kami mereka bentuk dan membina ejen penilaian kredit yang membaca dan menyemak silang dokumen pinjaman serta menandakan percanggahan — manusia-dalam-gelung, mematuhi BNM/PDPA.",
        keywords:
          "ejen penilaian kredit, automasi penilaian kredit Malaysia, AI semakan dokumen pinjaman, semakan dokumen underwriting AI, pengesahan dokumen kredit automatik Malaysia",
      },
      zh: {
        title: "信贷评估智能体：自动化贷款文件审核",
        description:
          "我们设计并构建信贷评估智能体，读取并交叉核对贷款文件、标记差异——人在环路、符合 BNM/PDPA。欢迎与我们的团队沟通。",
        keywords:
          "信贷评估智能体, 马来西亚信贷评估自动化, 贷款文件审核 AI, 承保文件核对 AI, 马来西亚自动信贷文件核验, 面向放贷机构的 AI 智能体",
      },
    },
  },
  "solutions/agent-use-cases/order-processing": {
    title: "WhatsApp Order Processing Agent: Automate Customer Orders",
    description:
      "We design and build a WhatsApp order processing agent that captures items, confirms the total, and drops a clean order into your POS — human-in-the-loop, for Malaysian F&B and retail. Talk to our team.",
    keywords:
      "WhatsApp order processing agent, WhatsApp ordering automation Malaysia, AI order taking agent, automate WhatsApp orders F&B Malaysia, WhatsApp order bot restaurant, AI agent for retail orders Malaysia, WhatsApp ordering automation Singapore, automate WhatsApp orders F&B Singapore, AI agent for retail orders Singapore",
    ogImage: "/images/agent-use-cases/order-processing-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen Pemprosesan Pesanan WhatsApp: Automasi Pesanan Pelanggan",
        description:
          "Kami mereka bentuk dan membina ejen pemprosesan pesanan WhatsApp yang merakam item, mengesahkan jumlah, dan memasukkan pesanan kemas ke POS anda — manusia-dalam-gelung, untuk F&B dan runcit Malaysia.",
        keywords:
          "ejen pemprosesan pesanan WhatsApp, automasi pesanan WhatsApp Malaysia, AI terima pesanan, automasi pesanan WhatsApp F&B Malaysia, bot pesanan WhatsApp restoran, ejen AI pesanan runcit Malaysia",
      },
      zh: {
        title: "WhatsApp 订单处理智能体：自动化客户订单",
        description:
          "我们设计并构建 WhatsApp 订单处理智能体，记录商品、确认总额，并把干净的订单写入你的 POS——人在环路，面向马来西亚餐饮与零售。欢迎与我们的团队沟通。",
        keywords:
          "WhatsApp 订单处理智能体, 马来西亚 WhatsApp 订单自动化, AI 接单智能体, 马来西亚餐饮 WhatsApp 订单自动化, 餐厅 WhatsApp 订单机器人, 马来西亚零售订单 AI 智能体",
      },
    },
  },
  "solutions/agent-use-cases/product-enquiry": {
    title: "WhatsApp Product Enquiry Agent: Instant Answers",
    description: "An AI agent that answers product questions on WhatsApp instantly — availability, specs and price, straight from your live catalogue. We design and build it.",
    keywords: "product enquiry agent, WhatsApp product enquiry Malaysia, AI product Q&A bot, catalogue answering agent, retail WhatsApp AI Malaysia, ecommerce enquiry automation, WhatsApp product enquiry Singapore, retail WhatsApp AI Singapore",
    ogImage: "/images/agent-use-cases/product-enquiry-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen pertanyaan produk",
        description: "Jawab pertanyaan produk serta-merta di WhatsApp — stok, spesifikasi dan harga — daripada katalog anda.",
        keywords: "ejen pertanyaan produk, bot soal jawab produk WhatsApp, AI katalog produk Malaysia, automasi pertanyaan e-dagang",
      },
      zh: {
        title: "产品咨询智能体",
        description: "在 WhatsApp 上即时回答产品问题——库存、规格与价格——全部来自你自己的目录。",
        keywords: "产品咨询智能体, WhatsApp 产品问答, 马来西亚零售 AI, 电商咨询自动化, 目录问答机器人",
      },
    },
  },
  "solutions/agent-use-cases/insurance-claims": {
    title: "Insurance Claims Agent: Faster Claim Checks",
    description: "An AI agent that checks each claim against the policy, report and receipts and flags what doesn't add up — a person still decides. We design and build it.",
    keywords: "insurance claims agent, claims processing automation Malaysia, AI claims verification, takaful claims AI, automated claim document check, insurtech Malaysia, claims processing automation Singapore, insurtech Singapore",
    ogImage: "/images/agent-use-cases/insurance-claims-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen tuntutan insurans",
        description: "Padankan tuntutan dengan polisi, laporan dan resit — dan tandakan apa yang tidak masuk akal.",
        keywords: "ejen tuntutan insurans, automasi pemprosesan tuntutan, AI semakan tuntutan takaful Malaysia, pengesahan dokumen tuntutan",
      },
      zh: {
        title: "保险理赔智能体",
        description: "将理赔与保单、报告和收据逐一比对——并标记出对不上的地方。",
        keywords: "保险理赔智能体, 马来西亚理赔自动化, AI 理赔核验, takaful 理赔 AI, 理赔文件核对",
      },
    },
  },
  "solutions/agent-use-cases/invoice-matching": {
    title: "Invoice Matching Agent: Automated 3-Way PO Match",
    description: "An AI agent that three-way matches your purchase order, delivery order and invoice before payment — catching mismatches automatically. We build it for you.",
    keywords: "invoice matching agent, three-way match automation Malaysia, PO invoice matching AI, accounts payable automation, procurement AI Malaysia, invoice verification agent, three-way match automation Singapore, procurement AI Singapore",
    ogImage: "/images/agent-use-cases/invoice-matching-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen pemadanan invois",
        description: "Padanan tiga hala pesanan belian, nota penghantaran dan invois sebelum bayaran dikeluarkan.",
        keywords: "ejen pemadanan invois, automasi padanan tiga hala, AI invois PO Malaysia, automasi akaun belum bayar",
      },
      zh: {
        title: "发票匹配智能体",
        description: "在付款前完成采购订单、送货单与发票的三方匹配。",
        keywords: "发票匹配智能体, 三方匹配自动化, 马来西亚采购 AI, 应付账款自动化, 发票核验",
      },
    },
  },
  "solutions/agent-use-cases/bank-reconciliation": {
    title: "Bank Reconciliation Agent: Automate the Rec",
    description: "An AI agent that reconciles your bank statement to the cash book both ways, posts bank charges and interest, explains timing differences, and flags real discrepancies — human-approved. We build it for you.",
    keywords: "bank reconciliation automation, bank reconciliation agent, bank reconciliation software, automated bank reconciliation Malaysia, statement to ledger reconciliation, GL reconciliation AI, bank rec automation Singapore, month-end close automation, cash book reconciliation, finance reconciliation AI",
    ogImage: "/images/agent-use-cases/bank-reconciliation-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen rekonsiliasi bank",
        description: "Ejen AI yang merekonsiliasi penyata bank dengan buku tunai dua hala, mencatat caj dan faedah, menjelaskan perbezaan masa, dan menandakan percanggahan sebenar.",
        keywords: "automasi rekonsiliasi bank, ejen rekonsiliasi bank, perisian rekonsiliasi bank, rekonsiliasi penyata ke lejar, automasi penutupan hujung bulan, rekonsiliasi buku tunai",
      },
      zh: {
        title: "银行对账智能体",
        description: "一个将银行对账单与现金账簿双向核对、登记手续费与利息、解释时间性差异并标记真正差异的 AI 智能体。",
        keywords: "银行对账自动化, 银行对账智能体, 银行对账软件, 对账单与总账核对, 月末结账自动化, 现金账簿对账, 财务对账 AI",
      },
    },
  },
  "solutions/agent-use-cases/leave-management": {
    title: "Leave & Shift-Cover Agent: Automate Leave & MC",
    description: "An AI agent that takes leave and MC requests, checks policy and balances, validates the certificate, and finds qualified shift cover — human-approved. We build it for you.",
    keywords: "leave management agent, shift scheduling automation, MC processing AI, leave automation Malaysia, staff scheduling AI, medical certificate automation, HR leave agent, shift cover automation, workforce management AI Singapore, absence management automation",
    ogImage: "/images/agent-use-cases/leave-management-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen cuti & ganti syif",
        description: "Ejen AI yang menerima permohonan cuti dan MC, menyemak polisi dan baki, mengesahkan sijil, dan mencari ganti syif berkelayakan.",
        keywords: "ejen pengurusan cuti, automasi penjadualan syif, AI proses MC, automasi cuti Malaysia, AI penjadualan kakitangan, automasi sijil sakit",
      },
      zh: {
        title: "请假与补班智能体",
        description: "一个接收请假与病假单申请、核查政策与余额、验证证明并寻找合格顶班的 AI 智能体。",
        keywords: "请假管理智能体, 排班自动化, 病假单处理 AI, 马来西亚请假自动化, 员工排班 AI, 病假证明自动化",
      },
    },
  },
  "solutions/agent-use-cases/cash-application": {
    title: "Cash Application Agent: Auto-Apply Receipts",
    description: "An AI agent that matches incoming customer receipts and POS settlements to open invoices and clears them automatically — lump-sums, part-payments, RM/SGD. We build it for you.",
    keywords: "cash application automation, accounts receivable reconciliation, auto cash application AI, receivables matching AI, cash allocation automation, remittance matching AI, AR automation Malaysia, cash application software, apply receipts to invoices, DSO reduction Singapore",
    ogImage: "/images/agent-use-cases/cash-application-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen aplikasi tunai",
        description: "Ejen AI yang memadankan resit bank dan penyelesaian POS dengan invois belum jelas dan menjelaskannya secara automatik.",
        keywords: "automasi aplikasi tunai, rekonsiliasi akaun belum terima, automasi AR Malaysia, padanan resit invois AI, automasi peruntukan tunai",
      },
      zh: {
        title: "现金核销智能体",
        description: "一个把银行收款与 POS 结算匹配到未清发票并自动核销的 AI 智能体。",
        keywords: "现金核销自动化, 应收账款核销, 自动现金核销 AI, 马来西亚应收自动化, 收款匹配 AI, 现金分配自动化",
      },
    },
  },
  "solutions/agent-use-cases/kyc-onboarding": {
    title: "KYC & Onboarding Agent: Automate Checks",
    description: "An AI agent that checks identity documents, proof of address and application details for consistency — BNM/PDPA-aware, human-in-the-loop. We build it for you.",
    keywords: "KYC agent, onboarding automation Malaysia, AI identity verification, eKYC AI, customer onboarding checks, regulated onboarding Malaysia, BNM KYC automation, onboarding automation Singapore, regulated onboarding Singapore",
    ogImage: "/images/agent-use-cases/kyc-onboarding-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen onboarding & KYC",
        description: "Semak bahawa dokumen pengenalan, bukti alamat dan butiran permohonan adalah konsisten.",
        keywords: "ejen KYC, automasi onboarding Malaysia, AI pengesahan identiti, eKYC AI, semakan onboarding pelanggan",
      },
      zh: {
        title: "开户与 KYC 智能体",
        description: "核对身份证件、地址证明与申请信息是否一致。",
        keywords: "KYC 智能体, 马来西亚开户自动化, AI 身份核验, eKYC, 客户开户核对, BNM KYC 自动化",
      },
    },
  },
  "solutions/agent-use-cases/contract-review": {
    title: "Contract Review Agent: Spot Changed Clauses Fast",
    description: "An AI agent that compares a signed contract against your agreed template and surfaces every changed clause with evidence. We design and build it.",
    keywords: "contract review agent, AI contract review Malaysia, clause comparison AI, legal document review automation, contract redlining AI, legaltech Malaysia, AI contract review Singapore, legaltech Singapore",
    ogImage: "/images/agent-use-cases/contract-review-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen semakan kontrak",
        description: "Bandingkan kontrak yang ditandatangani dengan templat yang dipersetujui dan tonjolkan klausa yang berubah.",
        keywords: "ejen semakan kontrak, AI semakan kontrak Malaysia, perbandingan klausa AI, automasi semakan dokumen undang-undang",
      },
      zh: {
        title: "合同审查智能体",
        description: "将已签署的合同与约定的模板对比，标出发生变动的条款。",
        keywords: "合同审查智能体, 马来西亚 AI 合同审查, 条款比对 AI, 法律文件审查自动化, 合同红线",
      },
    },
  },
  "solutions/agent-use-cases/support-triage": {
    title: "Support Triage Agent: Auto-Route Tickets",
    description: "An AI agent that reads, categorises and routes incoming support tickets — resolving routine ones on the spot and escalating the rest. We build it for you.",
    keywords: "support triage agent, ticket routing automation Malaysia, AI customer support triage, helpdesk automation AI, WhatsApp support agent Malaysia, ticket categorisation AI, ticket routing automation Singapore, WhatsApp support agent Singapore",
    ogImage: "/images/agent-use-cases/support-triage-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen triaj sokongan",
        description: "Baca, kategorikan dan halakan tiket masuk, menyelesaikan yang rutin serta-merta.",
        keywords: "ejen triaj sokongan, automasi penghalaan tiket, AI triaj sokongan pelanggan, automasi meja bantuan Malaysia",
      },
      zh: {
        title: "工单分流智能体",
        description: "读取、归类并分派进来的工单，当场解决那些常规问题。",
        keywords: "工单分流智能体, 马来西亚工单路由自动化, AI 客服分流, 帮助台自动化, WhatsApp 客服",
      },
    },
  },
  "solutions/agent-use-cases/knowledge-agent": {
    title: "Internal Knowledge Agent: Staff Q&A from Your Docs",
    description: "An AI agent that answers staff questions from your own SOPs, policies and handbooks — with the source cited, and honest when it doesn't know. We build it for you.",
    keywords: "internal knowledge agent, knowledge base agent Malaysia, AI staff Q&A, internal AI assistant Malaysia, SOP AI agent, RAG agent Malaysia, employee self-service AI, document Q&A agent, knowledge base agent Singapore, internal AI assistant Singapore, RAG agent Singapore",
    ogImage: "/images/agent-use-cases/knowledge-agent-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen pengetahuan dalaman",
        description: "Menjawab soalan kakitangan daripada SOP, dasar dan buku panduan anda sendiri — dengan sumber dipetik, dan jujur apabila ia tidak tahu.",
        keywords: "ejen pengetahuan dalaman, ejen pangkalan pengetahuan Malaysia, AI soal jawab kakitangan, pembantu AI dalaman Malaysia, ejen AI SOP, ejen soal jawab dokumen",
      },
      zh: {
        title: "内部知识智能体",
        description: "从你们自己的 SOP、政策和手册中回答员工问题——并引用来源，遇到不知道时如实说明。",
        keywords: "内部知识智能体, 马来西亚知识库智能体, AI 员工问答, 马来西亚内部 AI 助手, SOP AI 智能体, 文档问答智能体",
      },
    },
  },
  "solutions/agent-use-cases/hr-verification": {
    title: "HR Verification Agent: Credential Checks",
    description: "An AI agent that verifies a candidate's certificates, references and stated history before the offer goes out. We design and build it for you.",
    keywords: "HR verification agent, candidate credential check AI, background verification automation Malaysia, resume verification AI, hiring due diligence AI, HR tech Malaysia, background verification automation Singapore, HR tech Singapore",
    ogImage: "/images/agent-use-cases/hr-verification-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen pengesahan HR",
        description: "Sahkan bahawa sijil, rujukan dan sejarah kerja calon benar-benar sepadan — sebelum tawaran dihantar.",
        keywords: "ejen pengesahan HR, semakan kelayakan calon AI, automasi pengesahan latar belakang Malaysia, pengesahan resume AI",
      },
      zh: {
        title: "人力资源核实智能体",
        description: "在发出录用之前，核实候选人的证书、推荐人与所述经历是否对得上。",
        keywords: "人力资源核实智能体, 候选人资历核查 AI, 马来西亚背景核实自动化, 简历核验, 招聘尽职调查",
      },
    },
  },
  "solutions/agent-use-cases/corporate-comms": {
    title: "Corporate Communications Consistency Agent (Multilingual)",
    description: "Turn one approved message into on-brand English, Malay and Chinese versions — every figure and claim checked back to source, flagged, and signed off before release.",
    keywords: "corporate communications AI, multilingual investor relations, brand consistency across languages, AI translation consistency, IR announcement automation Malaysia, corporate communications agent Malaysia, multilingual press release AI, brand voice AI, corporate communications agent Singapore, investor relations AI Singapore, disclosure consistency AI",
    ogImage: "/images/agent-use-cases/corporate-comms-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen konsistensi komunikasi korporat",
        description: "Tukar satu mesej diluluskan kepada versi Inggeris, Bahasa Malaysia dan Cina yang menepati jenama — setiap angka dan dakwaan disemak semula dengan sumber, ditanda, dan diluluskan.",
        keywords: "komunikasi korporat AI, hubungan pelabur berbilang bahasa, konsistensi jenama merentas bahasa, konsistensi terjemahan AI, automasi pengumuman IR Malaysia, ejen komunikasi korporat, siaran akhbar berbilang bahasa AI, suara jenama AI",
      },
      zh: {
        title: "企业传播一致性智能体（多语言）",
        description: "把一条已批准的信息转化为符合品牌的英文、马来文和中文版本——每个数字与表述都与源核对、标记并签发。",
        keywords: "企业传播 AI, 多语言投资者关系, 跨语言品牌一致性, AI 翻译一致性, 马来西亚 IR 公告自动化, 企业传播智能体, 多语言新闻稿 AI, 品牌口吻 AI, 披露一致性",
      },
    },
  },
"solutions/ai-agents-for-logistics": {
    title: "AI Agents for Logistics in Malaysia & Singapore",
    description: "AI agents for order processing, 3-way invoice matching, contract review and support across the Malaysia–Singapore supply chain — human-in-the-loop.",
    keywords: "AI agents for logistics Malaysia, logistics automation AI Malaysia, supply chain AI Malaysia, freight AI, 3-way match automation, AI agents for logistics Singapore, cross-border logistics AI, warehouse automation AI",
    ogImage: "/images/industries/logistics-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen AI untuk Logistik di Malaysia & Singapura",
        description: "Ejen AI untuk pemprosesan pesanan, padanan invois 3-hala, semakan kontrak dan sokongan merentas rantaian bekalan Malaysia–Singapura — dibina atas Claude.",
        keywords: "ejen AI logistik Malaysia, automasi logistik AI, AI rantaian bekalan Malaysia, AI pengangkutan, automasi padanan 3-hala, ejen AI logistik Singapura, logistik rentas sempadan AI",
      },
      zh: {
        title: "面向物流的 AI 智能体（马来西亚与新加坡）",
        description: "为订单处理、三方发票核对、合同审查与客服打造的 AI 智能体，贯通马来西亚—新加坡供应链——基于 Claude、人在环路。",
        keywords: "物流 AI 智能体, 马来西亚物流自动化, 供应链 AI, 货运 AI, 三方核对自动化, 新加坡物流 AI, 跨境物流 AI",
      },
    },
  },
  "solutions/ai-agents-for-retail": {
    title: "AI Agents for Retail & F&B in Malaysia & Singapore",
    description: "AI agents that capture WhatsApp orders, answer product enquiries and keep messaging consistent for retail & F&B in Malaysia and Singapore — built on Claude.",
    keywords: "AI agents for retail Malaysia, AI for F&B Malaysia, WhatsApp order automation, retail AI agents Malaysia, AI agents for retail Singapore, F&B automation, product enquiry AI, retail chatbot Malaysia",
    ogImage: "/images/industries/retail-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Ejen AI untuk Runcit & F&B di Malaysia & Singapura",
        description: "Ejen AI yang merakam pesanan WhatsApp, menjawab pertanyaan produk dan mengekalkan mesej konsisten untuk runcit & F&B di Malaysia dan Singapura.",
        keywords: "ejen AI runcit Malaysia, AI untuk F&B Malaysia, automasi pesanan WhatsApp, ejen AI runcit, ejen AI runcit Singapura, automasi F&B, AI pertanyaan produk, chatbot runcit Malaysia",
      },
      zh: {
        title: "面向零售与餐饮的 AI 智能体（马来西亚与新加坡）",
        description: "在马来西亚与新加坡为零售与餐饮打造的 AI 智能体：接收 WhatsApp 订单、回答产品咨询、保持信息一致——基于 Claude。",
        keywords: "零售 AI 智能体, 马来西亚餐饮 AI, WhatsApp 订单自动化, 零售 AI, 新加坡零售 AI, 餐饮自动化, 产品咨询 AI, 零售聊天机器人",
      },
    },
  },
  "solutions/agent-rescue": {
    title: "AI Agent Rescue: Fix a Broken AI Agent | Malaysia & Singapore",
    description: "A vendor built you an AI agent that doesn't work? Get a free audit and an honest rectify-or-rebuild verdict. Handover from RM 2,000. Malaysia & Singapore.",
    keywords: "fix AI agent, AI agent not working, rescue AI agent project, AI agent remediation, broken AI agent Malaysia, AI agent audit, fix my AI agent, failed AI agent project, AI agent rescue Singapore, take over AI agent",
    ogImage: "/images/solutions/agent-rescue-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "AI Agent Rescue: Baiki Ejen AI yang Rosak | Malaysia & Singapura",
        description: "Vendor bina ejen AI yang tidak berfungsi? Dapatkan audit percuma dan keputusan jujur — baiki atau bina semula. Serahan dari RM 2,000. Malaysia & Singapura.",
        keywords: "baiki ejen AI, ejen AI tidak berfungsi, selamatkan projek ejen AI, pemulihan ejen AI, ejen AI rosak Malaysia, audit ejen AI, ambil alih ejen AI",
      },
      zh: {
        title: "AI 智能体救援：修复无法运行的 AI 智能体 | 马来西亚与新加坡",
        description: "供应商交付的 AI 智能体无法运行？获取免费诊断与诚实的“修复还是重建”结论。接手从 RM 2,000 起。马来西亚与新加坡。",
        keywords: "修复 AI 智能体, AI 智能体无法运行, 拯救 AI 智能体项目, AI 智能体修复, 损坏的 AI 智能体, AI 智能体诊断, 接手 AI 智能体, 新加坡 AI 智能体救援",
      },
    },
  },
  singapore: {
    title: "Claude AI & Agentic Development in Singapore | Anchor Sprint",
    description:
      "Claude implementation, partner services, training and managed operations in Singapore, Certified in the Claude Partner Network. PDPC + IMDA governance, costed in SGD.",
    keywords:
      "Claude Singapore, Claude AI Singapore, agentic AI Singapore, AI agent development Singapore, Claude implementation Singapore, Anthropic Singapore",
    ogImage: "/images/services/ai-agent-editorial.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "singapore/claude-implementation": {
    title: "Claude Implementation Singapore | Anchor Sprint",
    description:
      "Implement Claude and agentic AI in Singapore end to end — POC to production, wired into your systems, PDPC + IMDA governed, costed in SGD. Certified in the Claude Partner Network.",
    keywords:
      "Claude implementation Singapore, Claude deployment Singapore, agentic AI Singapore, AI agent development Singapore, Claude enterprise Singapore, Claude consulting Singapore",
    ogImage: "/images/services/ai-agent-editorial.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "singapore/claude-partner": {
    title: "Claude Partner Singapore | Anthropic Claude Partner Network",
    description:
      "Anchor Sprint is a homegrown Anthropic Claude Partner Network member serving Singapore from Suntec — a full team of Claude Certified Architects (CCAR-F) building production Claude agents with the Agent SDK, Claude Code, and MCP. Managed service, consulting, implementation, RAG and custom agents, and on-demand FDE teams with local SGD billing.",
    keywords:
      "Claude partner Singapore, claude singapore partner, singapore claude partner, Anthropic Claude partner Singapore, Anthropic partner Singapore, anthropic singapore partner, anthropic ai agency singapore, Claude AI Singapore, Claude agents Singapore, Claude enterprise Singapore",
    ogImage: "/images/services/ai-agent-editorial.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "singapore/claude-training": {
    title: "Claude Training Singapore | Anchor Sprint",
    description:
      // "SkillsFuture/IMDA-linked funding where eligible" removed 2026-08-08.
      // We are not an SSG Registered Training Provider, and a snippet is far too
      // little space to carry that nuance — a reader would infer claimability.
      // The full position is stated on the page and in its FAQ instead.
      "Practical Claude and agentic-AI training for Singapore teams — prompting, agent design and governed use under PDPC, delivered by Claude Certified Architects.",
    keywords:
      "Claude training Singapore, Claude AI training Singapore, Claude AI course Singapore, Claude Cowork training Singapore, Claude certification Singapore, AI training Singapore, corporate Claude training Singapore, prompt engineering training Singapore, agentic AI workshop Singapore, Claude workshop Singapore",
    ogImage: "/images/services/ai-agent-editorial.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "singapore/sonarqube": {
    // Owner page for "sonarqube singapore" + "sast tool singapore" (research doc
    // sonarqube-enterprise-push-2026-10-06 §5 Bet 1). Advanced Security and
    // Remediation Agent Singapore terms are FAQ-level here. The MY page owns
    // "sast tool malaysia"; no other page targets SAST.
    // 2026-10-06: also owns "MAS TRM source code review" (#mas-trm section) and
    // "Gitar Singapore" (research doc sonar-keywords-compliance-2026-10-06 §2.5).
    // 2026-10-23: title restored to the pre-#409 wording. It was live when the page
    // ranked #2 for "sonarqube singapore" (6 Oct); after the SAST/Remediation Agent
    // title the page fell to #9 and Google showed the H1 instead of this title.
    // SAST and the Remediation Agent stay in the description and on the page.
    title: "SonarQube Singapore | Licences, Setup & Training (SGD)",
    description:
      "Sonar reseller partner in Singapore: SonarQube licences in SGD, a SAST tool for Singapore teams, the Remediation Agent and add-ons, with setup and training.",
    keywords:
      "SonarQube Singapore, SAST tool Singapore, SonarQube reseller Singapore, SonarQube licence Singapore, SonarQube license Singapore, SonarQube price Singapore, SonarQube training Singapore, SonarQube Advanced Security Singapore, SonarQube Remediation Agent Singapore, MAS TRM source code review, Gitar Singapore",
    ogImage: "/images/enterprise/sonarqube-malaysia-og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
  },
  "singapore/ai-seo": {
    // Keyword ownership (one keyword, one page): the ONLY owner of the Singapore
    // commercial SEO terms below. The how-to terms stay on
    // blog/get-recommended-by-chatgpt-google-ai-overviews; the Malaysia terms stay on
    // services/seo-services-malaysia. SGD prices from seo-pricing.ts (2026-10-04).
    // 2026-11-03 (ai-seo-agency-tracker C005): title/description lead with "AI search
    // agency". GSC sgp 18–31 Oct: "singapore ai search agency" 100 impr p6.9 and
    // "ai search agency in singapore" 98 impr p12.6 dominate, with 0 clicks across all
    // SG target queries; SERP (gl=sg) shows page-1 titles mirroring the query wording.
    // Frozen until 2026-12-04 (measure_on 2026-12-01 + 3).
    title: `AI Search Agency Singapore | AI SEO for Google & ChatGPT from ${sgd(SEO_PLANS_SGD_MONTHLY.essential)}`,
    description: `AI search agency for Singapore businesses: AI SEO from ${sgd(SEO_PLANS_SGD_MONTHLY.essential)}/month. Weekly fixes by an AI agent, and every month we check whether ChatGPT, Gemini and AI Overviews name you.`,
    keywords:
      "AI search agency Singapore, AI search optimisation Singapore, AI SEO Singapore, AI SEO agency Singapore, SEO agency Singapore, SEO services Singapore, ChatGPT SEO Singapore, Google AI Overviews SEO Singapore, AI Overview SEO Singapore, AI visibility Singapore, GEO Singapore, AEO Singapore",
    ogImage: "/images/singapore/ai-seo-singapore-og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
  },
  "singapore/claude-managed-service": {
    title: "Claude Managed Service Singapore | Anchor Sprint",
    description:
      "Managed Claude operations in Singapore — monitoring, evals, governance and SLA-backed support so your agents keep performing. PDPC-aligned, costed in SGD.",
    keywords:
      "Claude managed service Singapore, Claude operations Singapore, AI agent monitoring Singapore, Claude support Singapore, LLMOps Singapore, Claude enterprise Singapore",
    ogImage: "/images/services/ai-agent-editorial.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "tools/ai-visibility-check": {
    // Created 2026-10-07. Keyword ownership (one keyword, one page): owns the CHECK /
    // tool-intent terms below and nothing else. "AI visibility Malaysia" stays on
    // services/seo-services-malaysia (its meta is frozen until 2026-10-12), "AI
    // visibility Singapore" on singapore/ai-seo, and the HOW-TO terms ("ChatGPT
    // recommend my business", "how to get recommended by ChatGPT") on
    // blog/get-recommended-by-chatgpt-google-ai-overviews. "check if ChatGPT
    // recommends my business" is the check-intent sibling of that blog term: this
    // page owns it, the blog links here from its CTA boxes.
    title: "Free AI Visibility Check: Is Your Business on ChatGPT?",
    description:
      "Free AI visibility check for Malaysian businesses: we ask ChatGPT, Gemini and Google AI Overviews five customer questions and show who they name instead of you.",
    keywords:
      "AI visibility check, free AI visibility check, is my business on ChatGPT, check if ChatGPT recommends my business, AI visibility checker, ChatGPT visibility check",
    ogImage: "/images/services/ai-seo-visibility-og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Semakan Keterlihatan AI Percuma: Adakah Perniagaan Anda di ChatGPT?",
        description:
          "Semakan keterlihatan AI percuma: kami tanya ChatGPT, Gemini dan Google AI Overviews lima soalan pelanggan anda dan tunjukkan siapa yang mereka namakan.",
        keywords:
          "semakan keterlihatan AI, semakan keterlihatan AI percuma, adakah perniagaan saya di ChatGPT, semak jika ChatGPT mengesyorkan perniagaan saya",
      },
      zh: {
        title: "免费 AI 可见度检查：ChatGPT 会推荐您的企业吗？",
        description:
          "免费 AI 可见度检查：我们向 ChatGPT、Gemini 和 Google AI Overviews 提出五个客户会问的问题，告诉您它们推荐了谁。",
        keywords:
          "AI 可见度检查, 免费 AI 可见度检查, ChatGPT 会推荐我的企业吗, 检查 ChatGPT 是否推荐我的企业",
      },
    },
  },
  "tools/claude-cost-calculator": {
    // CTR REWRITE 2026-09-23 — MEASURABLE HYPOTHESIS, revert if it does not move.
    // This page is the largest impression source on the site (26,962 impr/28d,
    // double any other) at avg p7.6, but 0.71% CTR. The split by query intent is
    // the whole story:
    //   claude team pricing        1,738 impr  0.17% CTR  p9.2
    //   claude enterprise pricing  1,185 impr  0.17% CTR  p9.5
    //   claude enterprise vs team    190 impr  2.11% CTR
    //   claude calculator            128 impr  2.34% CTR
    //   claude cost calculator        94 impr  2.13% CTR
    // Comparison/calculator intent converts ~2.1-2.3%. PLAIN PRICING intent
    // converts 0.17% -- 12x worse -- on 2,923 impressions. The hypothesis: someone
    // searching "claude team pricing" wants a NUMBER, and the old snippet offered a
    // Calculator (work) badged "Malaysia" (irrelevant to a non-geo query). So the
    // title now leads with the comparison and the word "cost", keeps "calculator"
    // for the terms that already convert on it, and drops the geo badge from the
    // title. Malaysia/SGD stays in the description, where it reassures without
    // gating the click.
    // BASELINE to judge against, 28d to 2026-09-21: 26,962 impr / 191 clicks /
    // 0.71% CTR / p7.6. Re-check ~2026-10-21: CTR is the metric, not position.
    //
    // RESULT 2026-10-28 (28d to 2026-10-24): 61,985 impr / 306 clicks / 0.49% CTR / p6.8.
    // Page CTR FELL. "claude team pricing" 5,301 impr 0.13% (was 0.17%) — no lift;
    // "claude enterprise pricing" 4,673 impr 0.34% (was 0.17%) — small lift. Anthropic's own
    // pricing page holds p1-3, so promising a calculator does not win the click. NEW
    // HYPOTHESIS: put the number in RM in the snippet, plus the one thing Anthropic's page
    // cannot offer — buying on a company PO in RM/SGD (purchase help, not resale; card buyers
    // go direct). Re-check ~2026-11-25 against this 0.49% baseline, same two queries.
    title: "Claude Team & Enterprise Pricing in RM/SGD + Calculator",
    description:
      "Claude Team is US$20–25 a seat (about RM82–102); Enterprise is US$20 a seat plus usage. Compare the 3-year cost at your headcount, and buy on a PO in RM or SGD.",
    keywords:
      "Claude pricing calculator, Claude pricing Malaysia, Claude price Malaysia, Claude cost calculator, Claude cost per user Malaysia, how much does Claude cost Malaysia, Claude subscription price Malaysia, Claude Team vs Enterprise, Claude for Work pricing, Claude Enterprise pricing Malaysia",
    ogImage: "/images/tools/claude-cost-calculator-og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Kalkulator Harga Claude: Kos Team vs Enterprise",
        description:
          "Kalkulator harga Claude percuma — banding kos Claude Team vs Enterprise selama 3 tahun untuk saiz pasukan dan penggunaan anda. Lihat kos setiap kerusi + API dalam RM/USD, muat turun Excel.",
        keywords:
          "kalkulator harga Claude, harga Claude Malaysia, harga Claude AI Malaysia, kos langganan Claude Malaysia, Claude Team vs Enterprise, harga Claude for Work",
      },
      zh: {
        title: "Claude 定价计算器：Team 与 Enterprise 成本",
        description:
          "免费的 Claude 定价计算器——按团队规模与用量比较 Claude Team 与 Enterprise 三年成本。查看每席位 + API 成本（令吉或美元），并下载 Excel。",
        keywords:
          "Claude 定价计算器, Claude 价格 马来西亚, Claude 成本计算器, Claude 订阅价格 马来西亚, Claude Team 与 Enterprise, Claude for Work 价格",
      },
    },
  },
  "careers": {
    title: "Careers: AI Engineering & GTM Jobs in Malaysia",
    description:
      "Join Anchor Sprint in Shah Alam and build AI Malaysian businesses actually use. Open roles in AI engineering, GTM and business development, plus internships.",
    keywords:
      "Anchor Sprint careers, AI jobs Malaysia, AI engineer jobs Malaysia, tech jobs Shah Alam, AI startup jobs Selangor, agentic AI jobs Malaysia, GTM engineer Malaysia, forward deployed engineer Malaysia, tech internship Malaysia",
    ogImage: "/images/careers/hero-team.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },


  // Anchor Academy hub — targets "hrd corp approved ai training provider"
  // (we rank ~p40 for it and ARE one; this page is that fix). The service page
  // at services/training-education keeps "HRD Corp claimable AI training".
  "academy": {
    title: "HRD Corp Approved AI Training Provider in Malaysia",
    description:
      "HRD Corp approved AI training provider — Claude certification prep, Vibe Engineering and custom in-house classes in Malaysia. We file the grant and the claim.",
    keywords:
      "Anchor Academy, Claude training Malaysia, Claude Cowork training, Vibe Engineering course, Vibe Engineering training Malaysia, AI training course catalogue Malaysia, in-house AI training Kuala Lumpur",
    localized: {
      ms: {
        title: "Penyedia Latihan AI Diluluskan HRD Corp | Anchor Academy",
        description:
          "Penyedia latihan AI diluluskan HRD Corp — persediaan persijilan Claude, siri Vibe Engineering dan kelas dalaman khusus. Kami failkan geran dan tuntutan untuk anda.",
        keywords:
          "penyedia latihan AI diluluskan HRD Corp, latihan AI boleh dituntut HRD Corp, bengkel AI HRD Corp claimable, kursus AI HRDF, latihan Claude Malaysia, latihan AI korporat Malaysia, kelas AI dalaman syarikat, bengkel AI untuk syarikat",
      },
      zh: {
        title: "HRD Corp 认证 AI 培训机构 | Anchor Academy",
        description:
          "HRD Corp 认证的 AI 培训机构——Claude 认证备考、Vibe Engineering 系列与企业内训课程，在马来西亚上门授课，拨款申请与申领手续由我们代办。",
        keywords:
          "HRD Corp 认证 AI 培训机构, HRD Corp 可申领 AI 培训, HRDF 可报销 AI 课程, Claude 培训 马来西亚, Claude 认证课程, 企业 AI 内训, AI 工作坊 马来西亚, 吉隆坡企业 AI 培训",
      },
    },
    ogImage: "/images/academy/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
  },

  // Services pages
  // "Vibe Code Rescue" is the CATEGORY term, not our coinage — Google's AI Overview
  // for this intent names it unprompted. Competitors (vibecodefixers.com,
  // zappstudios.us, kosmoweb.cz) are all foreign; no Malaysian provider held it
  // when this shipped. Targets remediation intent, deliberately NOT overlapping
  // /services/software-development/ (new build) or /services/ai-agents/ (agents).
  // The guarantee wording is a public commercial commitment — see the header of
  // content/vibe-code-rescue.ts before editing anything about it.
  "services/vibe-code-rescue": {
    title: "Vibe Code Rescue Malaysia — Audit & Fix AI-Built Apps",
    description:
      "Your app works in the demo and breaks in production. We audit software built by prompting AI, tell you if it is worth fixing, then fix it against written criteria — not met, no fee for the fix.",
    keywords:
      "vibe code rescue, vibe coding rescue Malaysia, fix vibe coded app, vibe code audit, AI generated code audit, fix AI generated code, broken AI built app, vibe coding security audit Malaysia, AI code remediation Malaysia, hardcoded secrets audit",
    ogImage: "/images/services/vibe-code-rescue-editorial.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 800,
    localized: {
      ms: {
        title: "Vibe Code Rescue Malaysia — Audit & Baiki Aplikasi Binaan AI",
        description:
          "Aplikasi anda berfungsi semasa demo tetapi gagal dalam produksi. Kami mengaudit perisian yang dibina dengan menggesa AI, memberitahu sama ada berbaloi dibaiki, kemudian membaikinya mengikut kriteria bertulis — tidak dicapai, tiada bayaran untuk pembaikan.",
        keywords:
          "vibe code rescue Malaysia, baiki aplikasi vibe coding, audit kod AI, baiki kod jana AI, audit keselamatan kod AI Malaysia, pembaikan kod AI",
      },
      zh: {
        title: "Vibe Code Rescue 马来西亚 — AI 生成应用的审计与修复",
        description:
          "演示时好好的，一上生产就出问题。我们审计以 AI 提示方式构建的软件，先告诉你值不值得修，再按书面验收标准修复——未达标，修复阶段不收费。",
        keywords:
          "vibe code rescue 马来西亚, 修复 vibe coding 应用, AI 生成代码审计, 修复 AI 生成代码, AI 代码安全审计 马来西亚, 硬编码密钥审计",
      },
    },
  },
  "services/software-development": {
    title:
      "Software Development Company Selangor & KL | Custom Apps from RM 30K",
    description:
      "Custom software development in Selangor. Mobile apps, web & enterprise systems. Agile 2-week sprints, working software in 30 days. Get a free quote today.",
    keywords:
      "software development company Malaysia, software development agency Selangor, software development company Selangor, software company Shah Alam, custom software Kuala Lumpur, mobile app development Malaysia, web development KL, enterprise software Malaysia",
    localized: {
      ms: {
        title:
          "Syarikat Pembangunan Perisian Selangor & KL | Aplikasi Khusus dari RM 30K",
        description:
          "Pembangunan perisian khusus di Selangor untuk aplikasi mudah alih, aplikasi web dan sistem perusahaan. Penghantaran agile dengan sprint dua minggu.",
        keywords:
          "syarikat pembangunan perisian Malaysia, agensi pembangunan perisian Selangor, perisian khusus Kuala Lumpur, pembangunan aplikasi mudah alih Malaysia, perisian perusahaan Malaysia",
      },
      zh: {
        title: "雪兰莪与吉隆坡软件开发公司 | 定制应用从 RM 30K 起",
        description:
          "雪兰莪定制软件开发服务，涵盖移动应用、Web 应用和企业系统。采用敏捷交付，两周一个 sprint。",
        keywords:
          "马来西亚软件开发公司, 雪兰莪软件开发机构, 吉隆坡定制软件, 马来西亚移动应用开发, 马来西亚企业软件",
      },
    },
    ogImage: "/images/services/svc-software-development.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "services/enterprise-resilience": {
    title: "Enterprise Resilience Assessment: BNM RMiT & MAS",
    description:
      "Hands-on resilience engineering — chaos, load-to-failure, DR and SLOs — mapped to BNM RMiT & MAS, not a compliance deck. Plus independent audits of the load testing you've already had done. Malaysia & Singapore.",
    keywords:
      "enterprise resilience assessment, resilience engineering Malaysia, resilience assessment Singapore, BNM RMiT resilience testing, MAS operational resilience, load testing audit, independent DR assessment, chaos engineering Malaysia, operational resilience Malaysia Singapore, SRE assessment",
    ogImage: "/images/services/enterprise-resilience-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Penilaian Daya Tahan Perusahaan: RMiT & MAS",
        description:
          "Kejuruteraan daya tahan langsung — chaos, ujian hingga gagal, DR dan SLO — dipetakan ke BNM RMiT & MAS, bukan sekadar dek pematuhan. Termasuk audit bebas ujian beban yang telah anda lakukan.",
        keywords:
          "penilaian daya tahan perusahaan, kejuruteraan daya tahan Malaysia, ujian daya tahan RMiT, daya tahan operasi MAS, audit ujian beban, penilaian DR Malaysia",
      },
      zh: {
        title: "企业韧性评估：符合 BNM RMiT 与 MAS",
        description:
          "亲手做的韧性工程——混沌测试、压到失效、灾备与 SLO——对齐 BNM RMiT 与 MAS，而非一份合规文档。并可独立审计你已做过的负载测试。马来西亚与新加坡。",
        keywords:
          "企业韧性评估, 韧性工程 马来西亚, RMiT 韧性测试, MAS 运营韧性, 负载测试审计, 灾备评估 新加坡, 混沌工程",
      },
    },
  },
  "ai-governance": {
    // Pillar for non-brand AI governance intent (MY + SG). Cluster pages in
    // phase 2 link back here. Facts: AI Governance Kit v1.0 pre-read.
    title: "AI Governance in Malaysia & Singapore: 2026 Guide + Free Kit",
    description:
      "AI governance for Malaysian and Singapore companies: 2026 rules (PDPA, draft AI Bill, MAS), a 90-day plan, a free AI policy pack and an AI risk management platform.",
    ogImage: "/images/ai-governance/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    keywords:
      "AI governance Malaysia, AI governance Singapore, AI governance framework, AI governance framework Malaysia, AI policy template Malaysia, AI policy for companies Malaysia, Malaysia AI Governance Bill, PDPA AI, MAS AI risk management guidelines, ISO 42001 Malaysia, NIST AI RMF, AI governance for businesses Malaysia, responsible AI Malaysia, shadow AI policy, AI risk management Malaysia, AI risk management Singapore, AI risk register, ISO 42001, ISO 42001 certification, EU AI Act Malaysia, AI Verify Singapore, responsible AI, ethical AI, shadow AI, ISO 42001 vs ISO 27001, model AI governance framework, high-risk AI system, pillars of AI governance",
    localized: {
      ms: {
        title: "Tadbir Urus AI Malaysia & Singapura: Panduan + Kit Percuma",
        description:
          "Tadbir urus AI untuk syarikat Malaysia dan Singapura: peraturan 2026 (PDPA, draf RUU AI, MAS), pelan 90 hari, pek dasar AI percuma dan platform pengurusan risiko AI.",
        keywords:
          "tadbir urus AI Malaysia, rangka kerja tadbir urus AI, dasar penggunaan AI syarikat, templat dasar AI, RUU Tadbir Urus AI Malaysia, PDPA AI, garis panduan AIGE, tadbir urus AI Singapura, shadow AI, ISO 42001",
      },
      zh: {
        title: "马来西亚与新加坡 AI 治理：2026 指南 + 免费工具包",
        description:
          "面向马来西亚与新加坡企业的 AI 治理：2026 年法规（PDPA、AI 治理法案草案、MAS）、90 天落地计划、免费 AI 政策模板包，以及 AI 风险管理平台。",
        keywords:
          "马来西亚 AI 治理, 新加坡 AI 治理, AI 治理框架, 企业 AI 使用政策, AI 政策模板, 马来西亚 AI 治理法案, PDPA AI, MAS AI 风险管理指引, ISO 42001, 影子 AI, AI Verify",
      },
    },
  },
  "ai-governance/ai-acceptable-use-policy-template": {
    title: "AI Acceptable Use Policy Template (Malaysia & Singapore)",
    description:
      "What an AI acceptable use policy must cover, a template excerpt and the data × AI tool matrix. Free Word download for Malaysian and Singapore companies.",
    ogImage: "/images/ai-governance/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    keywords:
      "AI acceptable use policy template, AI acceptable use policy, AI usage policy for employees, generative AI policy template, ChatGPT policy for employees Malaysia, AI policy template Malaysia, AI policy Singapore, company AI policy, Claude policy for employees",
  },
  "ai-governance/templates": {
    title: "AI Governance Templates: Free Policy & Register Previews",
    description:
      "Preview free AI governance templates in your browser: acceptable use policy, data handling standard, shadow AI procedure, AI system register and incident log.",
    ogImage: "/images/ai-governance/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    keywords:
      "AI governance templates, AI policy templates, AI governance template Malaysia, AI register template, AI governance documents",
  },
  "compliance": {
    // Hub for live compliance guides only (no "coming soon" cards). Deliberately does
    // NOT target any RMiT term — those belong to compliance/bnm-rmit (informational),
    // services/enterprise-resilience (resilience testing) and the RMiT code-review blog.
    title: "Technology Compliance Guides for Malaysia & Singapore",
    description:
      "Plain-English guides to what Malaysian and Singapore regulators and auditors expect from your technology, with the paragraph numbers to check and how we help with the evidence.",
    keywords:
      "technology compliance guides Malaysia, IT compliance guide Malaysia, technology regulation Singapore, financial services technology compliance, compliance guides for software vendors",
    ogImage: "/images/compliance/bnm-rmit-og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Panduan Pematuhan Teknologi untuk Malaysia & Singapura",
        description:
          "Panduan bahasa mudah tentang apa yang pengawal selia dan juruaudit di Malaysia dan Singapura harapkan daripada teknologi anda, dengan nombor perenggan untuk disemak.",
        keywords:
          "panduan pematuhan teknologi, pematuhan IT Malaysia, peraturan teknologi kewangan Malaysia, panduan pematuhan vendor perisian",
      },
      zh: {
        title: "马来西亚与新加坡科技合规指南",
        description:
          "用白话说明马来西亚和新加坡的监管机构与审计师对企业科技的期望，附上可核对的段落编号，以及我们如何协助准备证据。",
        keywords: "科技合规指南, 马来西亚 IT 合规, 新加坡 科技法规, 金融科技合规, 软件供应商合规",
      },
    },
  },
  "compliance/bnm-rmit": {
    // OWNS the informational head terms: "what is RMiT", "BNM RMiT", "RMiT Bank Negara",
    // "RMiT requirements", "RMiT Malaysia". Siblings keep their own terms (one keyword,
    // one page): services/enterprise-resilience = RMiT resilience testing (commercial; its
    // title also contains "BNM RMiT" — overlap reported, title left alone on purpose),
    // blog/rmit-source-code-review-malaysia = RMiT source code review,
    // enterprise/sonarqube-malaysia = RMiT code scanning. Gl=my SERP for "what is rmit bnm"
    // (2026-10-07): AI Overview, BNM pages, PwC/EY/Thales summaries, vendor checklists.
    title: "What is BNM RMiT? A Plain-English Guide for Malaysian Companies",
    description:
      "What is RMiT? Bank Negara's technology risk policy in plain English: who it covers, the main requirements, the Sep 2026 reissue and what auditors check.",
    keywords:
      "what is RMiT, BNM RMiT, RMiT Bank Negara, RMiT requirements, RMiT Malaysia, Risk Management in Technology BNM, RMiT policy document 2026, who does RMiT apply to",
    ogImage: "/images/compliance/bnm-rmit-og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Apa itu RMiT BNM? Panduan Mudah untuk Syarikat Malaysia",
        description:
          "Apa itu RMiT? Dasar risiko teknologi Bank Negara dalam bahasa mudah: siapa yang terlibat, keperluan utama, terbitan semula Sep 2026 dan apa yang juruaudit semak.",
        keywords:
          "apa itu RMiT, RMiT BNM, RMiT Bank Negara, keperluan RMiT, Pengurusan Risiko dalam Teknologi, RMiT Malaysia, dasar RMiT 2026",
      },
      zh: {
        title: "什么是 BNM RMiT？马来西亚企业白话指南",
        description:
          "什么是 RMiT？用白话解读马来西亚国家银行的科技风险管理政策：适用对象、主要要求、2026 年 9 月重新发布版本，以及审计师会检查什么。",
        keywords:
          "什么是 RMiT, BNM RMiT, 国家银行 RMiT, RMiT 要求, 科技风险管理 RMiT, 马来西亚 RMiT, RMiT 2026",
      },
    },
  },
  "services/ai-solutions": {
    // OWNS the umbrella "AI agency / AI company" intent — and holds it: p13.2 on
    // "ai agency malaysia" (200 of 224 impressions), cited by name in that query's
    // AI Overview. Judge this page on CTR, not position (geo-modifier-intent-split,
    // PR #277): the Overview caps clicks whatever we do.
    //
    // TWO KEYWORDS REMOVED 2026-09-11 — both were duplicated on sibling pages and
    // both showed the split in GSC (28d to 2026-09-08):
    //   · "AI automation agency Malaysia" -> /services/ai-agents/, whose title
    //     already claims it. Was splitting exactly 74i/74i between the two pages,
    //     ai-agents ranking better (p19.3 vs p34.3). One term, one page.
    //   · "AI consultant Malaysia" -> /services/ai-consulting-malaysia/, which the
    //     comment on that entry already declared the owner of the intent. The
    //     de-targeting here was documented in PR #362 but never executed.
    // This page keeps BUILD intent. Do not re-add agent, automation-agency or
    // consulting terms here — that is the claude-partner-malaysia mistake (#359).
    // TITLE FOLLOWS THE KEYWORDS, 2026-09-18. The de-targeting above was applied
    // to the `keywords` field — which Google has ignored since 2009 — while this
    // title went on claiming "Agents, Automation", the exact two terms the note
    // above says never to re-add. The separation was real but invisible to the
    // ranking signals. Measured before the change: this page drew 890 impressions
    // at p28.4 while /services/ai-agents/, which owns the agent set and is titled
    // for it, drew 12 at p19.8, and neither appeared in the top 20 for "ai
    // automation agency malaysia". BUILD intent stays; the agent claim goes.
    title:
      "AI Agency Malaysia — Build, Deploy & Support",
    description:
      "An AI agency in Malaysia building custom AI agents, workflow automation and Claude deployment. Projects from RM 5,000. Certified in the Claude Partner Network.",
    keywords:
      "AI agency Malaysia, AI company Malaysia, AI company in Malaysia, AI solutions Malaysia, AI agency Kuala Lumpur, AI vendor Malaysia",
    localized: {
      ms: {
        title: "Perkhidmatan AI Malaysia: Ejen, Claude Terurus & Latihan",
        description:
          "Bina ejen AI tersuai, onboard dan urus Claude dalam organisasi anda, serta latih pasukan bersama rakan AI tempatan yang diiktiraf Malaysia Digital.",
        keywords:
          "perkhidmatan AI Malaysia, pembangunan ejen AI Malaysia, Claude terurus Malaysia, onboarding Claude Malaysia, latihan Claude Malaysia, perunding AI Malaysia",
      },
      zh: {
        title: "马来西亚 AI 服务：智能体、Claude 托管与培训",
        description:
          "由马来西亚本地团队为您开发 AI 智能体、推动 Claude 在组织内上手与托管运营，并培训员工。",
        keywords:
          "马来西亚AI服务, 马来西亚AI智能体开发, Claude托管服务, Claude团队上手, Claude培训, 马来西亚AI顾问",
      },
    },
    ogImage: "/images/ai-solutions/ai-hero-2026.webp",
    ogImageType: "image/webp",
    ogImageWidth: 1200,
    ogImageHeight: 654,
  },
  "services/claude-managed-service-malaysia": {
    title: "Claude Managed Service Malaysia | Anchor Sprint",
    description:
      "Onboard Claude across your team with setup, governance, workflow integration, adoption support and ongoing optimisation from a local Malaysia Digital certified team.",
    keywords:
      "Claude managed service Malaysia, Claude onboarding Malaysia, Claude for business Malaysia, Claude enterprise setup, managed Claude support, Claude adoption consulting, AI governance Malaysia, Claude training Malaysia",
    localized: {
      ms: {
        title: "Perkhidmatan Terurus Claude Malaysia | Anchor Sprint",
        description:
          "Bawa Claude ke dalam pasukan anda dengan persediaan, tadbir urus, integrasi aliran kerja, sokongan penggunaan dan pengoptimuman berterusan.",
        keywords:
          "perkhidmatan terurus Claude Malaysia, onboarding Claude Malaysia, Claude untuk perniagaan, sokongan Claude, tadbir urus AI Malaysia, latihan Claude Malaysia",
      },
      zh: {
        title: "马来西亚 Claude 托管服务 | Anchor Sprint",
        description:
          "通过设置、治理、工作流程整合、团队上手支持和持续优化，将 Claude 安全地引入您的组织。",
        keywords:
          "马来西亚Claude托管服务, Claude企业设置, Claude团队上手, Claude支持, 马来西亚AI治理, Claude培训",
      },
    },
    ogImage: "/images/services/claude-managed-service-editorial.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "services/ai-agents": {
    // Leads with "AI agent" because Google Trends puts it ~5x above "AI agency" in
    // Malaysia — the opposite of what GSC impressions suggested when #292 was written.
    title:
      "AI Agent Development Malaysia | AI Automation Agency",
    description:
      "An AI automation agency in Malaysia building AI agents that run real multi-step workflows — invoice matching, order processing, support triage. AI workflow design, built on Claude, graded before go-live.",
    keywords:
      "AI agent Malaysia, AI agents Malaysia, AI agent development Malaysia, AI agent solution Malaysia, AI agent provider Malaysia, AI automation agency Malaysia, AI workflow Malaysia, AI automation Malaysia, AI automation company Malaysia, AI automation consultant Malaysia, digital workflow solutions Malaysia, agentic AI Malaysia",
    localized: {
      ms: {
        title: "Pembangunan Ejen AI Malaysia: Agentic AI atas Claude | Anchor Sprint",
        description:
          "Pembangunan ejen AI tersuai di Malaysia — agentic AI dibina atas Claude yang menjalankan aliran kerja berbilang langkah sebenar: invois, operasi, pemprosesan dokumen. Dilaksana dan disokong oleh pasukan disahkan MDEC.",
        keywords:
          "pembangunan ejen AI Malaysia, agentic AI Malaysia, penyelesaian ejen AI Malaysia, ejen AI tersuai Malaysia, agensi automasi AI Malaysia, ejen AI Malaysia",
      },
      zh: {
        title: "马来西亚 AI 智能体开发：基于 Claude 的 Agentic AI | Anchor Sprint",
        description:
          "马来西亚定制 AI 智能体开发——基于 Claude 构建的 agentic AI，可执行真实的多步骤工作流：发票、运营、文档处理。由 MDEC 认证的本地团队部署与支持。",
        keywords:
          "马来西亚AI智能体开发, 马来西亚Agentic AI, 马来西亚AI智能体解决方案, 马来西亚定制AI智能体, 马来西亚AI自动化机构, 马来西亚AI智能体",
      },
    },
    ogImage: "/images/services/ai-agent-editorial.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "services/ai-consulting-malaysia": {
    // The e2e lifecycle page. Targets "ai consulting malaysia" — a verified
    // commercial SERP (Local pack, RM rates discussed, Agmo/AI Labs/Oxydata
    // ranking) where our best page sat at p54.7 with a second page at p48.5
    // splitting authority. See
    // docs/analytics/competitors/2026-W36-ai-consulting-malaysia-gap.md
    // This page is the OWNER of that intent; ai-solutions stays on build
    // intent and digital-transformation keeps its own cluster (it is #1
    // organic for "digital transformation consultants kl").
    title: "AI Consulting Malaysia: The Full AI Adoption Lifecycle",
    description:
      "End-to-end AI consulting in Malaysia — awareness, tooling, workflow redesign and adoption measurement. HRD Corp approved, Claude Certified Architects in house.",
    keywords:
      "AI consulting Malaysia, AI consultant Malaysia, AI consulting services Malaysia, AI adoption Malaysia, AI advisory Malaysia, AI strategy Malaysia, AI implementation partner Malaysia, workflow automation consulting Malaysia, HRD Corp claimable AI consulting, AI transformation Malaysia, AI consulting Kuala Lumpur, AI consulting Selangor",
    ogImage: "/images/services/ai-consulting-lifecycle-editorial.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 800,
    tags: ["AI", "Malaysia Business"],
  },
  "services/seo-services-malaysia": {
    // Repositioned 2026-09-23 (was "AI SEO Malaysia from RM699/month | No Agency
    // Needed"). 8 weeks: 1,199 impr, 1 click, 0 enquiries. The top query was "ai seo
    // agency malaysia" (338 impr) and the title argued against the word "agency".
    // MY SERPs and AI Overviews define AI SEO as being found in AI answers
    // (GEO/AEO), so the page now sells Google + AI visibility, with the agent as the
    // mechanism. Trends: "ai seo" is the only AI-SEO term with volume in MY/SG;
    // GEO/AEO/agentic/LLM SEO are ~0, so they appear as synonyms, not targets.
    // Keyword ownership: price terms -> blog/seo-price-malaysia-2026; "AI SEO
    // agent" -> blog/ai-seo-agent-malaysia; "vs"/GEO-vs-SEO -> the comparison blog.
    title: `AI SEO Agency Malaysia | Get Found on Google & ChatGPT from ${rm(SEO_PLANS.essential.monthly)}`,
    description:
      `AI SEO for Malaysian businesses: weekly fixes by an AI agent, plus monthly tracking of whether ChatGPT, Gemini and AI Overviews name you. From ${rm(SEO_PLANS.essential.monthly)}, month to month.`,
    // 2026-11-01: KL terms moved to services/ai-seo-kuala-lumpur (city owner page).
    keywords:
      "AI SEO Malaysia, AI SEO agency Malaysia, AI SEO services Malaysia, AI SEO company Malaysia, AI visibility Malaysia, AI search visibility Malaysia, GEO Malaysia, AI SEO cost Malaysia, AI SEO monthly retainer",
    ogImage: "/images/services/ai-seo-visibility-og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
  },
  "services/ai-seo-kuala-lumpur": {
    // City owner page (2026-11-01). Owns the KL / Kuala Lumpur / Klang Valley AI SEO
    // terms, which sat at p17-30 on the Malaysia page (GSC Aug-Oct 2026, ~560 impr).
    // Country terms stay on services/seo-services-malaysia. English-only, like the
    // Malaysia page.
    title: `AI SEO Agency Kuala Lumpur (KL) | Google & ChatGPT Visibility from ${rm(SEO_PLANS.essential.monthly)}`,
    description:
      `AI SEO agency for Kuala Lumpur and Klang Valley businesses: weekly fixes by an AI agent and monthly tracking of whether ChatGPT, Gemini and AI Overviews name you. Month to month.`,
    keywords:
      "AI SEO agency Kuala Lumpur, AI SEO agency KL, AI SEO Kuala Lumpur, AI SEO KL, AI SEO company Kuala Lumpur, AI SEO company KL, AI SEO services Kuala Lumpur, AI SEO Klang Valley, AI SEO Petaling Jaya, hire AI SEO agency Kuala Lumpur",
    ogImage: "/images/services/ai-seo-visibility-og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
  },
  "enterprise/sonarqube-malaysia": {
    // SonarQube cluster (spec 2026-09-25). Owns the MY geo-commercial terms;
    // pricing/editions/AI terms belong to the three SonarQube blogs.
    // 2026-10-06: also owns "sast tool malaysia" (confirmed by a gl=my SERP pull).
    // 2026-10-06: owns the MY product-line terms (Remediation Agent, Sonar Vortex, Hunter
    // Agent, Gitar, AI agents). No other page targets them; the SG page owns the Singapore
    // variants, the AI Code Assurance blog only AI Code Assurance / AI CodeFix.
    // 2026-10-06 (banks exception, SonarQube only): owns "SAST for banks Malaysia" and
    // "RMiT code scanning" (#financial-institutions). "RMiT source code review" belongs to
    // blog/rmit-source-code-review-malaysia, never this page.
    title: "SonarQube Malaysia | Licences, Setup & Training | Sonar Reseller",
    description:
      "SonarQube in Malaysia from a Sonar reseller partner: a SAST tool with Team and Enterprise licences, AI CodeFix and agent add-ons, CI/CD setup and HRD Corp training.",
    keywords:
      "SonarQube Malaysia, SAST tool Malaysia, SAST for banks Malaysia, RMiT code scanning, SonarQube reseller Malaysia, SonarQube licence Malaysia, SonarQube license Malaysia, SonarQube price Malaysia, SonarQube Enterprise Malaysia, SonarQube Advanced Security Malaysia, SonarQube Remediation Agent Malaysia, Sonar Vortex, Sonar Vortex Malaysia, SonarQube Hunter Agent Malaysia, Gitar Malaysia, SonarQube AI agents Malaysia, SonarQube training Malaysia, SonarQube HRD Corp",
    ogImage: "/images/enterprise/sonarqube-malaysia-og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
  },
  "services/conversational-ai": {
    title:
      "AI Chatbot Malaysia: Customer Support That Answers 24/7",
    description:
      "AI chatbot for Malaysian businesses — answers customer enquiries on WhatsApp and web around the clock, escalates to a human when it should, and is measured before go-live.",
    keywords:
      "AI chatbot Malaysia, ai chat bot malaysia, chatbot Malaysia, chatbot service Malaysia, AI chat Malaysia, chatbot customer service Malaysia, chatbot developer Malaysia, AI chatbot for business Malaysia, conversational AI Malaysia, WhatsApp chatbot Malaysia, WhatsApp AI chatbot Malaysia",
    localized: {
      ms: {
        title: "Chatbot AI untuk Perniagaan Malaysia: Sokongan WhatsApp 24/7 | Anchor Sprint",
        description:
          "Chatbot AI tersuai untuk perniagaan Malaysia. Jawab pelanggan 24/7 di WhatsApp dan web dalam Bahasa Malaysia, Inggeris dan Cina, serta serahkan kepada staf apabila perlu.",
        keywords:
          "chatbot AI perniagaan Malaysia, chatbot AI WhatsApp Malaysia, AI perbualan Malaysia, AI khidmat pelanggan Malaysia, chatbot Bahasa Malaysia",
      },
      zh: {
        title: "马来西亚企业 AI 聊天机器人：WhatsApp 全天候客服 | Anchor Sprint",
        description:
          "为马来西亚企业定制的 AI 聊天机器人。以马来语、英语和中文在 WhatsApp 和网站上全天候回复客户，需要时转交人工处理。",
        keywords:
          "马来西亚企业AI聊天机器人, 马来西亚WhatsApp AI聊天机器人, 马来西亚对话式AI, 马来西亚AI客服, 马来语聊天机器人",
      },
    },
    ogImage: "/images/services/conversational-editorial.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "services/cloud-architecture": {
    title: "AWS Cloud Consultant in Selangor | Cloud Architecture Services Malaysia",
    description:
      "Certified AWS cloud consultant in Shah Alam, Selangor. Cloud architecture, migration & DevOps for businesses in KL, Petaling Jaya & across Malaysia.",
    keywords:
      "AWS consultant Selangor, cloud architecture Malaysia, AWS services Shah Alam, cloud migration Kuala Lumpur, AWS partner Selangor, DevOps services KL",
    ogImage: "/images/services/svc-cloud-architecture.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Seni Bina Awan Malaysia | Perunding AWS di Selangor",
        description:
          "Reka bentuk dan pelaksanaan seni bina awan di Malaysia oleh perunding AWS di Shah Alam, Selangor: migrasi awan dan DevOps untuk perniagaan di KL dan seluruh Malaysia.",
        keywords:
          "perunding AWS Selangor, seni bina awan Malaysia, migrasi awan Kuala Lumpur, perkhidmatan AWS Shah Alam, DevOps Malaysia",
      },
      zh: {
        title: "马来西亚云架构设计与实施 | 雪兰莪 AWS 顾问",
        description:
          "位于雪兰莪莎阿南的 AWS 顾问，为吉隆坡及全马企业提供云架构设计与实施、云迁移和 DevOps 服务。",
        keywords:
          "雪兰莪 AWS 顾问, 马来西亚云架构, 吉隆坡云迁移, 莎阿南 AWS 服务, 马来西亚 DevOps",
      },
    },
  },
  "services/digital-transformation": {
    // 228 impressions, ZERO clicks at p7.4 for "digital transformation
    // consultants kl". The old title already matched the query almost exactly,
    // so keyword coverage was never the problem — it was two keyword phrases
    // welded together by a pipe, making a promise no stronger than the six
    // results above it. At the bottom of page 1 the lever is proof, not more
    // keywords, so the second phrase gives way to the 50+ project count (a
    // claim already made and substantiated on the page). "Services Malaysia"
    // is carried by the description instead, which Google matches semantically.
    title: "Digital Transformation Consultants KL: 50+ Projects Delivered",
    description:
      "Digital transformation services in Malaysia from a KL consultancy — 50+ projects delivered, Malaysia Digital status, and a clear path from assessment to live AI.",
    keywords:
      "digital transformation services Malaysia, digital transformation company Malaysia, digital transformation consultancy, digital transformation consultants KL, digital transformation advisory Malaysia, business automation Selangor, digital strategy Malaysia",
    localized: {
      ms: {
        title: "Perkhidmatan Transformasi Digital Malaysia | Dari Strategi ke Pelaksanaan",
        description:
          "Konsultansi transformasi digital disahkan MDEC untuk penilaian, automasi perniagaan, adopsi AI dan pelaksanaan teknologi bagi organisasi Malaysia.",
        keywords:
          "perkhidmatan transformasi digital Malaysia, syarikat transformasi digital Malaysia, perunding transformasi digital KL, automasi perniagaan Selangor, strategi digital Malaysia",
      },
      zh: {
        title: "马来西亚数字化转型服务 | 从策略到落地执行",
        description:
          "MDEC 认证的数字化转型咨询服务，支持马来西亚组织进行评估、业务自动化、AI 采用和技术落地。",
        keywords:
          "马来西亚数字化转型服务, 马来西亚数字化转型公司, 吉隆坡数字化转型顾问, 雪兰莪业务自动化, 马来西亚数字策略",
      },
    },
    ogImage: "/images/services/svc-digital-transformation.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "services/training-education": {
    // Primary target is the levy-funded buyer ("hrd corp approved ai training
    // provider" — 118 impressions at position 49 before this rewrite). "AI
    // Fluency" is the programme name, not the ranking target: Anthropic and
    // Coursera own that term.
    title: "HRD Corp Claimable AI Training Malaysia | AI Fluency",
    description:
      "HRD Corp approved AI training provider in Malaysia. AI fluency and AI literacy courses for business teams, claimable under SBL Khas against your HRDF levy. Taught on the AI Fluency framework by a firm Certified in the Claude Partner Network.",
    keywords:
      "HRD Corp claimable AI training, HRD Corp approved AI training provider, HRDF AI course, HRDF claimable AI course, AI training Malaysia, AI corporate training Malaysia, corporate AI training Malaysia, AI training for employees Malaysia, SBL Khas AI training, AI fluency training Malaysia, AI literacy training Malaysia, in-house AI training Malaysia, AI training Selangor",
    ogImage: "/images/services/svc-training-education.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Latihan AI Boleh Dituntut HRD Corp Malaysia | AI Fluency",
        description:
          "Penyedia latihan AI yang diluluskan HRD Corp di Malaysia. Kursus kefasihan dan literasi AI untuk pasukan perniagaan, boleh dituntut di bawah SBL Khas daripada levi HRDF anda.",
        keywords:
          "latihan AI boleh dituntut HRD Corp, kursus AI HRDF, latihan AI Malaysia, kursus AI korporat Malaysia, latihan AI untuk pekerja, SBL Khas latihan AI, literasi AI Malaysia",
      },
      zh: {
        title: "马来西亚 HRD Corp 可申领 AI 培训 | AI Fluency",
        description:
          "马来西亚 HRD Corp 认证 AI 培训供应商。面向企业团队的 AI 素养与 AI 应用能力课程，可通过 SBL Khas 从你的 HRDF 征费中申领。",
        keywords:
          "HRD Corp 可申领 AI 培训, HRDF AI 课程, 马来西亚 AI 培训, 企业 AI 培训 马来西亚, 员工 AI 培训, SBL Khas AI 培训, AI 素养培训 马来西亚",
      },
    },
  },
  "services/hardware-software": {
    title: "IT Hardware & Software Vendor in Selangor | IT Procurement Malaysia",
    description:
      "Trusted IT hardware and software vendor in Shah Alam, Selangor. IT procurement for businesses in KL, Petaling Jaya & across Malaysia at competitive prices.",
    keywords:
      "IT hardware Selangor, software vendor Malaysia, IT procurement Shah Alam, hardware supplier Kuala Lumpur, IT vendor KL",
    ogImage: "/images/services/svc-hardware-software.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "services/product-management": {
    title: "Digital Product Development Consultants in Selangor & KL",
    description:
      "Digital product development consultants in Shah Alam, Selangor. Product strategy, UX design, and agile development for businesses in Kuala Lumpur and Malaysia.",
    keywords:
      "product development Selangor, digital product consultants Kuala Lumpur, product management Shah Alam, UX design KL, agile development Malaysia",
    ogImage: "/images/services/svc-product-management.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Perkhidmatan Pengurusan Produk di Malaysia",
        description:
          "Perunding pembangunan produk digital di Shah Alam, Selangor: strategi produk, reka bentuk UX dan pembangunan agile untuk perniagaan di Kuala Lumpur dan Malaysia.",
        keywords:
          "pengurusan produk Malaysia, pembangunan produk Selangor, perunding produk digital Kuala Lumpur, reka bentuk UX KL, pembangunan agile Malaysia",
      },
      zh: {
        title: "马来西亚产品管理服务",
        description:
          "位于雪兰莪莎阿南的数字产品开发顾问，为吉隆坡及马来西亚企业提供产品策略、UX 设计与敏捷开发。",
        keywords:
          "马来西亚产品管理, 雪兰莪产品开发, 吉隆坡数字产品顾问, 吉隆坡 UX 设计, 马来西亚敏捷开发",
      },
    },
  },
  "services/smart-manufacturing": {
    title: "Smart Manufacturing Companies Malaysia | Industry 4.0 & IoT Solutions",
    description:
      "Smart manufacturing solutions for Malaysian factories. IoT sensors, predictive maintenance & real-time analytics. Cut downtime up to 50%. Free assessment.",
    keywords:
      "smart manufacturing Malaysia, smart manufacturing solutions Malaysia, smart manufacturing companies Malaysia, industrial automation Selangor, IoT solutions Malaysia, Industry 4.0 Kuala Lumpur, smart factory Malaysia",
    ogImage: "/images/services/svc-smart-manufacturing.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Penyelesaian Pembuatan Pintar Malaysia | Industri 4.0 & IoT",
        description:
          "Penyelesaian pembuatan pintar untuk kilang di Malaysia: sensor IoT, penyelenggaraan ramalan dan analitik masa nyata. Kurangkan masa henti sehingga 50%. Penilaian percuma.",
        keywords:
          "pembuatan pintar Malaysia, penyelesaian pembuatan pintar, automasi industri Selangor, penyelesaian IoT Malaysia, Industri 4.0 Kuala Lumpur, kilang pintar Malaysia",
      },
      zh: {
        title: "马来西亚智能制造解决方案 | 工业 4.0 与物联网",
        description:
          "为马来西亚工厂提供智能制造解决方案：物联网传感器、预测性维护与实时分析，停机时间最多可减少 50%，提供免费评估。",
        keywords:
          "马来西亚智能制造, 智能制造解决方案, 雪兰莪工业自动化, 马来西亚物联网解决方案, 吉隆坡工业 4.0, 马来西亚智能工厂",
      },
    },
  },
  "services/system-integration": {
    title: "IT System Integrator Malaysia | Business Integration KL",
    description:
      "IT system integrator in Malaysia connecting ERP, CRM, e-commerce and accounting systems — and connecting AI to them via MCP. Serving KL, Selangor, Penang & Singapore.",
    keywords:
      "IT system integrator Malaysia, business integration Malaysia, system integration company KL, IT system integration strategy Malaysia, enterprise integration Kuala Lumpur, API development Selangor, MCP integration Malaysia, AI system integration Malaysia",
    ogImage: "/images/services/svc-system-integration.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "services/website-development": {
    title: "Affordable Web Design Malaysia from RM1,800",
    description:
      "Affordable web design in Malaysia from RM1,800. Own your website with no platform lock-in. Built for businesses by a Malaysian web development company.",
    keywords:
      "affordable web design Malaysia, web design Malaysia, website design Malaysia, web development company Malaysia, web design agency Malaysia, web design Selangor, website development KL, custom website Malaysia, web design Kuala Lumpur",
    localized: {
      ms: {
        title: "Reka Bentuk Web Mampu Milik Malaysia dari RM1,800 | Kualiti Premium",
        description:
          "Reka bentuk web khusus di Malaysia dari RM1,800. Bayaran sekali, milik penuh, tanpa lock-in. Laman profesional untuk SME di KL, Selangor dan seluruh Malaysia.",
        keywords:
          "reka bentuk web Malaysia, reka bentuk web mampu milik Malaysia, harga reka bentuk web Malaysia 2026, pembangunan laman web KL, kos laman web Malaysia, laman web khusus Malaysia",
      },
      zh: {
        title: "马来西亚高性价比网页设计从 RM1,800 起 | 专业品质",
        description:
          "马来西亚定制网页设计从 RM1,800 起。一次性付款、完整拥有权、无绑定，适合吉隆坡、雪兰莪和全马 SME。",
        keywords:
          "马来西亚网页设计, 马来西亚高性价比网页设计, 马来西亚网页设计价格2026, 吉隆坡网站开发, 马来西亚网站费用, 马来西亚定制网站",
      },
    },
    ogImage: "/images/services/wdh-og-2026.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
  },

  // Blog pages
  blog: {
    title: "Tech Insights & Articles | Anchor Sprint Blog",
    description:
      "Explore technology insights, AI tutorials, and digital transformation tips on the Anchor Sprint blog.",
    ogImage: "/images/services/abstract-agentic-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    keywords:
      "technology blog Malaysia, AI insights for Malaysian businesses, software development tips Malaysia, affordable website development Malaysia, Claude for business Malaysia, digital transformation articles KL, enterprise AI trends Selangor",
  },
  "blog/malaysia-ai-governance-bill-2026": {
    title: "Malaysia AI Governance Bill: Business Steps for 2026",
    description:
      "Malaysia AI Governance Bill readiness starts now: register AI use, assign owners, classify risks, and keep evidence. Build a practical compliance plan today.",
    keywords:
      "Malaysia AI Governance Bill, Malaysia AI Governance Bill 2026, Malaysia AI law 2026, responsible AI for business, AI compliance Malaysia, AI risk assessment",
    ogImage: "/blog/malaysia-ai-governance-bill-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1792,
    ogImageHeight: 1024,
    tags: ["News", "AI", "Compliance", "Malaysia Business"],
  },
  "blog/ai-as-service-not-system": {
    title:
      "How AI Will Change the Way We Do Business - AI as a Service, Not Just a System",
    description:
      "AI is no longer just software you configure - it is now a service delivering outcomes. Explore how AI agents are reshaping business operations in Malaysia.",
    keywords:
      "AI as a service Malaysia, AI agents Malaysia, agentic AI for business, Malaysian business AI adoption, AI operations automation, AI service delivery",
    ogImage: "/blog/ai-as-service-not-system/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1408,
    ogImageHeight: 768,
    tags: ["Insights", "AI", "AI Agents", "Malaysia Business"],
  },
  "blog/ai-process-automation-malaysia": {
    title: "AI Process Automation for Malaysian Businesses: A Practical Guide",
    description:
      "A practical guide to AI process automation for Malaysian businesses - from customer service to finance. Learn what to automate, what it costs, and how to start.",
    keywords:
      "AI process automation Malaysia, business automation Malaysia, AI workflow automation Selangor, enterprise automation Kuala Lumpur, Malaysian business AI guide",
    ogImage: "/blog/ai-process-automation-malaysia/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1408,
    ogImageHeight: 768,
    tags: ["AI Agents", "Automation", "Malaysia Business", "Enterprise"],
  },
  "blog/ai-seo-agent-malaysia": {
    title: "What Is an AI SEO Agent? Malaysia Business Guide",
    description:
      "Learn how an AI SEO Agent monitors search performance, proposes website improvements, waits for approval, applies changes, and reports results.",
    keywords:
      "AI SEO agent Malaysia, what is an AI SEO agent, SEO agent Malaysia, SEO automation Malaysia, AI agent for SEO, approval-gated SEO agent",
    ogImage: "/images/blogs/ai-seo-agent-malaysia-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["AI SEO", "AI Agents", "Malaysia Business"],
  },
  "blog/seo-price-malaysia-2026": {
    title: "SEO Price Malaysia 2026: What Should a Business Pay?",
    description:
      `Compare SEO prices in Malaysia, from ${rm(SEO_PLANS.essential.monthly)} AI SEO plans to agency retainers. See what each level should include and where cheap SEO becomes risky.`,
    keywords:
      "SEO price Malaysia, SEO cost Malaysia, SEO package Malaysia, affordable SEO Malaysia, monthly SEO Malaysia, SEO services price Malaysia, small business SEO Malaysia, how much does SEO cost in Malaysia",
    ogImage: "/images/blogs/seo-price-malaysia-2026-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["SEO", "Malaysia Business", "Website Development"],
  },
  "blog/website-cost-malaysia-2026": {
    title: "Website Cost Malaysia 2026: Real Prices & Hidden Fees",
    description:
      "How much does a website cost in Malaysia in 2026? Real RM pricing from RM500 freelancer builds to RM30,000 custom — plus hidden fees to avoid.",
    keywords:
      "website cost Malaysia 2026, how much website cost Malaysia, website price Malaysia 2026, website design Malaysia cost, cost to build website Malaysia, affordable website Malaysia, web design cost business Malaysia, website pricing KL Selangor, custom website Malaysia RM1800",
    ogImage: "/blog/website-cost-malaysia-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Website Development", "Malaysia Business", "Web Design"],
  },
  "blog/malaysia-web-design-pricing-2026-wordpress-vs-code": {
    title: "Web Design Malaysia Price: WordPress vs Code",
    description:
      "Compare web design Malaysia prices for WordPress and custom code over 3 years. See real RM build, plugin, hosting, maintenance, and ownership costs.",
    keywords:
      "web design Malaysia price, web design price Malaysia, website design price Malaysia, Malaysia web design pricing 2026, WordPress vs custom website cost Malaysia, WordPress total cost of ownership Malaysia, web design pricing comparison Malaysia, static site vs WordPress price, website maintenance cost Malaysia",
    ogImage: "/blog/malaysia-web-design-pricing-2026-wordpress-vs-code/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Website Development", "Web Design", "WordPress"],
  },
  "blog/web-design-company-selangor-kl": {
    title: "Web Design Company Malaysia: Buyer Checklist",
    description:
      "Choosing a web design company in Malaysia? Compare price, ownership, SEO, speed, support, scope, and 3-year cost, with practical Selangor and KL guidance.",
    keywords:
      "web design company Malaysia, web design agency Malaysia, web design company in Malaysia, web design company Selangor, web design company KL, website development KL, web design services in Selangor, web design Shah Alam, website development Selangor",
    ogImage: "/images/services/wdh-og-2026.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["Website Development", "Selangor", "Kuala Lumpur"],
  },
  "blog/n8n-ai-agent-vs-claude-agent-sdk": {
    title: "n8n AI Agent vs Claude Agent SDK: When to Switch",
    description:
      "n8n handles 400k executions without complaint — the fortieth workflow is what breaks. Where the maintenance bill comes from, and which jobs belong on the Claude Agent SDK.",
    keywords:
      "n8n ai agent, n8n alternative, claude agent sdk, n8n vs claude, n8n limitations, n8n workflow maintenance, n8n scaling problems, ai agent development Malaysia, ai agent development Singapore, when to move off n8n, custom ai agent vs n8n",
    ogImage: "/blog/n8n-ai-agent-vs-claude-agent-sdk/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["AI Agents", "Automation", "Malaysia", "Singapore"],
  },
  "blog/system-one-models-typesafe-jev": {
    // Vendor-news explainer. The demand term is the ENTITY (TypeSafe / System One
    // Models / Jev), not a geography -- deliberately no "-malaysia" in the slug,
    // because there is no sourced Malaysian material on this topic and a geo slug
    // would promise local intent the body cannot satisfy. The Malaysia references
    // in the body are first-party experience framing only.
    // Does NOT target "AI agent development Malaysia" -- that belongs to
    // /services/ai-agents/, whose title claims it. One term, one page.
    title: "TypeSafe's System One Models and Jev: The 95% Problem",
    description:
      "TypeSafe's Jev gives up string generation for typed, calibrated decisions. What 'can't hallucinate' really means, and six reasons to discount the numbers.",
    keywords:
      "TypeSafe AI, System One Models, Jev model, calibrated decisions AI, structured output model, can AI hallucinate, schema conformance LLM, AI automation decisions",
    ogImage: "/blog/system-one-models-typesafe-jev/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["AI Agents", "Insights"],
  },
  "blog/what-claude-partner-actually-means": {
    // Deliberately NOT targeting "claude partner" / "anthropic partner" — those are
    // owned by /partners/anthropic-claude-partner/ at p6.8-9.2. This takes the
    // buyer-education intent instead: how to evaluate any vendor's partner claim.
    title: "What 'Claude Partner' Actually Means (And What It Doesn't)",
    description:
      "Anchor Sprint is Certified in the Claude Partner Network with Claude Certified Architects on the team. Here is what that status actually means, what it does not, and four questions worth asking any AI vendor in Malaysia.",
    keywords:
      // "official Claude partner Malaysia" REMOVED 2026-09-21. This post's own
      // body says Anthropic does not designate an official partner for a
      // country, so targeting the phrase had us bidding to rank for a claim we
      // debunk three screens below. Every other occurrence on the site is a FAQ
      // question we answer "no" to; this was the only one stated as a target.
      "what is a Claude partner, Anthropic Claude Partner Network explained, Claude Certified Architect, how to choose an AI vendor Malaysia, verify AI partner claims, how to check a Claude partner claim",
    ogImage: "/blog/what-claude-partner-actually-means/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["Claude", "AI Agents", "Malaysia", "Singapore"],
  },
  "blog/what-is-an-openai-select-partner": {
    // OWNER (2026-10-08) of the informational, non-geo terms "OpenAI Select
    // Partner", "OpenAI Partner Network", "OpenAI partner tiers" and "what is an
    // OpenAI partner". /partners/openai-partner/ owns the Malaysia geo terms.
    // Facts: docs/research/openai-partner-network.md. Never links the Partner
    // Locator or OpenAI's partner listing pages (Jazz); no vendor comparison.
    title: "What Is an OpenAI Select Partner? The 3 Partner Tiers",
    description:
      "What an OpenAI Select Partner is, the three OpenAI Partner Network tiers, what a partner should do for you, and six questions to check any partner's claim.",
    keywords:
      "OpenAI Select Partner, OpenAI Partner Network, OpenAI partner tiers, what is an OpenAI partner, OpenAI Partner Network tiers, Select Advanced Elite OpenAI, OpenAI Partner Locator, how to check an OpenAI partner",
    ogImage: "/blog/what-is-an-openai-select-partner/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["OpenAI", "Insights", "Malaysia", "Singapore"],
  },
  "blog/intern-story-product-growth": {
    // Intern feature (2026-10-03), quote-led, consent recorded in
    // docs/research/intern-story-suzanne-2026.md. Owns the internship terms;
    // /careers/ keeps "tech internship Malaysia" + "software engineer intern Selangor".
    title: "AI Internship in Malaysia: A Product Growth Intern's Story",
    description:
      "An AI internship in Malaysia, in her own words: how our Product Growth Intern went from no technical background to explaining agentic AI in three months.",
    keywords:
      "AI internship Malaysia, marketing internship AI company Malaysia, product growth internship Malaysia, intern story agentic AI, University of Nottingham Malaysia internship",
    ogImage: "/blog/intern-story-product-growth/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["Careers", "AI Agents", "Malaysia"],
    localized: {
      ms: {
        title: "Latihan Industri AI di Malaysia: Kisah Pelatih Kami",
        description:
          "Bagaimana rupa latihan industri di syarikat AI di Malaysia: Pelatih Product Growth kami belajar AI agentik dengan menulis tentangnya, dalam kata-katanya sendiri.",
        keywords:
          "latihan industri AI Malaysia, internship syarikat AI Malaysia, latihan industri pemasaran Malaysia, pelatih product growth Malaysia, kisah pelatih AI agentik",
      },
      zh: {
        title: "马来西亚 AI 实习：一位产品增长实习生的三个月",
        description:
          "在马来西亚 AI 公司实习是什么样子？我们的产品增长实习生通过写作学会了智能体 AI。这里摘录她的几句话，并附上她的完整文章。",
        keywords:
          "马来西亚 AI 实习, 马来西亚 AI 公司 实习, 马来西亚 营销实习, 产品增长实习 马来西亚, 智能体 AI 实习生",
      },
    },
  },
  "blog/claude-partner-network-certified": {
    // Announcement post (2026-10-03). Deliberately NOT targeting "claude partner
    // malaysia" / "certified claude partner malaysia" (owned by
    // /partners/anthropic-claude-partner/) or "why build on Claude" (owned by
    // /anthropic-claude-partner-malaysia-singapore/). This takes the news intent.
    // The approved line stays English on every locale (CPN email 2026-10-02).
    title: "Anchor Sprint is Certified in the Claude Partner Network: why we chose Anthropic",
    description:
      "Anchor Sprint is Certified in the Claude Partner Network. Why we chose Claude, powered by Anthropic, and the agent future we see for Malaysian businesses.",
    keywords:
      "Claude Partner Network Certified, Claude Partner Network certification, Anchor Sprint Claude Partner Network, why we chose Anthropic, Anthropic AI safety agents, Model Context Protocol open standard, future of AI agents Malaysian businesses",
    ogImage: "/blog/claude-partner-network-certified/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["Claude", "AI Agents", "Malaysia", "Singapore"],
    localized: {
      ms: {
        title: "Anchor Sprint is Certified in the Claude Partner Network: mengapa kami memilih Anthropic",
        description:
          "Anchor Sprint is Certified in the Claude Partner Network. Mengapa kami memilih Claude, dikuasakan oleh Anthropic, dan masa depan ejen AI untuk perniagaan Malaysia.",
        keywords:
          "Claude Partner Network Certified, rangkaian rakan kongsi Claude, mengapa pilih Anthropic, ejen AI perniagaan Malaysia, masa depan ejen AI Malaysia, Model Context Protocol, keselamatan AI Anthropic",
      },
      zh: {
        title: "Anchor Sprint is Certified in the Claude Partner Network：我们为何选择 Anthropic",
        description:
          "Anchor Sprint is Certified in the Claude Partner Network. 我们为何选择由 Anthropic 打造的 Claude，以及我们为马来西亚企业构想的 AI 智能体未来。",
        keywords:
          "Claude Partner Network Certified, Claude 合作伙伴网络, 为什么选择 Anthropic, 马来西亚企业 AI 智能体, AI 智能体 未来, Model Context Protocol, Anthropic AI 安全",
      },
    },
  },
  "blog/connect-ai-to-business-systems-mcp": {
    title: "How AI Connects to Your Business Systems (MCP Explained)",
    description:
      "How AI gets access to your ERP, CRM and accounting system — MCP explained in business terms, what it costs in Malaysia, and when you do not need AI at all.",
    keywords:
      "connect AI to business systems, what is MCP business, Model Context Protocol business, AI ERP integration Malaysia, AI system integration Malaysia, AI CRM integration Singapore, MCP Malaysia",
    ogImage: "/blog/connect-ai-to-business-systems-mcp/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["AI Agents", "Integration", "Malaysia", "Singapore"],
  },
  "blog/chatbot-vs-ai-agent-2026": {
    title:
      "Chatbot vs AI Agent: What Businesses Should Really Build in 2026",
    description:
      "A chatbot answers; an agent completes the task. Where the line sits, what an agent costs to run, and how to tell which one your workflow actually needs.",
    keywords:
      "chatbot vs AI agent, AI agent vs chatbot difference, when to build an AI agent, task-owning AI, agent vs assistant, AI agent ROI",
    ogImage:
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["AI Agents", "Automation", "Malaysia"],
  },
  "blog/what-is-prompt-engineering": {
    title:
      "Prompt Engineering Guide 2026: Techniques, Strategies & Best Practices",
    description:
      "Master prompt engineering in 15 minutes. Learn chain-of-thought, role prompting, and structured output techniques for ChatGPT, Claude, and modern AI models.",
    keywords:
      "prompt engineering, what is prompt engineering, AI prompts, ChatGPT, chain-of-thought prompting, few-shot prompting, role prompting, prompt design, structured output, prompt templates",
    ogImage: "/images/prompt-engineering.png",
    ogImageType: "image/png",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["AI", "Prompt Engineering"],
  },
  "blog/adobe-creative-cloud-reseller-malaysia-local-support": {
    title:
      "Why Malaysian Creative Agencies Choose Adobe Resellers Over Direct Licensing",
    description:
      "Why creative agencies in Malaysia choose Adobe resellers over direct licensing: local support, ETLA pricing, and RM18,000+ annual savings.",
    keywords:
      "Adobe reseller Malaysia, Adobe Creative Cloud Selangor, Adobe certified reseller KL, Adobe licensing Malaysia, Creative Cloud support, Adobe team licenses, Adobe enterprise agreement Malaysia, ETLA VIP Malaysia, Adobe for agencies",
    ogImage:
      "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1200&h=630&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["Adobe", "Creative", "Malaysia", "Business"],
  },
  // Bank education: source code review vs penetration testing; RMiT requirements remain on the existing RMiT guide.
  "blog/source-code-review-vs-penetration-testing-banks": {
    title: "Source Code Review vs Penetration Testing for Banks",
    description: "Compare source-code review and penetration testing for banks: what each checks, where automated scans fit, and how to agree scope in Malaysia and Singapore.",
    keywords: "source code review vs penetration testing, code review vs pentest banks, bank application security testing, manual code review and SAST",
    ogImage: "/blog/source-code-review-vs-penetration-testing-banks/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: [
      "SonarQube",
      "Compliance",
      "Malaysia",
      "Singapore"
    ]
  },

  // Bank education: software vendor handover; RMiT requirements remain on the existing RMiT guide.
  "blog/bank-software-vendor-handover": {
    title: "Bank Software Vendor Handover: Code Quality Checklist",
    description: "A software vendor handover checklist for bank application owners: match reports to the delivered release, review open findings and agree who fixes what next.",
    keywords: "software vendor handover, bank software handover checklist, vendor code quality evidence, software delivery acceptance banks, source code review report version",
    ogImage: "/blog/bank-software-vendor-handover/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: [
      "SonarQube",
      "Compliance",
      "Malaysia",
      "Singapore"
    ]
  },

  "blog/buy-sonarqube-direct-or-local-reseller": {
    title: "Buy SonarQube Direct or Through a Local Reseller?",
    description: "Buy SonarQube direct or through a local reseller? Compare when each option makes sense, RM and SGD quotes, setup help and what to send for a quote.",
    keywords: "SonarQube direct vs reseller, buy SonarQube Malaysia, SonarQube SGD quote, SonarQube RM quote, software procurement Singapore",
    ogImage: "/blog/buy-sonarqube-direct-or-local-reseller/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: [
      "SonarQube",
      "Procurement",
      "Malaysia",
      "Singapore"
    ]
  },

  "blog/overseas-software-purchases-malaysia": {
    title: "Overseas Software Purchases in Malaysia: Tax Checks",
    description: "Planning overseas software purchases in Malaysia? Check withholding tax, seller details, e-Invoice documents and local reseller quotes before approving payment.",
    keywords: "overseas software purchases Malaysia, software withholding tax Malaysia, local software reseller Malaysia, software invoice Malaysia, self-billed e-Invoice software",
    ogImage: "/blog/overseas-software-purchases-malaysia/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: [
      "Procurement",
      "Malaysia",
      "Software"
    ]
  },

  "blog/sonarqube-pricing-lines-of-code": {
    // SonarQube cluster (spec 2026-09-25). Owns the pricing-mechanics terms;
    // geo-commercial terms stay on enterprise/sonarqube-malaysia and singapore/sonarqube.
    // Rewrite 2026-10-25/26: US$34 Team price re-verified on Sonar's page 25 Oct 2026; RM/SGD are
    // BNM 12:00 middle-rate conversions for 23 Oct 2026 (research C57-C80). Refresh when C57 changes.
    title: "SonarQube Pricing 2026: Cloud from US$34, Server by Quote",
    description:
      "SonarQube pricing: Cloud Team from US$34/month, about RM139 or S$43 (BNM 12:00 middle rates, 23 Oct 2026; not a quote). Server editions by quote.",
    keywords:
      "SonarQube pricing, SonarQube price, SonarQube cost, SonarQube price Malaysia, SonarQube price Singapore, SonarQube license cost, SonarQube licence cost, SonarQube Developer Edition price, SonarQube Enterprise pricing, SonarQube Cloud pricing, SonarQube pricing lines of code, SonarQube Cloud vs Server",
    ogImage: "/blog/sonarqube-pricing-lines-of-code/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["SonarQube", "DevOps", "Malaysia Business", "Singapore"],
  },
  "blog/sonarqube-community-vs-developer-vs-enterprise": {
    // SonarQube cluster (spec 2026-09-25). Owns the edition-choice terms only;
    // pricing/licence/Cloud-vs-Server terms stay on blog/sonarqube-pricing-lines-of-code.
    title: "SonarQube Community vs Developer vs Enterprise: When to Upgrade",
    description:
      "SonarQube Community vs Developer vs Enterprise: the free build scans only main, so pull requests merge unscanned. What each edition adds, plus a checklist.",
    keywords:
      "SonarQube Community vs Developer, SonarQube Community Edition limitations, SonarQube Community Build limitations, SonarQube Developer vs Enterprise, which SonarQube edition, SonarQube editions compared",
    ogImage: "/blog/sonarqube-community-vs-developer-vs-enterprise/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["SonarQube", "DevOps", "Malaysia Business", "Singapore"],
  },
  "blog/rmit-source-code-review-malaysia": {
    // SonarQube cluster, banks exception (Jazz, 2026-10-06). Sole owner of "RMiT source
    // code review", "BNM RMiT secure SDLC" and "RMiT application security testing".
    // /enterprise/sonarqube-malaysia/ owns "SAST for banks Malaysia" / "RMiT code scanning";
    // /services/enterprise-resilience/ owns the RMiT resilience-testing terms.
    // Facts: docs/research/sonar-keywords-compliance-2026-10-06.md §8.
    title: "RMiT Source Code Review: What BNM Requires, How to Prove It",
    description:
      "RMiT source code review explained: what BNM's Sep 2026 RMiT asks of code, APIs and vendors' secure SDLC, the evidence auditors want, and SonarQube's role.",
    keywords:
      "RMiT source code review, BNM RMiT secure SDLC, RMiT application security testing, RMiT 10.10 source code review, BNM RMiT API security testing, RMiT third party secure by design",
    ogImage: "/blog/rmit-source-code-review-malaysia/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["SonarQube", "Compliance", "Malaysia Business", "Singapore"],
    // Per-locale keywords (not translated EN lists): BM searchers write "semakan kod sumber",
    // "keperluan RMiT Bank Negara"; ZH searchers write "RMiT 源代码审查", "国家银行 RMiT".
    localized: {
      ms: {
        title: "Semakan Kod Sumber RMiT: Apa Yang BNM Mahu, Cara Membuktikannya",
        description:
          "Semakan kod sumber RMiT dijelaskan: apa yang RMiT BNM (Sep 2026) minta untuk kod, API dan SDLC selamat vendor, bukti yang juruaudit mahu, dan peranan SonarQube.",
        keywords:
          "semakan kod sumber RMiT, keperluan RMiT Bank Negara, RMiT BNM SDLC selamat, ujian keselamatan aplikasi RMiT, ujian keselamatan API RMiT, bukti audit RMiT, semakan kod bank Malaysia, perenggan 10.10 RMiT",
      },
      zh: {
        title: "RMiT 源代码审查：BNM 的要求，以及如何证明",
        description:
          "RMiT 源代码审查详解：国家银行（BNM）2026年9月版 RMiT 对代码、API 和供应商安全 SDLC 的要求，审计师要看的证据，以及 SonarQube 的作用。",
        keywords:
          "RMiT 源代码审查, 国家银行 RMiT 要求, BNM RMiT 安全开发生命周期, RMiT 应用安全测试, RMiT API 安全测试, RMiT 审计证据, 马来西亚银行代码审查, RMiT 第三方供应商要求",
      },
    },
  },
  "blog/sonarqube-ai-code-assurance": {
    // SonarQube cluster (spec 2026-09-25). Owns AI Code Assurance / AI CodeFix /
    // "AI code review quality gate" only; pricing and edition-choice terms stay on the
    // two sibling SonarQube posts.
    title: "SonarQube AI Code Assurance: How the AI Quality Gate Works",
    description:
      "SonarQube AI Code Assurance runs AI-written code through a quality gate built for it before merge. The six conditions, where AI CodeFix fits, and setup steps.",
    keywords:
      "SonarQube AI Code Assurance, SonarQube AI CodeFix, AI code review quality gate, Sonar way for agentic AI, quality gate for AI-generated code, SonarQube AI-generated code",
    ogImage: "/blog/sonarqube-ai-code-assurance/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["SonarQube", "DevOps", "Malaysia Business", "Singapore"],
  },
  "blog/what-is-sast-tool-malaysia-singapore": {
    // SonarQube cluster (2026-10-26). Sole owner of the category terms "sast", "sast tool",
    // "static code analysis", "what is sast", "sast vs dast" and "sonarqube vs snyk" for
    // Malaysia and Singapore. The two service pages keep their country-suffixed SAST terms
    // and link here from their #sast sections.
    title: "What Is SAST? Static Code Analysis Tools Compared (MY & SG)",
    description:
      "SAST and static code analysis explained, six SAST tools compared, and what RMiT and MAS TRM say. SonarQube in RM or SGD from a Sonar reseller partner.",
    keywords:
      "SAST, SAST tool, what is SAST, static code analysis, static application security testing, SAST vs DAST, SAST tools comparison, SonarQube vs Snyk, SAST Malaysia, SAST Singapore, static code analysis Malaysia, static code analysis Singapore",
    ogImage: "/blog/what-is-sast-tool-malaysia-singapore/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["SonarQube", "DevOps", "Malaysia Business", "Singapore"],
  },
  "blog/who-reviews-ai-generated-code": {
    // SonarQube cluster (2026-10-07). Sole owner of "who reviews AI-generated code",
    // "code review for AI coding agents" and "review AI agent pull requests". Product-line
    // geo terms (Gitar / Remediation Agent / Hunter Agent Malaysia) stay on
    // enterprise/sonarqube-malaysia; AI Code Assurance terms stay on blog/sonarqube-ai-code-assurance.
    // Facts: docs/research/agent-code-review-sonar-2026-10-07.md. Two gl=my SERP pulls 2026-10-07.
    title: "Who Reviews AI-Generated Code When Agents Write It?",
    description:
      "Who reviews AI-generated code when agents open pull requests? The checks at each stage, the Sonar plan each needs, and the sign-off only a person can give.",
    keywords:
      "who reviews AI-generated code, code review for AI coding agents, review AI agent pull requests, AI agent pull request review, Gitar AI code review, Sonar Vortex, SonarQube Hunter Agent, SonarQube Remediation Agent, segregation of duties AI code",
    ogImage: "/blog/who-reviews-ai-generated-code/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["SonarQube", "AI Agents", "Malaysia Business", "Singapore"],
    // Per-locale keywords: BM "semakan kod janaan AI"; ZH "AI 生成代码审查" / "谁来审查 AI 代码".
    // Product names stay English in every locale (searched that way).
    localized: {
      ms: {
        title: "Siapa Menyemak Kod Janaan AI Apabila Ejen Yang Menulisnya?",
        description:
          "Siapa menyemak kod janaan AI apabila ejen membuka pull request? Semakan di setiap peringkat, pelan Sonar yang diperlukan, dan kelulusan yang hanya manusia boleh beri.",
        keywords:
          "semakan kod janaan AI, siapa semak kod AI, semakan pull request ejen AI, semakan kod ejen pengekodan AI, pengasingan tugas kod AI, keselamatan kod janaan AI, Gitar semakan kod AI, Sonar Vortex, SonarQube Remediation Agent, SonarQube Hunter Agent",
      },
      zh: {
        title: "AI 智能体写代码，谁来审查 AI 生成的代码？",
        description:
          "AI 智能体提交拉取请求时，谁来审查 AI 生成的代码？各阶段的检查、每一层所需的 Sonar 方案，以及只有人才能给出的最终批准。",
        keywords:
          "AI 生成代码审查, 谁来审查 AI 代码, AI 编程智能体代码审查, AI 智能体 PR 审查, AI 代码职责分离, AI 生成代码安全, Gitar AI 代码审查, Sonar Vortex, SonarQube Remediation Agent, SonarQube Hunter Agent",
      },
    },
  },
  "blog/ai-at-work-malaysia-what-to-do-now-2026": {
    title:
      "AI at Work in Malaysia: What Every Company Should Do Now",
    description:
      "Your staff already use AI daily; your company probably lags. The two mistakes to avoid — and the awareness, training, and agent steps to take now.",
    keywords:
      "AI at work Malaysia, AI in the workplace Malaysia, AI adoption Malaysia business, what companies should do AI Malaysia, AI training Malaysia, AI awareness Malaysia, AI upskilling Malaysia, future of work Malaysia, AI governance Malaysia, custom AI agents Malaysia",
    ogImage: "/blog/ai-at-work-malaysia-what-to-do-now-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Insights", "AI", "Malaysia"],
  },
  "blog/ai-at-work-challenges-malaysia-2026": {
    title:
      "AI at Work in Malaysia: Why Teams Adopt the Tools but Miss the Value (2026)",
    description:
      "Malaysian teams have adopted AI but see no measurable payoff. The real AI at work challenges are organisational — training, leadership, workflow. Here's the fix.",
    keywords:
      "AI at work challenges, AI adoption challenges Malaysia, AI training Malaysia, AI training Malaysia business, why AI fails at work, AI upskilling Malaysia, HRD Corp AI training, AI workforce training Malaysia",
    ogImage: "/blog/ai-at-work-challenges-malaysia-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Insights", "AI", "Malaysia Business"],
  },
  "blog/ai-adoption-roadmap-malaysian-businesses": {
    // 201 impressions, ZERO clicks, ranking p5.6 for "ai adoption roadmap for
    // enterprises malaysia" — a page-1 position earning nothing. Two problems,
    // both in the title: it said "SMEs" where the query says businesses (buyers
    // do not self-describe as SMEs — see the standing no-SME-keyword rule), and
    // the old subtitle "A Step-by-Step Guide to Embracing AI" promised nothing
    // a searcher wants. "Where to Start" is the actual question being asked.
    // NOT retitled to "Enterprises" despite the query wording: the body is
    // written for SMEs and mid-market, and claiming otherwise would be false.
    title:
      "AI Adoption Roadmap for Malaysian Businesses: Where to Start",
    description:
      "A practical AI adoption roadmap for Malaysian businesses: where to start, how to run a pilot that proves value, and which government programmes help fund it.",
    keywords:
      "AI adoption Malaysia, AI adoption roadmap Malaysia, AI roadmap Malaysian business, AI pilot Malaysia, Malaysian government AI grants, AI untuk Rakyat, digital transformation Malaysia",
    ogImage:
      "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1200&h=630&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["AI", "Business", "Digital Transformation"],
  },
  "blog/google-gemini-25-computer-use": {
    title:
      "Google Gemini 2.5 Computer Use for Business",
    description:
      "Google Gemini 2.5 Computer Use explained — how Anchor Sprint applies browser-native agents to Malaysian enterprise automation, QA & customer journeys.",
    keywords:
      "Gemini 2.5 Computer Use, Google AI agents, browser automation Malaysia, Anchor Sprint AI services, Malaysian enterprise automation, Gemini benchmark performance",
    ogImage:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&h=630&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["AI Agents", "Automation", "Anchor Sprint"],
  },
  "blog/b2b-vs-b2c-ai-assistants-2026": {
    title:
      "B2B vs B2C AI Assistants 2026: Malaysia Guide",
    description:
      "The operational, integration & governance differences between B2B and B2C AI assistants, so Malaysia & Singapore businesses deploy the right architecture in 2026.",
    keywords:
      "B2B AI assistants, B2C AI assistants, AI automation Malaysia, AI automation Singapore, business AI strategy, AI governance enterprise, agentic AI 2026",
    ogImage:
      "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1200&h=630&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["AI Assistants", "B2B", "Business"],
  },
  "blog/why-whatsapp-ai-assistants-next-big-thing-business-2026": {
    title:
      "Why WhatsApp AI Assistants Are the Next Big Thing for Business (2026 Edition)",
    description:
      "WhatsApp-first AI assistants close leads after hours, standardise answers, and lift conversion. See the typical stack and revenue impact.",
    keywords:
      "WhatsApp AI assistant, conversational commerce Malaysia, AI customer service, WhatsApp automation 2026, Anchor Sprint AI, 24/7 chat response",
    ogImage:
      "https://images.unsplash.com/photo-1545239351-1141bd82e8a6?auto=format&fit=crop&w=1200&h=630&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["WhatsApp", "AI Assistants", "Customer Experience"],
  },
  "blog/lazada-marketplace-fees-ai-commerce": {
    title:
      "Lazada Marketplace Fees Rise: Own Your AI Store",
    description:
      "Lazada is lifting seller commissions up to 22.5%. How Malaysian brands protect margins with AI-enabled owned commerce while keeping marketplaces in the loop.",
    keywords:
      "Lazada commission increase, Malaysia marketplace fees 2026, AI ecommerce strategy, owned storefront Malaysia, Anchor Sprint retail transformation, business omnichannel resilience",
    ogImage: "/images/lazada-marketplace-fees-ai.svg",
    ogImageType: "image/svg+xml",
    ogImageWidth: 1600,
    ogImageHeight: 900,
    tags: ["E-commerce", "AI", "Business"],
  },
  "blog/aws-cloud-cost-optimisation-case-study-malaysia": {
    title:
      "Cut AWS Cloud Costs 20%: Malaysia Case Study",
    description:
      "Case study: how Anchor Sprint cut a Selangor publisher's AWS spend by 20% (RM12k/month) using Savings Plans and Graviton3 — with zero downtime.",
    keywords:
      "AWS cost optimisation Malaysia, AWS cost optimization Selangor, AWS cost optimization Kuala Lumpur, Savings Plans case study Malaysia, ACM managed certificates, AWS Graviton3 migration, zero downtime SSL cutover, FinOps Malaysia, AWS Shah Alam, cloud cost reduction Malaysia, AWS Certificate Manager Malaysia, Compute Savings Plans Malaysia",
    ogImage:
      "https://images.unsplash.com/photo-1506765515384-028b60a970df?auto=format&fit=crop&w=1200&h=630&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["AWS", "FinOps", "Case Study", "Selangor", "Kuala Lumpur"],
  },
  "blog/aws-malaysia-region-vs-singapore-cloud-cost-savings": {
    title:
      "AWS Malaysia Region vs Singapore: Business Savings",
    description:
      "With AWS launching its Malaysia region, businesses can cut cloud costs 10–20% by migrating from Singapore. Get the migration playbook and Anchor Sprint support.",
    keywords:
      "AWS Malaysia region, AWS Singapore migration, cloud cost optimisation Malaysia, business FinOps strategy, Anchor Sprint AWS services",
    ogImage:
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&h=630&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["AWS", "Cloud Cost Optimisation", "Business Digitalisation"],
  },
  // News/explainer on Anthropic signing the EU AI Act Article 50(2) Code of
  // Practice. Angle is deliberately NOT "here is the news" -- it is what a
  // mark does and does not prove, because both misreadings (mark = AI wrote
  // it; no mark = human wrote it) are wrong and Anthropic's own docs say so.
  // Facts taken from support.claude.com on 2026-08-11.
  "blog/claude-ai-content-marking-what-it-means": {
    title: "Claude AI Content Marking: What It Actually Proves",
    description:
      "Claude now embeds invisible watermarks in text and C2PA provenance in files — worldwide, not just the EU. What it means if you use Claude, and what it means if you publish for a living.",
    keywords:
      "Claude AI watermark, Claude AI content marking, AI generated content detection, C2PA Claude, EU AI Act Article 50, AI transparency, AI content disclosure Malaysia, AI watermark Singapore, is AI content detectable",
    ogImage: "/blog/claude-ai-content-marking-what-it-means/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["News", "AI", "Claude", "Malaysia"],
  },
  // Targets "prompt engineering for business teams". The HEAD term
  // "prompt engineering" is MY 1K-10K at Low advertiser competition but the
  // SERP is IBM, Wikipedia, DeepLearning.AI, Coursera, OpenAI, Anthropic and
  // McKinsey — unwinnable, do not chase it. The business-teams long tail is a
  // genuine content SERP where positions 2-5 and 7 are small consultancies
  // and SaaS blogs. Also maps to a service we sell (HRD Corp approved).
  "blog/prompt-engineering-business-teams": {
    title: "Prompt Engineering for Business Teams: Beyond Chat Tips",
    description:
      "Nearly all prompt engineering advice assumes you can read the answer and try again. Inside a business system you cannot — and some classic advice has quietly expired.",
    keywords:
      "prompt engineering for business, prompt engineering business teams, enterprise prompt engineering, system prompt design, prompt engineering training Malaysia, AI prompt engineering Singapore, prompt caching, prompt versioning",
    ogImage: "/blog/prompt-engineering-business-teams/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["AI", "Claude", "Malaysia", "Singapore"],
  },
  // Targets "how to use claude code" — MY 100-1K, Low competition, +900% YoY.
  // SERP has Anthropic's own docs at #1 and #7 and freecodecamp high-DA, but
  // #2-#5 are personal setups and substacks written for a single developer.
  // The wedge is the team/governance angle — permission modes and deny rules,
  // verified from code.claude.com/docs on 2026-08-11.
  "blog/how-to-use-claude-code-team": {
    title: "How to Use Claude Code as a Team, Not Just Solo",
    description:
      "Every Claude Code guide is written for one developer. The team questions are permission modes, what it may touch, and where review happens — here is how those controls actually work.",
    keywords:
      "how to use Claude Code, Claude Code team setup, Claude Code permission modes, Claude Code for engineering teams, Claude Code CI, Claude Code deny rules, agentic coding governance, Claude Code Malaysia, Claude Code Singapore",
    ogImage: "/blog/how-to-use-claude-code-team/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["AI", "Claude", "Malaysia", "Singapore"],
  },
  // Spoke of the model-comparison cluster. Targets "claude vs chatgpt" — the
  // largest term in the W33 study at SG 1K-10K, Low competition, +900% YoY.
  // SERP is reddit #1 then a training company, a startup and a CMS vendor at
  // #6; Zapier only #8. Wedge is output-token and long-context pricing, taken
  // from both vendors' own pages on 2026-08-11 (see docs/research/claude-model-lineup-pricing.md).
  // News spoke of the pricing cluster. Anthropic cancelled the 1 Sept 2026 rise and made
  // Sonnet 5's $2/$10 the standard rate. Verified at platform.claude.com on 2026-08-12 —
  // see docs/research/claude-model-lineup-pricing.md before editing any figure here.
  "blog/claude-sonnet-5-pricing-standard": {
    title: "Claude Sonnet 5 Pricing: $2/$10 Is Now the Standard Rate",
    description:
      "The September rise to $3/$15 was cancelled. Once the ~30% tokenizer effect is netted out, Sonnet 5 works out about 13% cheaper per job than Sonnet 4.6 — with RM and SGD figures.",
    keywords:
      "Claude Sonnet 5 pricing, Claude Sonnet 5 price, Sonnet 5 vs Sonnet 4.6 cost, Claude API pricing Malaysia, Claude API pricing Singapore, Claude token cost, Anthropic pricing change, Claude prompt caching cost",
    ogImage: "/blog/claude-sonnet-5-pricing-standard/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["AI", "Claude", "Malaysia", "Singapore"],
    localized: {
      ms: {
        title: "Harga Claude Sonnet 5 Kini Standard — Lebih Murah daripada 4.6",
        description:
          "Kenaikan ke US$3/US$15 pada 1 September dibatalkan. Selepas mengambil kira kesan tokenizer ~30%, Sonnet 5 kira-kira 13% lebih murah setiap tugas berbanding Sonnet 4.6.",
        keywords:
          "harga Claude Sonnet 5, kos Claude Sonnet 5, Sonnet 5 lwn Sonnet 4.6, harga API Claude Malaysia, kos token Claude, perubahan harga Anthropic",
      },
      zh: {
        title: "Claude Sonnet 5 价格转为标准价——比 4.6 更便宜",
        description:
          "9 月 1 日涨至 3/15 美元的计划已取消。计入约 30% 的 tokenizer 影响后，Sonnet 5 每项任务比 Sonnet 4.6 便宜约 13%。",
        keywords:
          "Claude Sonnet 5 价格, Claude Sonnet 5 成本, Sonnet 5 与 Sonnet 4.6, Claude API 价格 马来西亚, Claude token 成本, Anthropic 调价",
      },
    },
  },
  "blog/claude-vs-chatgpt-business": {
    title: "Claude vs ChatGPT: Where the Real Cost Difference Is",
    description:
      "Every comparison leads with input price. For business work the money goes out the other side — and the Claude/ChatGPT gap widens sharply once context runs long.",
    keywords:
      "Claude vs ChatGPT, ChatGPT vs Claude, Claude or ChatGPT for business, GPT-5.6 pricing, Claude Opus 5 vs GPT, AI model cost comparison, Claude vs ChatGPT Malaysia, Claude vs ChatGPT Singapore, output token pricing",
    ogImage: "/blog/claude-vs-chatgpt-business/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["AI", "Claude", "Malaysia", "Singapore"],
    localized: {
      ms: {
        title: "Claude lwn ChatGPT: Di Mana Beza Kos Sebenar",
        description:
          "Semua perbandingan bermula dengan harga input. Untuk kerja perniagaan, wang keluar di sebelah output — dan jurang Claude/ChatGPT melebar apabila konteks menjadi panjang.",
        keywords:
          "Claude lwn ChatGPT, ChatGPT atau Claude untuk perniagaan, banding kos AI, harga token output, Claude Malaysia, ChatGPT Malaysia perniagaan",
      },
      zh: {
        title: "Claude 与 ChatGPT：真正的成本差距在哪里",
        description:
          "所有比较都从输入价格讲起。但企业工作的钱花在输出端，而当上下文变长时，Claude 与 ChatGPT 的差距会明显拉大。",
        keywords:
          "Claude 与 ChatGPT, 企业该选 Claude 还是 ChatGPT, AI 成本比较, 输出 token 价格, Claude 马来西亚, ChatGPT 企业版",
      },
    },
  },
  // Spoke of the model-comparison cluster. Targets "claude vs gemini" — SG
  // 100-1K, Low competition, +900% YoY, and the weakest SERP found in the W33
  // study: gurusup.com holds #1 AND #3, #2 is a meeting-transcription SaaS.
  // Wedge is the context-tier pricing structure, verified on both vendors'
  // own pages — see docs/research/claude-model-lineup-pricing.md.
  "blog/claude-vs-gemini-business": {
    title: "Claude vs Gemini: The Comparison Changes at 200k Tokens",
    description:
      "Gemini's rate doubles past 200k tokens; Claude charges one flat rate across its full 1M window. Why that matters more for agents than the headline price — and where Gemini genuinely wins.",
    keywords:
      "Claude vs Gemini, Gemini vs Claude, Claude or Gemini for business, Gemini 3.1 Pro pricing, Claude Sonnet 5 vs Gemini, AI model comparison Malaysia, AI model comparison Singapore, Gemini context pricing, Claude context window",
    ogImage: "/blog/claude-vs-gemini-business/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["AI", "Claude", "Malaysia", "Singapore"],
    localized: {
      ms: {
        title: "Claude lwn Gemini untuk Perniagaan: Kos Konteks Panjang",
        description:
          "Gemini menaikkan harga input selepas 200k token; Claude mengekalkan satu kadar rata merentas 1 juta token. Untuk kerja dokumen panjang, perbezaan itu yang menentukan bil.",
        keywords:
          "Claude lwn Gemini, Gemini atau Claude untuk perniagaan, harga konteks panjang, kos AI dokumen panjang, Claude Malaysia, Gemini Malaysia perniagaan",
      },
      zh: {
        title: "Claude 与 Gemini 企业对比：长上下文的成本",
        description:
          "Gemini 在 20 万 token 之后提高输入价格；Claude 在整个 100 万 token 窗口维持单一费率。处理长文档时，这一点才是账单的决定因素。",
        keywords:
          "Claude 与 Gemini, 企业该选 Claude 还是 Gemini, 长上下文价格, 长文档 AI 成本, Claude 马来西亚, Gemini 企业",
      },
    },
  },
  // Spoke of the model-comparison cluster; the hub is
  // blog/chatgpt-vs-claude-vs-gemini-malaysia-business (p6.2). Targets
  // "claude opus vs sonnet" — MY 100-1K, Low competition, +900% YoY. The SG
  // SERP is held by a CMS vendor, a job board and a personal blog, and every
  // incumbent is still comparing 4.x models, so currency is the wedge.
  // All figures sourced from platform.claude.com on 2026-08-11; see
  // docs/research/claude-model-lineup-pricing.md before editing.
  "blog/claude-opus-5-vs-sonnet-5": {
    title: "Claude Opus 5 vs Sonnet 5: Which to Run and When",
    description:
      "Opus 5 and Sonnet 5 both carry 1M-token context, so capacity no longer decides it. The tokenizer change matters more than the headline rate — and the September price rise was cancelled.",
    keywords:
      "Claude Opus 5 vs Sonnet 5, Claude Opus vs Sonnet, Claude model comparison, which Claude model, Claude Sonnet 5 pricing, Claude Opus 5 pricing, Claude Fable 5, Claude model routing, Claude API cost Malaysia, Claude models Singapore",
    ogImage: "/blog/claude-opus-5-vs-sonnet-5/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["AI", "Claude", "Malaysia", "Singapore"],
    localized: {
      ms: {
        title: "Claude Opus 5 lwn Sonnet 5: Bila Guna Yang Mana",
        description:
          "Kedua-duanya membawa konteks 1 juta token, jadi kapasiti bukan lagi penentu. Perubahan tokenizer dan kos sebenar setiap tugas lebih penting daripada kadar setiap token.",
        keywords:
          "Claude Opus 5, Claude Sonnet 5, banding model Claude, kos API Claude, model Claude mana untuk perniagaan, harga token Claude Malaysia",
      },
      zh: {
        title: "Claude Opus 5 与 Sonnet 5：何时用哪一个",
        description:
          "两者都有 100 万 token 上下文，容量已不是决定因素。真正影响账单的是 tokenizer 变化与每项任务的实际成本，而非表面的每 token 价格。",
        keywords:
          "Claude Opus 5, Claude Sonnet 5, Claude 模型比较, Claude API 成本, 企业该用哪个 Claude 模型, Claude token 价格 马来西亚",
      },
    },
  },
  "blog/anthropic-api-vs-bedrock-malaysia-2026": {
    // 761 impressions, ZERO clicks (90d). The diagnosis is intent mismatch, not
    // a weak title: its top three queries are "claude ai price malaysia" (p9.0),
    // "claude pricing malaysia" (p7.2) and "claude malaysia price" (p6.8) — all
    // subscription-pricing intent that /guides/claude-plans-malaysia/ owns and
    // answers far better. Deliberately NOT retitled toward pricing: that would
    // put this post in direct competition with the 7,913-impression guide and
    // cost more than it could win. Instead the title is sharpened for this
    // post's own audience — teams choosing a platform — by naming the three
    // axes the decision actually turns on.
    title:
      "Anthropic API vs Bedrock in Malaysia: Cost, Region, PDPA",
    description:
      "Anthropic API vs Amazon Bedrock for Claude in Malaysia: pricing is identical, so the real decision is region, integration, and compliance. 2026 guide.",
    keywords:
      "Anthropic API vs Bedrock, Claude API vs Bedrock, Anthropic direct vs Bedrock Malaysia, Claude on Bedrock cost, Claude pricing Malaysia, Claude Opus 4.6 pricing, Claude Sonnet 4.6 cost, Anthropic API Malaysia PDPA, AWS Bedrock Claude Malaysia, AI gateway Malaysia, AI platform Malaysia business",
    ogImage: "/blog/anthropic-api-vs-bedrock-malaysia-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["AWS", "AI", "Cloud Cost Optimisation"],
    localized: {
      ms: {
        title: "API Anthropic lwn Amazon Bedrock: Mana Lebih Murah di Malaysia",
        description:
          "Harga setiap token adalah sama pada kedua-dua platform. Perbezaan sebenar ialah bil, rantau data dan siapa yang menyelenggara integrasi — bukan kadar token.",
        keywords:
          "API Anthropic lwn Bedrock, Claude di Amazon Bedrock, kos Claude Malaysia, harga API Claude, Bedrock Malaysia, platform AI untuk perniagaan Malaysia",
      },
      zh: {
        title: "Anthropic API 与 Amazon Bedrock：马来西亚哪个更划算",
        description:
          "两个平台的每 token 价格完全相同。真正的差别在账单、数据区域与谁来维护集成，而不是 token 费率。",
        keywords:
          "Anthropic API 与 Bedrock, Amazon Bedrock 上的 Claude, Claude 成本 马来西亚, Claude API 价格, Bedrock 马来西亚, 马来西亚企业 AI 平台",
      },
    },
  },
  "blog/claude-deployment-investment-firms-malaysia": {
    title:
      "Claude for Investment Firms: Enterprise vs AWS vs Google Cloud",
    description:
      "Investment firms choose a Claude deployment, not just a model. A data-sensitivity framework for Enterprise vs AWS Bedrock vs Vertex — residency, MNPI & cost.",
    keywords:
      "Claude deployment investment firm, AI deployment data sensitivity, Claude Enterprise vs Bedrock vs Vertex, AI data residency Malaysia, enterprise AI investment firm, Claude Vertex Singapore residency, MNPI AI controls, BNM RMiT AI, private AI deployment Malaysia, Claude for asset managers, AI governance financial services Malaysia",
    ogImage: "/blog/claude-deployment-investment-firms-malaysia/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Insights", "AI", "Malaysia"],
  },
  "blog/wordpress-vs-custom-web-development": {
    title:
      "WordPress & CMS vs Custom Web Development for Malaysian Business Owners",
    description:
      "See how WordPress, Webflow, and headless CMS + Next.js builds compare for Malaysian businesses with realistic Ringgit budgets and AI-driven savings.",
    keywords:
      "WordPress vs headless CMS Malaysia, Webflow pricing Malaysia, Next.js website cost, AI-assisted web development, Malaysian business website budget, headless CMS Ringgit pricing, web framework affordability",
    ogImage:
      "https://images.unsplash.com/photo-1483478550801-ceba5fe50e8e?auto=format&fit=crop&w=1200&h=630&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["Web Development", "Headless CMS", "AI"],
  },
  "blog/signs-business-outgrown-wordpress-2026": {
    title: "5 Signs Your Business Has Outgrown WordPress (And What to Build Instead)",
    description:
      "Five clear signs your Malaysian or Singapore business has outgrown WordPress — with real RM and SGD numbers, and what to build instead.",
    keywords:
      "outgrown WordPress, WordPress alternatives Malaysia, WordPress alternatives Singapore, WordPress vs custom build, when to leave WordPress, WordPress maintenance cost Malaysia, WordPress total cost ownership, custom website Malaysia, custom website Singapore, Next.js Malaysia, headless CMS Singapore, WordPress migration",
    ogImage: "/blog/signs-business-outgrown-wordpress-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Website Development", "WordPress", "Malaysia Business"],
    localized: {
      zh: {
        title: "WordPress 已经不够用？5大信号告诉你企业网站该升级了（附替代方案）",
        description:
          "WordPress 能带你走到一定阶段，但走不到底。马来西亚和新加坡企业何时该告别 WordPress？5 个明确信号、真实 RM 和 SGD 维护成本、Headless CMS 与 Next.js 替代方案，附升级决策框架。",
        keywords:
          "WordPress 替代方案, WordPress 不够用, WordPress 维护成本, WordPress 插件冲突, WordPress 慢, WordPress 升级, 企业网站升级, 马来西亚定制网站, 新加坡定制网站, Next.js 马来西亚, headless CMS 新加坡, WordPress 迁移, 网站重建, 网站性能优化, 马新企业",
      },
    },
  },
  "blog/php2shell-php-vulnerability-wordpress-malaysia": {
    title: "PHP2Shell on WordPress: How to Fix the PHP Vulnerability",
    description:
      "How to fix PHP2Shell on WordPress and cPanel for Malaysia and Singapore businesses: patched PHP versions, what governments did, and the CVE-2026-115560 follow-up.",
    keywords:
      "PHP2Shell, how to fix PHP2Shell, PHP2Shell WordPress, PHP2Shell patch, PHP2Shell check, PHP2Shell cPanel, PHP2Shell patched versions, PHP2Shell Malaysia, PHP2Shell Singapore, MyCERT PHP2Shell, CSA PHP2Shell, CVE-2026-109618, CVE-2026-109619, CVE-2026-115560, PHP vulnerability, PHP vulnerability WordPress, PHP RCE, PHP 8.2.35, PHP 8.3.36, session.upload_progress.enabled, PHP end of life, WordPress security Malaysia, WordPress security Singapore, WordPress migration Malaysia, WordPress to Next.js, headless CMS Malaysia, website security Malaysia, PDPA data breach notification",
    ogImage: "/blog/php2shell-php-vulnerability-wordpress-malaysia/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Website Development", "WordPress", "Security"],
  },
  "blog/wordpress-to-static-site-migration-2026": {
    title: "WordPress to Static Site Migration: 2026 Guide for Malaysia & SG",
    description:
      "A business-owner's guide to moving off WordPress in 2026 — what it costs, how long, what to ask your vendor, and how to protect your search traffic.",
    keywords:
      "WordPress migration Malaysia, WordPress migration Singapore, WordPress migration cost Malaysia, when to leave WordPress, WordPress to custom website Malaysia, WordPress alternatives business, keep SEO after WordPress migration, WordPress migration vendor questions, WordPress migration timeline Malaysia, custom website Malaysia, custom website Singapore",
    ogImage: "/blog/wordpress-to-static-site-migration-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Website Development", "WordPress", "Malaysia Business"],
  },
  "blog/whatsapp-ai-human-handover": {
    title: "WhatsApp AI and Human Handover: How We Handle Enquiries",
    description: "Two actual Anchor Sprint WhatsApp AI enquiries show when the agent can answer and when staff take over. Includes redacted EzyChat screenshots.",
    keywords: "WhatsApp AI, WhatsApp human handover, EzyChat enquiry workflow, WhatsApp customer service Malaysia",
    ogImage: "/blog/whatsapp-ai-human-handover/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: [
      "WhatsApp",
      "AI",
      "Malaysia",
      "EzyChat"
    ]
  },
  "blog/whatsapp-business-api-malaysia-ban-safe-marketing-blast-ai": {
    title: "WhatsApp Business API Malaysia: App, API or Team Inbox?",
    description: "Compare WhatsApp Business API options in Malaysia. Get Anchor Sprint's setup guide to connect Meta, Composio and Claude, with costs and message examples.",
    keywords: "WhatsApp Business API Malaysia, WhatsApp Business API setup guide, send WhatsApp from Claude, Composio WhatsApp Claude, WhatsApp Business app vs API, official WhatsApp API, WhatsApp team inbox Malaysia, EzyChat Lite connection",
    ogImage: "/blog/whatsapp-business-api-malaysia-ban-safe-marketing-blast-ai/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: [
      "WhatsApp",
      "AI",
      "Malaysia Business"
    ]
  },
  "blog/whatsapp-business-platform-pricing-translation-sea": {
    title: "WhatsApp Business Platform Pricing: What Makes Up Your Bill?",
    description: "Understand WhatsApp Business Platform pricing in Malaysia: delivered-message charges, software, AI usage, hosting and setup. Know what to ask before accepting a quote.",
    keywords: "WhatsApp Business Platform pricing, WhatsApp API costs Malaysia, WhatsApp delivered message pricing, WhatsApp AI usage costs, EzyChat costs",
    ogImage: "https://images.unsplash.com/photo-1523475472560-d2df97ec485c?auto=format&fit=crop&w=1200&h=630&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: [
      "WhatsApp",
      "Business",
      "Malaysia",
      "Pricing"
    ]
  },
  "blog/johor-nvidia-cloud-partner-malaysian-businesses": {
    title:
      "Johor NVIDIA Cloud Partner: What It Means for Business",
    description:
      "Johor's NVIDIA Cloud Partner status validates the RM20.6b YTL-NVIDIA build in Kulai. What it means for Malaysian businesses and how to get AI-ready.",
    keywords:
      "NVIDIA Cloud Partner Johor, YTL Power NVIDIA collaboration, Malaysian business AI adoption, Johor Singapore Special Economic Zone AI, GPU infrastructure Malaysia, AI compliance PDPA, Anchor Sprint AI consulting",
    ogImage:
      "https://www.bernama.com/storage/photos/46a27fc4a593b68b0231ef2739d4dbe768d3543a3e57e-medium",
    ogImageType: "image/jpeg",
    ogImageWidth: 350,
    ogImageHeight: 219,
    tags: [
      "AI Adoption",
      "Malaysia Business",
      "Digital Transformation",
      "Grants & Government Programs",
    ],
  },
  "blog/anchor-sprint-malaysia-digital-status-mdec": {
    title:
      "Anchor Sprint Achieves Official Malaysia Digital Status by MDEC | Company News",
    description:
      "Anchor Sprint receives MDEC Malaysia Digital Status certification. Learn how our internal expertise delivers AI solutions and software development for Malaysian businesses.",
    keywords:
      "Malaysia Digital Status, MDEC certification, AI solutions Malaysia, software development Malaysia, digital transformation, business technology solutions, Malaysian tech company",
    ogImage: "/images/blogs/mdec.jpeg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1162,
    ogImageHeight: 649,
    tags: ["Company News", "MDEC", "Digital Transformation"],
  },
  "blog/malaysia-national-cloud-policy-nccp-business-guide": {
    title:
      "Malaysia National Cloud Policy (NCCP) 2026: Business Guide",
    description:
      "Plain-language guide to Malaysia's NCCP for business owners. What it means for your cloud strategy, PDPA compliance, and an NCCP-aligned roadmap.",
    keywords:
      "NCCP Malaysia, National Cloud Computing Policy Malaysia, NCCP business guide, Malaysian cloud compliance, PDPA cloud strategy, Malaysia digital policy, national cloud policy",
    ogImage:
      "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1200&h=630&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["Cloud", "Policy", "Business"],
  },
  "blog/traditional-vs-modern-ai-chatbots": {
    title:
      "Rule-Based vs Generative Chatbots: What Actually Changed",
    description:
      "Rule-based chatbots answered from a script; generative ones reason from your content. What changed, what broke, and which one your customer enquiries actually need.",
    keywords:
      "rule-based vs generative chatbot, traditional chatbot vs AI, scripted chatbot limitations, generative AI customer service, chatbot technology comparison, decision tree chatbot",
    ogImage:
      "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1200&h=630&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["AI", "Customer Experience", "Malaysia"],
  },

  "blog/unlocking-ai-malaysian-businesses-practical-use-cases": {
    title: "AI for Malaysian Businesses: 6 Practical Use Cases (2026)",
    description:
      "Real AI use cases for Malaysian businesses — from customer service to finance automation. How teams use AI agents to save 20+ hours a week.",
    keywords:
      "ai for malaysian businesses, AI LLM applications Malaysia, AI LLM solutions Malaysia, malaysia business ai use cases, ai nation 2030 roadmap, malaysian automation partner",
    ogImage:
      "https://images.unsplash.com/photo-1684369175809-f9642140a1bd?auto=format&fit=crop&w=1200&h=630&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["AI for Business", "Malaysia", "Automation"],
  },

  "blog/private-llm-malaysia-enterprise-data-privacy": {
    title:
      "Private LLM in Malaysia: Who Needs It and When It Makes Sense",
    description:
      "When should Malaysian enterprises deploy private LLMs instead of public APIs? Covers PDPA compliance, Bank Negara guidelines, and practical deployment options.",
    keywords:
      "private LLM Malaysia, enterprise AI deployment, PDPA compliance AI, Bank Negara AI guidelines, self-hosted LLM, data privacy AI Malaysia, Llama deployment, Mistral enterprise, regulated industry AI, financial services AI Malaysia",
    ogImage: "/images/blogs/private-llm-malaysia-enterprise-data-security.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1792,
    ogImageHeight: 1000,
    tags: ["AI", "Data Privacy", "Malaysia", "Enterprise"],
  },

  
"blog/website-builder-vs-wordpress-vs-custom-malaysia": {
    title: "Website Builder vs WordPress vs Custom",
    description: "Compare DIY website builders, WordPress, low-code, and custom Next.js development in Malaysia by ownership, integrations, maintenance, and total cost.",
    keywords: "website builder vs WordPress Malaysia, WordPress vs custom website Malaysia, DIY website builder Malaysia, low-code website Malaysia, custom website Malaysia RM1800, website development options Malaysia, website platform comparison Malaysia",
    ogImage: "/blog/website-builder-vs-wordpress-vs-custom-malaysia/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1792,
    ogImageHeight: 1024,
    tags: ["Website Development", "Malaysia Business", "Web Design"],
  },
// About pages
  company: {
    title: "Company | Anchor Sprint — AI & Software Engineering Malaysia",
    description:
      "Get to know Anchor Sprint: the team, mission, values, and partnerships behind our AI and software engineering work for enterprises across Malaysia and ASEAN.",
    keywords:
      "Anchor Sprint company, software company Malaysia, AI company Selangor, technology company Shah Alam, MDEC certified company, enterprise software Malaysia",
    ogImage: "/images/company/office-dark.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Syarikat | Anchor Sprint — Kejuruteraan AI & Perisian Malaysia",
        description:
          "Kenali pasukan, misi, nilai dan rakan kongsi di sebalik kerja kejuruteraan AI dan perisian Anchor Sprint untuk perusahaan di Malaysia dan ASEAN.",
        keywords:
          "syarikat Anchor Sprint, syarikat perisian Malaysia, syarikat AI Selangor, syarikat teknologi Shah Alam, syarikat disahkan MDEC",
      },
      zh: {
        title: "公司简介：马来西亚 AI 与软件工程",
        description:
          "了解安克极速的团队、使命、价值观与合作伙伴，以及我们为马来西亚和东盟企业提供的 AI 与软件工程服务。",
        keywords:
          "安克极速公司, 马来西亚软件公司, 雪兰莪 AI 公司, 莎阿南科技公司, MDEC 认证公司",
      },
    },
  },
  "our-people": {
    title: "Our People: Jazz Tong & Liew Jee Fong",
    description:
      "Meet Anchor Sprint founders Jazz Tong and Liew Jee Fong: software and agent engineering, business evaluation, funding assessment and practical AI adoption.",
    keywords: "Jazz Tong, Liew Jee Fong, Anchor Sprint founders, software engineering, agent engineering, business evaluation, AI adoption Malaysia",
    ogImage: "/images/people/our-people-og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Pasukan Kami: Jazz Tong & Liew Jee Fong",
        description: "Kenali pengasas bersama Anchor Sprint: Jazz Tong dengan 18 tahun pengalaman kejuruteraan perisian dan Liew Jee Fong dengan lebih 15 tahun pengalaman perbankan.",
      },
      zh: {
        title: "我们的团队：Jazz Tong 与 Liew Jee Fong",
        description: "认识安克极速联合创始人：Jazz Tong 拥有十八年软件工程经验，Liew Jee Fong 拥有超过十五年银行业经验，共同推动企业实际应用 AI。",
      },
    },
  },
  "about-us": {
    title: "About Anchor Sprint | Our Story & Mission",
    description:
      "Learn about Anchor Sprint's journey, mission, and commitment to delivering innovative technology solutions in Malaysia.",
    ogImage: "/images/company/office-dark.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    keywords:
      "about Anchor Sprint, professional software development company Malaysia, MDEC certified technology company Selangor, trusted AI solutions provider KL, software company Shah Alam, enterprise digital transformation partner Malaysia",
    localized: {
      ms: {
        title: "Tentang Anchor Sprint | Kisah & Misi Kami",
        description:
          "Kenali Anchor Sprint: pasukan di Selangor yang membina ejen AI dan perisian untuk perniagaan di Malaysia dan Singapura, serta kisah dan misi kami.",
        keywords:
          "tentang Anchor Sprint, syarikat AI Malaysia, syarikat perisian Selangor, pembangunan ejen AI Malaysia, syarikat teknologi Shah Alam",
      },
      zh: {
        title: "关于我们：团队故事与使命",
        description:
          "认识安克极速：位于雪兰莪、为马来西亚和新加坡企业打造 AI 智能体与软件的团队，以及我们的故事与使命。",
        keywords:
          "关于安克极速, 马来西亚 AI 公司, 雪兰莪软件公司, 马来西亚 AI 智能体开发, 莎阿南科技公司",
      },
    },
  },
  "corporate-mission": {
    title: "Our Corporate Mission",
    description:
      "Discover Anchor Sprint's corporate mission and our commitment to driving digital transformation in Malaysia.",
    ogImage: "/images/company/office-dark.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    keywords:
      "Anchor Sprint mission, professional technology company Malaysia, digital transformation mission Malaysia, trusted software partner Selangor, MDEC certified software company KL, enterprise innovation Malaysia",
    localized: {
      ms: {
        title: "Misi Korporat Kami",
        description:
          "Misi korporat Anchor Sprint: teknologi yang memacu perniagaan Malaysia ke hadapan melalui AI, perisian dan transformasi digital.",
        keywords:
          "misi Anchor Sprint, misi transformasi digital Malaysia, syarikat teknologi Malaysia, rakan kongsi perisian Selangor",
      },
      zh: {
        title: "我们的企业使命",
        description:
          "安克极速的企业使命：以 AI、软件与数字化转型，用技术推动马来西亚企业业务前行。",
        keywords:
          "安克极速使命, 马来西亚数字化转型, 马来西亚科技公司, 雪兰莪软件合作伙伴",
      },
    },
  },
  "corporate-values": {
    title: "Our Corporate Values",
    description:
      "Explore the core values that drive Anchor Sprint's commitment to excellence and innovation in technology solutions.",
    ogImage: "/images/company/office-dark.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    keywords:
      "Anchor Sprint values, professional software company Malaysia, trusted technology partner Selangor, agile software delivery KL, customer-first software development Malaysia, enterprise engineering culture Malaysia",
    localized: {
      ms: {
        title: "Nilai Korporat Kami",
        description:
          "Prinsip di sebalik setiap pecutan: nilai teras yang memandu cara Anchor Sprint membina AI dan perisian untuk pelanggan di Malaysia.",
        keywords:
          "nilai Anchor Sprint, budaya kejuruteraan Malaysia, penghantaran perisian agile, syarikat perisian Malaysia",
      },
      zh: {
        title: "我们的企业价值观",
        description:
          "贯穿每一次冲刺的原则：指导安克极速为马来西亚客户打造 AI 与软件的核心价值观。",
        keywords:
          "安克极速价值观, 马来西亚工程文化, 敏捷软件交付, 马来西亚软件公司",
      },
    },
  },

  // PDPA privacy notice (EN + BM required by PDPA; ZH as the site is trilingual)
  "privacy-policy": {
    title: "Privacy Notice (PDPA)",
    description:
      "How Anchor Sprint collects, uses and protects personal data under Malaysia's PDPA 2010 and Singapore's PDPA 2012, and how to access, correct or opt out of marketing.",
    localized: {
      ms: {
        title: "Notis Privasi (PDPA)",
        description:
          "Cara Anchor Sprint mengumpul, menggunakan dan melindungi data peribadi di bawah PDPA 2010 Malaysia dan PDPA 2012 Singapura, serta cara meminta akses, pembetulan atau berhenti menerima pemasaran.",
        keywords:
          "notis privasi Anchor Sprint, notis privasi PDPA, perlindungan data peribadi Anchor Sprint",
      },
      zh: {
        title: "隐私声明（PDPA）",
        description:
          "安克极速如何依据马来西亚《2010年个人资料保护法》及新加坡《2012年个人资料保护法》收集、使用和保护个人资料，以及如何查阅、更正资料或取消营销邮件。",
        keywords:
          "安克极速 隐私声明, PDPA 隐私声明, 安克极速 个人资料保护",
      },
    },
    ogImage: "/images/company/office-dark.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    keywords:
      "Anchor Sprint privacy notice, Anchor Sprint PDPA notice, Anchor Sprint personal data",
  },

  // Contact page
  contact: {
    title: "Contact Anchor Sprint | Get in Touch",
    description:
      "Contact Anchor Sprint for your enterprise technology needs. We're here to help transform your business through innovative solutions.",
    localized: {
      ms: {
        title: "Hubungi Anchor Sprint | Mari Berbincang",
        description:
          "Hubungi Anchor Sprint untuk keperluan teknologi perusahaan anda. Kami membantu transformasi perniagaan melalui penyelesaian digital dan AI.",
      },
      zh: {
        title: "联系安克极速 | 与我们交流",
        description:
          "联系安克极速，讨论您的企业技术需求。我们通过数字化和 AI 解决方案协助企业转型。",
      },
    },
    ogImage: "/images/company/office-dark.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    keywords:
      "contact software development company Malaysia, hire software developers Selangor, AI consultant Kuala Lumpur, free software consultation Malaysia, request custom software quote KL, professional IT services Shah Alam",
  },

  // Industries page
  "industries-we-serve": {
    title: "Industries We Serve",
    description:
      "Discover how Anchor Sprint serves various industries with tailored technology solutions and digital transformation services.",
    ogImage: "/images/services/sm.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    keywords:
      "industry software solutions Malaysia, manufacturing software Selangor, retail trading software Malaysia, education technology Malaysia, construction software KL, smart manufacturing Malaysia, professional industry-specific solutions Shah Alam, digital transformation by industry Malaysia",
    localized: {
      ms: {
        title: "Industri Yang Kami Layani",
        description:
          "Kepakaran mendalam dalam industri anda: penyelesaian AI dan perisian Anchor Sprint untuk pembuatan, runcit, pendidikan, pembinaan dan banyak lagi di Malaysia.",
        keywords:
          "perisian industri Malaysia, perisian pembuatan Selangor, perisian runcit Malaysia, teknologi pendidikan Malaysia, pembuatan pintar Malaysia",
      },
      zh: {
        title: "我们服务的行业",
        description:
          "深耕您所在的行业：安克极速为马来西亚制造、零售、教育、建筑等行业提供 AI 与软件解决方案。",
        keywords:
          "马来西亚行业软件, 雪兰莪制造业软件, 马来西亚零售软件, 马来西亚教育科技, 马来西亚智能制造",
      },
    },
  },

  // Partners page
  "our-partners": {
    title: "Our Technology Partners | Anchor Sprint Malaysia",
    description:
      "Authorised reseller for Microsoft, Lenovo, NVIDIA DGX Spark, Red Hat, Alibaba Cloud and TrueWatch in Malaysia, plus Google Workspace, SonarQube and Adobe, quoted in RM.",
    ogImage: "/images/partners/anthropic-editorial.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    keywords:
      "Anchor Sprint partners, Adobe reseller Selangor, AWS partner Malaysia, Microsoft Surface partner KL, Alibaba Cloud partner Malaysia, Alibaba Cloud reseller Malaysia, authorised Alibaba Cloud reseller, TrueWatch reseller Malaysia, TrueWatch Malaysia, Google Workspace reseller Malaysia, certified technology partners Malaysia, professional enterprise technology alliances",
    localized: {
      ms: {
        title: "Rakan Kongsi Teknologi Kami | Anchor Sprint Malaysia",
        description:
          "Penjual semula sah Microsoft, Lenovo, NVIDIA DGX Spark, Red Hat, Alibaba Cloud dan TrueWatch di Malaysia, serta Google Workspace, SonarQube dan Adobe, disebut harga dalam RM.",
        keywords:
          "rakan kongsi Anchor Sprint, penjual semula Microsoft Malaysia, penjual semula Lenovo Malaysia, penjual semula Alibaba Cloud Malaysia, penjual semula Adobe Selangor",
      },
      zh: {
        title: "我们的技术合作伙伴 | 马来西亚",
        description:
          "在马来西亚授权经销 Microsoft、Lenovo、NVIDIA DGX Spark、Red Hat、Alibaba Cloud 与 TrueWatch，并提供 Google Workspace、SonarQube 与 Adobe，以马币报价。",
        keywords:
          "安克极速合作伙伴, 马来西亚 Microsoft 经销商, 马来西亚 Lenovo 经销商, 马来西亚阿里云经销商, 雪兰莪 Adobe 经销商",
      },
    },
  },

  // Partner detail pages
  // "Why Claude" sibling of partners/anthropic-claude-partner. 2026-09-30: that
  // page is the ONLY owner of "Claude partner Malaysia" (GSC had started crediting
  // this page's /ms/ variant for the English query). This one owns the
  // engineering case for building on Claude; partner terms were stripped from its
  // keywords and localized titles. The slug is legacy; do not change it here.
  "anthropic-claude-partner-malaysia-singapore": {
    title: "Why We Build on Claude (and When We Don't)",
    description:
      "Why we deploy Claude over OpenAI for agentic work, the Claude Certified Architects who build it, and a Malaysian-owned engineering team delivering in MY and SG.",
    keywords:
      "why build on Claude, Claude Certified Architect Malaysia, AI agent company Malaysia, Claude agency Singapore",
    ogImage: "/images/partners/claude-partner-my-sg-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1400,
    ogImageHeight: 933,
    localized: {
      ms: {
        title: "Mengapa Kami Membina di atas Claude (dan Bila Tidak)",
        description:
          "Mengapa kami melaksanakan Claude berbanding OpenAI untuk kerja agentik, Claude Certified Architect yang membinanya, dan pasukan kejuruteraan milik Malaysia di MY dan SG.",
        keywords:
          "mengapa Claude, Claude berbanding OpenAI, Claude Certified Architect Malaysia, syarikat ejen AI Malaysia, pelaksanaan Claude Malaysia, agensi AI Malaysia Singapura",
      },
      zh: {
        title: "我们为何选择 Claude 构建（以及何时不用）",
        description:
          "在智能体工作上我们为何选择 Claude 而非 OpenAI、由谁来构建（Claude Certified Architect），以及一支在马新两地交付的马来西亚本土工程团队。",
        keywords:
          "为何选择 Claude, Claude 与 OpenAI 对比, Claude 认证架构师, 马来西亚 AI 智能体公司, 企业 AI 部署 马来西亚",
      },
    },
  },

  // Not an SEO page and not written as one. Keyword Planner (2026-08-10, MY)
  // puts "invest in ai startup malaysia" and "ai startup investment malaysia"
  // below the reporting threshold and "southeast asia ai investment" at 0-10.
  // Investors arrive from an intro or a deck, then look you up — so this is
  // written for someone who already has the link.
  "quickbooks-connector": {
    title: "QuickBooks Connector for Claude | Anchor Sprint",
    description:
      "Let Claude Desktop read your QuickBooks Online P&L, GST tax codes and transactions, and bank transactions. Read-only, runs on your PC, data not stored by Anchor Sprint.",
    keywords:
      "QuickBooks Claude connector, QuickBooks Online Claude, Claude Desktop QuickBooks, QuickBooks GST Claude, Anchor Sprint QuickBooks Connector",
  },
  "quickbooks-connector/terms": {
    title: "QuickBooks Connector Terms (EULA) | Anchor Sprint",
    description:
      "End-User Licence Agreement for the Anchor Sprint QuickBooks Connector for Claude: licence, read-only scope, data handling, warranty, liability and governing law.",
  },
  "quickbooks-connector/disconnected": {
    title: "QuickBooks Connector Disconnected | Anchor Sprint",
    description:
      "The Anchor Sprint QuickBooks Connector has been disconnected from your QuickBooks company.",
  },
  "invest": {
    title: "Investment & Partnership | Building AI Delivery for SEA",
    description:
      "Anchor Sprint builds and runs production AI agents for businesses in Malaysia and Singapore. Information for investors, product partners and programme partners.",
    keywords:
      "Anchor Sprint investment, Anchor Sprint partnership, AI company Malaysia Singapore, AI agent company Southeast Asia, AI delivery partner ASEAN",
    ogImage: "/images/services/ai-agent-editorial.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "partners/anthropic-claude-partner": {
    // Short on purpose: generatePageMetadata appends " | Anchor Sprint", and
    // Google truncates around 60 characters. The geo term has to survive that.
    //
    // ENTITY CLAIM, NOT AN EXPLAINER (2026-09-17). This read "Claude Partner
    // Malaysia: How to Verify One" while we sat at #2 behind Agmo, whose title
    // and every heading assert that they ARE the partner. "claude partner
    // malaysia" is vendor intent — the searcher wants a partner, not a guide to
    // assessing one — so the explainer framing was losing a SERP our page
    // otherwise out-resourced 2:1 on content and 19-schema-types-to-zero.
    // This mirrors "singapore/claude-partner" exactly, which is the same
    // position with roughly 3x the CTR (4.74% vs 1.62%, 28d to 2026-09-15).
    // The page H1 changed with it: Google was rewriting this title FROM that
    // H1, so changing one without the other would have been overwritten.
    // 2026-10-03: "Certified" added after Anchor Sprint became Certified in the
    // Claude Partner Network. It is true, it is tier language the co-marketing
    // guidelines allow, and no sibling page or aideploy carries it, which ends
    // the self-cannibalisation behind the 2026-09-25 drop. H1 moved with it.
    // 2026-10-08 (Jazz: "follow their way of putting title and description"):
    // pattern = search phrase first, then the programme name with "Anthropic" in
    // it (covers "anthropic partner malaysia" without claiming a partnership with
    // Anthropic); description = capability roll-call in our own words with the
    // hard number (10 Claude Certified Architects) AI answers quote.
    title: "Claude Partner Malaysia | Anthropic Claude Partner Network",
    // Google's AI Overview paraphrases this string almost verbatim — on
    // 2026-08-07 it described us as "MYR billing, and localized agent deployment
    // out of Selangor", which is this description read back. Agmo's richer AIO
    // sentence is likewise their own meta description. So this field is the
    // capability roll-call, not a positioning line, and "delivered from
    // Selangor" is deliberately gone: the delivery detail lives on the page.
    // 2026-09-30: the previous roll-call ("Claude Certified Architects build
    // production agents with ... billed in MYR") read clause-for-clause like a
    // competitor's own description, and aideploy.my carried a near-copy too, so
    // Google had three near-identical claims to choose between. This version is
    // in our own voice and leads with what only we can say: Malaysian-owned,
    // Partner Network (Certified), Selangor + Singapore. The page's answer-first
    // block (AnthropicPartner.tsx) carries the full services/credentials list.
    // Certification: we prep candidates and the exam fee is included; Anthropic
    // awards the credential, so never "we certify" (see Academy.tsx Path A).
    // See docs/analytics/competitors/2026-09-30-claude-partner-malaysia.md.
    description:
      "Anchor Sprint is Malaysia's homegrown Anthropic Claude Partner Network member — a full team of Claude Certified Architects (CCAR-F) building production Claude agents with the Agent SDK, Claude Code, and MCP. Managed service, consulting, implementation, RAG and custom agents, and on-demand FDE teams with local MYR billing and HRDF-claimable training.",
    keywords:
      "Claude partner Malaysia, Certified Claude partner Malaysia, Claude AI partner, Claude AI partner Malaysia, who is Claude partnered with, Claude implementation Malaysia, Claude consulting Malaysia, Claude Agent SDK Malaysia, Claude multi-agent Malaysia, claude malaysia partner, Anthropic Claude partner Malaysia, Anthropic partner Malaysia, anthropic malaysia partner, anthropic ai agency malaysia, Claude partner Singapore, claude singapore partner, Anthropic partner Singapore, anthropic singapore partner, Claude partner Malaysia Singapore, Claude AI Malaysia, Claude Partner Network Malaysia, Claude agents Malaysia, Anthropic Claude enterprise, Claude Agent SDK, MCP integration Malaysia, Claude deployment partner ASEAN, Claude Certified Architect, Claude Certified Architect Malaysia, Claude certified developer Malaysia, Claude Certified Architect Singapore",
    ogImage: "/images/services/ai-brain-circuit.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Rakan Kongsi Claude Malaysia | Anthropic Claude Partner Network",
        description:
          "Anchor Sprint ialah ahli tempatan Malaysia dalam Anthropic Claude Partner Network: pasukan penuh Claude Certified Architect (CCAR-F) membina ejen Claude untuk produksi dengan Agent SDK, Claude Code dan MCP. Perkhidmatan terurus, perundingan, pelaksanaan, RAG dan ejen tersuai, serta pasukan FDE atas permintaan, dibilkan dalam MYR, dengan latihan boleh dituntut HRDF.",
        keywords:
          "rakan kongsi Claude Malaysia, syarikat AI Malaysia, ejen AI Malaysia, Anthropic Malaysia, Claude Malaysia, pembangunan ejen AI Malaysia, latihan AI HRD Corp, syarikat Malaysia Digital MDEC",
      },
      zh: {
        title: "马来西亚 Claude 合作伙伴 | Anthropic Claude Partner Network",
        description:
          "Anchor Sprint 是马来西亚本土的 Anthropic Claude Partner Network 成员：由 Claude Certified Architect（CCAR-F）组成的完整团队，使用 Agent SDK、Claude Code 与 MCP 构建可投入生产的 Claude 智能体。提供托管服务、咨询、实施、RAG 与定制智能体，以及按需 FDE 团队，以令吉计费，培训可申领 HRDF。",
        keywords:
          "马来西亚 Claude 合作伙伴, 马来西亚 AI 公司, 马来西亚 AI 智能体, Anthropic 马来西亚, Claude 马来西亚, 企业 AI 部署 马来西亚, MDEC 认证 AI 公司, HRD Corp AI 培训",
      },
    },
  },
  "partners/openai-partner": {
    // OWNER page (2026-10-08) for "OpenAI partner Malaysia", "OpenAI Select
    // Partner Malaysia" and "ChatGPT Enterprise Malaysia". "ChatGPT training
    // Malaysia" / "HRD Corp ChatGPT training" moved to /academy/chatgpt-training/
    // (2026-10-08), which this page links to. One keyword, one page: the
    // /enterprise/ OpenAI card is enquiry-only and links here; the announcement
    // post takes the news intent only; no Claude page targets OpenAI terms.
    // Status wording exactly "OpenAI Select Partner" (OpenAI Social Guidance).
    title: "OpenAI Select Partner Malaysia | ChatGPT Enterprise & API",
    description:
      "Anchor Sprint is an OpenAI Select Partner in Malaysia. ChatGPT Business and Enterprise rollout, OpenAI API agents, PDPA governance and HRD Corp claimable ChatGPT training.",
    keywords:
      "OpenAI partner Malaysia, OpenAI Select Partner Malaysia, ChatGPT Enterprise Malaysia, ChatGPT rollout Malaysia, OpenAI API development Malaysia, buy ChatGPT Business Malaysia, Azure OpenAI Malaysia, OpenAI partner Singapore",
    ogImage: "/images/partners/openai-partner-og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "OpenAI Select Partner di Malaysia | ChatGPT Enterprise & API",
        description:
          "Anchor Sprint ialah OpenAI Select Partner di Malaysia. Pelaksanaan ChatGPT Business dan Enterprise, ejen OpenAI API, tadbir urus PDPA dan latihan ChatGPT boleh dituntut HRD Corp.",
        keywords:
          "rakan kongsi OpenAI Malaysia, OpenAI Select Partner Malaysia, ChatGPT Enterprise Malaysia, ChatGPT Business Malaysia, beli ChatGPT Business Malaysia, Azure OpenAI Malaysia",
      },
      zh: {
        title: "OpenAI Select Partner 马来西亚 | ChatGPT Enterprise 与 API",
        description:
          "Anchor Sprint 是马来西亚的 OpenAI Select Partner。ChatGPT Business 与 Enterprise 部署、OpenAI API 智能体、PDPA 治理，以及可申领 HRD Corp 补助的 ChatGPT 团队培训。",
        keywords:
          "马来西亚 OpenAI 合作伙伴, OpenAI Select Partner 马来西亚, ChatGPT Enterprise 马来西亚, ChatGPT Business 马来西亚, 马来西亚购买 ChatGPT Business, Azure OpenAI 马来西亚",
      },
    },
  },
  "guides/how-to-use-claude-malaysia": {
    title: "How to Access & Use Claude in Malaysia (2026 Guide)",
    description:
      "Is Claude available in Malaysia? Yes. How to sign up, fix the credit-card problem, plans in MYR, using Claude in Bahasa Malaysia, and the 3 ways businesses access Claude — a complete guide.",
    keywords:
      "Claude AI Malaysia, how to use Claude Malaysia, is Claude available in Malaysia, access Claude Malaysia, Claude Malaysia sign up, how to pay for Claude Malaysia, Claude AI Malaysia price, Claude free Malaysia, Claude Bahasa Malaysia, use Claude in Malaysia, Claude for business Malaysia, Anthropic Claude Malaysia",
    localized: {
      ms: {
        title: "Cara Akses & Guna Claude di Malaysia (Panduan 2026)",
        description:
          "Adakah Claude tersedia di Malaysia? Ya. Cara daftar, selesaikan masalah kad kredit, harga dalam MYR, guna Claude dalam Bahasa Malaysia, dan 3 cara perniagaan akses Claude — panduan lengkap.",
        keywords:
          "Claude AI Malaysia, cara guna Claude Malaysia, adakah Claude ada di Malaysia, cara daftar Claude Malaysia, cara bayar Claude Malaysia, harga Claude Malaysia, Claude percuma Malaysia, Claude Bahasa Malaysia, guna Claude di Malaysia, Claude untuk perniagaan Malaysia",
      },
      zh: {
        title: "如何在马来西亚使用 Claude（2026 完整指南）",
        description:
          "Claude 在马来西亚可用吗？可以。如何注册、解决信用卡付款问题、马币价格、用马来语使用 Claude，以及企业接入 Claude 的三种方式——一份完整指南。",
        keywords:
          "Claude 马来西亚, 如何使用 Claude 马来西亚, Claude 在马来西亚可用吗, 如何注册 Claude 马来西亚, 如何支付 Claude 马来西亚, Claude 马来西亚价格, Claude 免费 马来西亚, Claude 马来语, 企业使用 Claude 马来西亚",
      },
    },
    ogImage: "/images/guides/claude-malaysia-access-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "guides/agent-gpa": {
    title: "Agent GPA: How We Score an AI Agent Before It Ships",
    description:
      "We score the manual process before building any AI agent — time per case, error rate, correction cost, escalation quality — and the agent does not go live until it beats that baseline. The method, and when it says do not build.",
    keywords:
      "AI agent evaluation, how to evaluate an AI agent, AI agent accuracy benchmark, agent evaluation Malaysia, AI agent scorecard, BNM MAS AI evaluation, should we build an AI agent",
    localized: {
      ms: {
        title: "Agent GPA: Cara Kami Menilai Ejen AI Sebelum Ia Dilancarkan",
        description:
          "Kami memberi skor kepada proses manual sebelum membina sebarang ejen AI — masa setiap kes, kadar ralat, kos pembetulan, kualiti eskalasi — dan ejen itu tidak dilancarkan sehingga ia mengatasi garis dasar tersebut.",
        keywords:
          "penilaian ejen AI, cara menilai ejen AI, penanda aras ketepatan ejen AI, penilaian ejen Malaysia, skor ejen AI, patutkah kami bina ejen AI",
      },
      zh: {
        title: "Agent GPA：我们如何在 AI 代理上线前为它评分",
        description:
          "在构建任何 AI 代理之前，我们先为人工流程打分——单案耗时、错误率、纠错成本、上报质量——代理只有超过这条基线才会上线。",
        keywords:
          "AI 代理评估, 如何评估 AI 代理, AI 代理准确率基准, 马来西亚代理评估, AI 代理评分卡, 该不该做 AI 代理",
      },
    },
    ogImage: "/images/services/svc-system-integration.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "guides/claude-cowork": {
    // Retargeted 2026-08-09 to Malaysia AND Singapore. Over 90 days the two
    // biggest cowork queries were Singaporean — "claude cowork training
    // singapore" (114 impr, p17.7) and "claude cowork singapore" (93, p28.2) —
    // 207 impressions, a third of all cowork demand, against a keyword list that
    // named only Malaysia. "Custom training" is the deliberate wedge: the SG
    // training SERP is nine paid public courses from polytechnics and
    // SkillsFuture providers, which we cannot and should not compete with (we
    // are not an SSG Registered Training Provider — see PR #313). A private
    // engagement run on the client's own workflows is a different product.
    title: "Claude Cowork Guide: Singapore & Malaysia + Custom Training",
    description:
      "What Claude Cowork is, how to use it, and where it stops — plus custom in-house Cowork training for teams in Singapore and Malaysia, run on your own workflows.",
    keywords:
      "Claude Cowork, what is Claude Cowork, how to use Claude Cowork, Claude Cowork guide, Claude Cowork tutorial, Claude Cowork Singapore, Claude Cowork training Singapore, Claude Cowork custom training, Cowork training for teams, Claude Cowork Malaysia, Claude Cowork vs Claude Code, Claude Cowork examples, Claude Cowork use cases, Claude Cowork pricing, Anthropic Cowork, Claude desktop agent",
    localized: {
      ms: {
        title: "Panduan Claude Cowork + Latihan Tersuai (MY & SG)",
        description:
          "Apa itu Claude Cowork, cara menggunakannya, dan di mana ia berhenti — serta latihan Cowork tersuai untuk pasukan di Malaysia dan Singapura, berdasarkan aliran kerja anda sendiri.",
        keywords:
          "apa itu Claude Cowork, cara guna Claude Cowork, panduan Claude Cowork, latihan Claude Cowork, latihan Cowork tersuai, Claude Cowork Malaysia, Claude Cowork Singapura, Claude Cowork lawan Claude Code, contoh Claude Cowork, kegunaan Claude Cowork, harga Claude Cowork, ejen AI desktop Claude",
      },
      zh: {
        title: "Claude Cowork 指南：新马团队 + 定制培训",
        description:
          "Claude Cowork 是什么、如何使用、以及它的局限——并为马来西亚与新加坡团队提供基于自身工作流程的 Cowork 定制培训。",
        keywords:
          "什么是 Claude Cowork, 如何使用 Claude Cowork, Claude Cowork 指南, Claude Cowork 培训, Cowork 定制培训, Claude Cowork 新加坡, Claude Cowork 马来西亚, Claude Cowork 与 Claude Code, Claude Cowork 用例, Claude Cowork 教程, Claude Cowork 价格, Claude 桌面代理",
      },
    },
    ogImage: "/images/guides/claude-cowork-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "guides/malaysia-budget-2027": {
    // OWNER page (2026-10-10) for "Malaysia Budget 2027" / "budget 2027 malaysia"
    // (+ summary/highlights), "Belanjawan 2027" / "Bajet 2027" and
    // "2027财政预算案". Lead page for the Budget carousel ("Download link in the
    // post"). Keywords researched per locale from gl=my autocomplete + 3 SERPs:
    // docs/research/budget-2027-malaysia.md. No "SME" (official "MSME" only in copy).
    // 2026-10-11: long-tail terms (STR/SARA, civil servants, pensioners, OKU, KAFA,
    // PTPTN, maternity leave, tax relief, date/theme) added ONLY where the speech has a
    // measure, each backed by a FAQ/table row. "STR bujang" and "live" deliberately left out.
    // 2026-10-10 (SerpAPI gl=my): "budget 2027 malaysia pdf" and "... tax relief"
    // have weak SERPs, and AI answers cite headings that state the answer, so the
    // title leads with summary + PDF + tax relief and the page carries exact-answer
    // topic headings. "Free" dropped from the title only to stay <=60 chars.
    title: "Budget 2027 Malaysia Summary + PDF: Tax Relief, Wages, Aid",
    description:
      "Budget 2027 Malaysia summary with a free PDF guide: tax relief rises to RM12,000, RM2,000 minimum wage, STR & SARA, RM1,500 civil servant aid, PTPTN and AI.",
    keywords:
      "Malaysia Budget 2027, budget 2027 malaysia, budget 2027 malaysia summary, budget 2027 highlights, budget 2027 malaysia date, budget 2027 malaysia tax relief, budget 2027 malaysia income tax, budget 2027 malaysia tax exemption, budget 2027 minimum wage, budget 2027 malaysia str, budget 2027 str sara allocation, budget 2027 civil servants, budget 2027 malaysia pensioners, budget 2027 malaysia ptptn, budget 2027 malaysia maternity leave, budget 2027 OKU, budget 2027 malaysia pdf, budget 2027 malaysia summary pdf, budget 2027 malaysia infographic, budget 2027 malaysia business summary, budget 2027 malaysia AI, budget 2027 malaysia AI MDEC, Budget 2027 business tax Malaysia, budget 2027 theme, budget 2027 malaysia theme, reaching for the skies while anchored on our values",
    ogImage: "/images/guides/malaysia-budget-2027-og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Belanjawan 2027 (Bajet 2027): Ringkasan, Intipati & PDF",
        description:
          "Ringkasan & intipati Belanjawan 2027 dengan PDF percuma: pelepasan cukai RM12,000, gaji minimum RM2,000, STR & SARA, Bantuan Khas RM1,500 penjawat awam, PTPTN.",
        keywords:
          "Belanjawan 2027, Bajet 2027, ringkasan Belanjawan 2027, bajet 2027 ringkasan, intipati Belanjawan 2027, bila Bajet 2027, tarikh Bajet 2027, pembentangan Bajet 2027, tema Bajet 2027, Belanjawan 2027 STR, Bajet 2027 STR, Belanjawan 2027 SARA, Belanjawan 2027 penjawat awam, Bajet 2027 penjawat awam, bonus penjawat awam 2027, Belanjawan 2027 pesara, Belanjawan 2027 OKU, Bajet 2027 OKU, Belanjawan 2027 guru KAFA, Belanjawan 2027 PTPTN, Belanjawan 2027 gaji minimum, Belanjawan 2027 pelepasan cukai, Belanjawan 2027 PDF, Bajet 2027 PDF, Belanjawan 2027 perniagaan, Belanjawan MADANI Kelima, Menggapai di Langit Mengakar di Bumi",
      },
      zh: {
        title: "2027财政预算案重点（大马财案）一页看懂＋免费PDF",
        description:
          "2027财案重点一页看懂＋免费PDF：个人减免增至12,000令吉、最低薪金2,000令吉、STR与SARA、公务员1,500令吉特别援助、PTPTN与AI措施。",
        keywords:
          "2027财政预算案, 2027年财政预算案, 2027预算案, 2027财案, 大马财案2027, 大马财政预算案2027, 马来西亚财政预算案2027, 2027财政预算案重点, 2027财政预算案几时, 2027财政预算案日期, 2027预算案STR SARA, 2027预算案最低薪金, 2027预算案最低工资, 2027预算案个人所得税减免, 2027预算案公务员, 2027预算案退休人员, 2027预算案残障人士, 2027预算案PTPTN, 2027预算案产假, 2027财案主题, 2027财政预算案主题",
      },
    },
  },
  "guides/chatgpt-intelligent-ui": {
    // Dedicated Intelligent UI topic; existing pricing/training ownership is unchanged.
    title: "ChatGPT Intelligent UI: What It Is & Examples",
    description: "What is ChatGPT Intelligent UI? Explore visual, interactive examples and prompts for business teams, with a practical guide from Anchor Sprint.",
    keywords: "ChatGPT Intelligent UI, what is ChatGPT Intelligent UI, ChatGPT interactive answers, generative UI, interactive AI examples",
    ogImage: "/images/chatgpt-intelligent-ui/og.webp",
    ogImageType: "image/webp",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "ChatGPT Intelligent UI: Maksud dan Contoh Interaktif",
        description: "Apakah ChatGPT Intelligent UI? Terokai contoh visual, kawalan interaktif dan prompt untuk pasukan perniagaan melalui panduan Anchor Sprint.",
        keywords: "ChatGPT Intelligent UI, apakah ChatGPT Intelligent UI, jawapan interaktif ChatGPT, UI generatif, contoh AI interaktif",
      },
      zh: {
        title: "ChatGPT Intelligent UI：智能交互界面与示例",
        description: "什么是 ChatGPT Intelligent UI？通过可视化示例、交互控件与实用提示词，了解如何让 ChatGPT 的回答更适合团队工作。",
        keywords: "ChatGPT Intelligent UI, ChatGPT 智能界面, ChatGPT 交互式回答, 生成式界面, AI 交互示例",
      },
    },
  },
  "guides/chatgpt-plans-malaysia": {
    // OWNER page (2026-10-08) for "ChatGPT plans Malaysia", "ChatGPT pricing
    // Malaysia", "ChatGPT price Malaysia" and "ChatGPT Business vs Enterprise".
    // The partner page keeps "OpenAI (Select) partner Malaysia" and "ChatGPT
    // Enterprise Malaysia"; /academy/chatgpt-training/ owns "ChatGPT training
    // Malaysia". Never compares with
    // another vendor. Figures: docs/research/chatgpt-plans-malaysia.md.
    title: "ChatGPT Pricing Malaysia: Every Plan in RM + Which to Pick",
    description:
      "ChatGPT Free, Go, Plus, Pro, Business and Enterprise compared for Malaysian teams: USD and RM prices, seat rules, admin, security and data training defaults.",
    keywords:
      "ChatGPT plans Malaysia, ChatGPT pricing Malaysia, ChatGPT price Malaysia, ChatGPT Business vs Enterprise, ChatGPT Plus price Malaysia, ChatGPT Business price Malaysia, ChatGPT Pro price Malaysia, ChatGPT Go Malaysia, ChatGPT Team renamed Business, compare ChatGPT plans, which ChatGPT plan",
    ogImage: "/images/partners/openai-partner-og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Harga ChatGPT Malaysia: Semua Pelan dalam RM + Pilihan Terbaik",
        description:
          "ChatGPT Free, Go, Plus, Pro, Business dan Enterprise dibandingkan untuk pasukan di Malaysia: harga USD dan RM, syarat seat, pentadbiran, keselamatan dan latihan data.",
        keywords:
          "pelan ChatGPT Malaysia, harga ChatGPT Malaysia, harga ChatGPT Plus Malaysia, ChatGPT Business lwn Enterprise, harga ChatGPT Business Malaysia, bandingkan pelan ChatGPT, pelan ChatGPT mana",
      },
      zh: {
        title: "马来西亚 ChatGPT 价格：全部方案马币价与选择建议",
        description:
          "为马来西亚团队比较 ChatGPT Free、Go、Plus、Pro、Business 与 Enterprise：美元与马币价格、席位规则、管理、安全及数据训练默认设置。",
        keywords:
          "马来西亚 ChatGPT 方案, 马来西亚 ChatGPT 价格, ChatGPT Plus 马币价格, ChatGPT Business 与 Enterprise 区别, ChatGPT Business 马来西亚价格, ChatGPT 方案比较",
      },
    },
  },
  "guides/claude-plans-malaysia": {
    // CTR rewrite (2026-08-10). 7,913 impressions at 0.56% — 60% of the site's
    // entire position-4-10 pool sits on this one page. It ranks p4.2 for
    // "claude price" and p5.6 for "claude ai pricing", so the constraint is the
    // click, not the rank. Anthropic's own pricing page sits above us on every
    // one of those queries and it is a plan grid — so the title has to promise
    // the two things a plan grid cannot: the number in ringgit, and a
    // recommendation. "2026" is dropped from the title (freshness now comes
    // from dateModified in the schema) to buy the room for "Which to Pick".
    title: "Claude Pricing Malaysia: Every Plan in RM + Which to Pick",
    description:
      "Claude Free, Pro, Max, Team and Enterprise priced in ringgit, with a plain recommendation for each team size and a 3-year cost calculator. Updated 2026.",
    keywords:
      "Claude pricing Malaysia, Claude price Malaysia, Claude subscription price Malaysia, Claude plans Malaysia, compare Claude plans, Claude plan comparison, Claude Free vs Pro vs Team vs Enterprise, which Claude plan, Claude Team plan Malaysia, Claude Enterprise Malaysia, Claude Team vs Enterprise",
    ogImage: "/blog/claude-ai-pricing-malaysia-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    localized: {
      ms: {
        title: "Harga Claude Malaysia: Semua Pelan dalam RM + Pilihan Terbaik",
        description:
          "Harga Claude Free, Pro, Max, Team dan Enterprise dalam ringgit, dengan cadangan jelas untuk setiap saiz pasukan dan kalkulator kos 3 tahun.",
        keywords:
          "harga Claude Malaysia, harga langganan Claude, pelan Claude Malaysia, bandingkan pelan Claude, Claude Team atau Enterprise, harga Claude dalam ringgit",
      },
      zh: {
        title: "马来西亚 Claude 价格：全部方案马币价与选择建议",
        description:
          "以马币列出 Claude Free、Pro、Max、Team 与 Enterprise 的价格，按团队规模给出明确建议，并附 3 年成本计算器。",
        keywords:
          "马来西亚 Claude 价格, Claude 订阅价格, Claude 方案比较, Claude Team 还是 Enterprise, Claude 马币价格, Claude 团队版价格",
      },
    },
  },
  "guides/claude-privacy-compliance-malaysia": {
    title: "Claude Privacy & Compliance: Individual, Team & Enterprise",
    description:
      "Can my admin see my Claude chats? Does Anthropic train on my data? A plain-English guide to Claude privacy, data retention & compliance across all plans.",
    keywords:
      "can my admin see my Claude chats, can my company see my Claude chats, who can see my Claude conversations, Claude privacy, Claude Team privacy, Claude Enterprise privacy, Claude Enterprise data privacy, does Anthropic train on my data, Claude data retention, Claude SOC 2 HIPAA compliance, Claude Compliance API, Claude admin access conversations, Claude PDPA Malaysia, Claude Enterprise compliance Malaysia, is Claude safe for business data, privasi Claude, pematuhan Claude Malaysia",
    localized: {
      ms: {
        title: "Privasi & Pematuhan Claude: Individu, Team & Enterprise",
        description:
          "Bolehkah admin anda melihat chat Claude anda? Adakah Anthropic melatih model dengan data anda? Panduan privasi, pengekalan data & pematuhan Claude untuk pasukan Malaysia.",
        keywords:
          "bolehkah admin saya lihat chat Claude, bolehkah syarikat saya baca perbualan Claude, privasi Claude, privasi Claude Team Enterprise, adakah Anthropic melatih model dengan data saya, pengekalan data Claude, pematuhan Claude Malaysia, Claude PDPA Malaysia, adakah Claude selamat untuk data syarikat, keselamatan data Claude untuk perniagaan, pematuhan SOC 2 HIPAA Claude",
      },
      zh: {
        title: "Claude 隐私与合规：个人、Team 与 Enterprise 方案",
        description:
          "公司管理员能看到我的 Claude 聊天吗？Anthropic 会用我的数据训练模型吗？一份面向马来西亚团队的 Claude 隐私、数据保留与合规（SOC 2、HIPAA、PDPA）指南。",
        keywords:
          "管理员能看到我的 Claude 聊天吗, 公司能看到我的 Claude 对话吗, Claude 隐私, Claude Team 隐私, Claude Enterprise 隐私, Anthropic 会用我的数据训练吗, Claude 数据保留, Claude 合规 马来西亚, Claude PDPA 马来西亚, Claude 商业数据安全",
      },
    },
    ogImage: "/blog/claude-ai-pricing-malaysia-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
  },
  "enterprise": {
    title: "IT Supplier & Reseller Malaysia | AI, Cloud & Licences",
    description:
      "IT supplier in Malaysia for NVIDIA DGX Spark and GPUs, OpenAI, Alibaba Cloud, Microsoft, Red Hat, Adobe, GitHub, Dell and Lenovo. RM quotes, SST and MyInvois handled.",
    keywords:
      "IT supplier Malaysia, IT reseller Malaysia, IT distributor Malaysia, authorised reseller Malaysia, enterprise software reseller Malaysia, enterprise software supplier Malaysia, IT hardware supplier Malaysia, server supplier Malaysia, software license Malaysia, software licensing Malaysia, enterprise IT procurement Malaysia, buy enterprise software Malaysia, ICT reseller Malaysia, GitHub reseller Malaysia, GitHub Enterprise Malaysia, GitHub Copilot Malaysia, buy GitHub Copilot Malaysia",
    ogImage: "/images/enterprise/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Pembekal & Penjual Semula IT Malaysia | AI, Awan & Lesen",
        description:
          "Pembekal IT di Malaysia untuk NVIDIA DGX Spark dan GPU, OpenAI, Alibaba Cloud, Microsoft, Red Hat, Adobe, GitHub, Dell dan Lenovo. Sebut harga RM, SST dan MyInvois diuruskan.",
        keywords:
          "pembekal IT Malaysia, penjual semula IT Malaysia, pembekal perisian enterprise Malaysia, pembekal perkakasan IT Malaysia, pembekal server Malaysia, lesen perisian Malaysia, harga lesen perisian, pelesenan perisian Malaysia, beli perisian syarikat Malaysia, penjual semula GitHub Malaysia, GitHub Copilot Malaysia",
      },
      zh: {
        title: "马来西亚 IT 供应商与经销商 | AI、云与软件许可",
        description:
          "马来西亚 IT 供应商：NVIDIA DGX Spark 与 GPU、OpenAI、Alibaba Cloud、Microsoft、Red Hat、Adobe、GitHub、Dell 与 Lenovo。令吉报价，SST 与 MyInvois 一并办理。",
        keywords:
          "马来西亚 IT 供应商, 马来西亚 IT 经销商, 马来西亚企业软件经销商, 马来西亚 IT 硬件供应商, 马来西亚服务器供应商, 软件授权 马来西亚, 企业软件采购 马来西亚, 企业软件报价, GitHub 经销商 马来西亚, GitHub Copilot 马来西亚",
      },
    },
  },
  "academy/claude-training-kuala-lumpur": {
    // Per-location page. NOT a doorway: worked examples, delivery logistics and
    // funding position all differ by market — see the anti-doorway note in
    // content/claude-training-locations.ts. Competitor equivalents measured at
    // 92-94% identical; ours must never approach that.
    title: "Claude AI Training Kuala Lumpur & Selangor | Register Interest",
    description:
      "HRD Corp claimable Claude training for teams in KL, Petaling Jaya, Subang and Shah Alam — delivered in your office. Register interest and we open a cohort when the numbers are there.",
    keywords:
      "Claude AI training Kuala Lumpur, Claude training Selangor, AI training KL, AI training Petaling Jaya, HRD Corp claimable AI training KL, corporate AI training Kuala Lumpur, Claude certification Kuala Lumpur, in-house AI training Selangor, AI course Shah Alam",
    ogImage: "/images/academy/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
  },
  "academy/claude-training-penang": {
    // Per-location page. NOT a doorway: worked examples, delivery logistics and
    // funding position all differ by market — see the anti-doorway note in
    // content/claude-training-locations.ts. Competitor equivalents measured at
    // 92-94% identical; ours must never approach that.
    title: "Claude AI Training Penang | E&E and Manufacturing Teams",
    description:
      "HRD Corp claimable Claude training for Penang manufacturers — production, quality and supplier workflows. Register interest and we bring a certified trainer when a cohort fills.",
    keywords:
      "Claude AI training Penang, AI training Penang, AI training Bayan Lepas, manufacturing AI training Malaysia, HRD Corp claimable AI training Penang, Claude certification Penang, corporate AI training Penang, E&E AI training",
    ogImage: "/images/academy/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
  },
  "academy/claude-training-johor": {
    // Per-location page. NOT a doorway: worked examples, delivery logistics and
    // funding position all differ by market — see the anti-doorway note in
    // content/claude-training-locations.ts. Competitor equivalents measured at
    // 92-94% identical; ours must never approach that.
    title: "Claude AI Training Johor Bahru | Cross-Border and Logistics Teams",
    description:
      "HRD Corp claimable Claude training for Johor teams — logistics, manufacturing and cross-border operations with Singapore. Register interest and we open a cohort when numbers allow.",
    keywords:
      "Claude AI training Johor, AI training Johor Bahru, AI training Iskandar Puteri, HRD Corp claimable AI training Johor, Claude certification Johor, corporate AI training JB, logistics AI training Malaysia",
    ogImage: "/images/academy/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
  },
  // Owns the CERTIFICATION cluster. We sold Claude certification prep and targeted
  // the term nowhere — absent from the top 20 and the AI Overview on
  // "claude certification course malaysia", while Agmo holds it on a dedicated
  // domain. Certification terms were previously scattered across /academy/ and the
  // three location pages with no page owning the head term.
  // Exam fees are Anthropic's and public; OUR course fee is never published here
  // (Jazz, 2026-09-13) — see content/academy-certification.ts for the full rules.
  "academy/claude-certification": {
    title: "Claude Certification Malaysia — All 4 Exams, Costs & Prep",
    description:
      "The four Anthropic Claude certifications explained: CCAO-F, CCDV-F, CCAR-F and CCAR-P, what each exam costs, and which track each role should take. HRD Corp claimable prep with the exam included.",
    keywords:
      "Claude certification Malaysia, Claude certification cost, Claude certification course Malaysia, Claude Certified Associate, Claude Certified Developer, Claude Certified Architect, CCAO-F, CCDV-F, CCAR-F, CCAR-P, Claude certification exam prep, Claude AI certification Singapore, Claude certification training",
    ogImage: "/images/academy/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Pensijilan Claude Malaysia — 4 Peperiksaan, Kos & Persediaan",
        description:
          "Empat pensijilan Claude Anthropic dijelaskan: CCAO-F, CCDV-F, CCAR-F dan CCAR-P, kos setiap peperiksaan, dan trek mana untuk setiap peranan. Persediaan boleh tuntut HRD Corp dengan peperiksaan disertakan.",
        keywords:
          "pensijilan Claude Malaysia, kos pensijilan Claude, kursus pensijilan Claude Malaysia, Claude Certified Associate, Claude Certified Architect, persediaan peperiksaan Claude, latihan pensijilan Claude",
      },
      zh: {
        title: "Claude 认证（马来西亚）— 四项考试、费用与备考",
        description:
          "完整解析 Anthropic 的四项 Claude 认证：CCAO-F、CCDV-F、CCAR-F 与 CCAR-P，各项考试费用，以及每个岗位该考哪一张。备考课程可申请 HRD Corp 补助，考试费已含在课程费内。",
        keywords:
          "Claude 认证 马来西亚, Claude 认证费用, Claude 认证课程, Claude Certified Associate, Claude Certified Architect, Claude 考试备考, Claude 认证培训",
      },
    },
  },
  // OWNER page (2026-10-08) for "ChatGPT training Malaysia", "ChatGPT course
  // Malaysia", "ChatGPT corporate training", "HRD Corp ChatGPT training" and
  // "ChatGPT HRDF course". /partners/openai-partner/ links here with the anchor
  // "ChatGPT training for teams" and no longer targets these terms;
  // /services/training-education/ keeps the general "HRD Corp claimable AI
  // training". No price published (Academy rule). Content rules:
  // src/content/chatgpt-training.ts.
  "academy/chatgpt-training": {
    title: "ChatGPT Training for Teams in Malaysia | HRD Corp Claimable",
    description:
      "Hands-on ChatGPT training for business teams in Malaysia: Projects, custom GPTs, data analysis, deep research and safe use of always-on agents. HRD Corp claimable.",
    keywords:
      "ChatGPT training Malaysia, ChatGPT course Malaysia, ChatGPT corporate training, HRD Corp ChatGPT training, ChatGPT HRDF course, ChatGPT workshop for teams, ChatGPT training Kuala Lumpur, ChatGPT agents training",
    ogImage: "/images/academy/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Latihan ChatGPT untuk Pasukan di Malaysia | Boleh Tuntut HRD Corp",
        description:
          "Latihan ChatGPT secara praktikal untuk pasukan perniagaan di Malaysia: Projects, custom GPTs, analisis data, deep research dan penggunaan ejen sentiasa aktif dengan selamat. Boleh dituntut HRD Corp.",
        keywords:
          "latihan ChatGPT Malaysia, kursus ChatGPT Malaysia, kursus ChatGPT HRD Corp, kursus ChatGPT HRDF, latihan ChatGPT korporat, bengkel ChatGPT untuk syarikat, latihan ChatGPT Kuala Lumpur",
      },
      zh: {
        title: "马来西亚团队 ChatGPT 培训 | 可申领 HRD Corp",
        description:
          "面向马来西亚企业团队的 ChatGPT 实战培训：Projects、custom GPTs、数据分析、deep research，以及安全使用全天候智能体。可申领 HRD Corp。",
        keywords:
          "ChatGPT 培训 马来西亚, ChatGPT 课程 马来西亚, HRD Corp ChatGPT 培训, HRDF ChatGPT 课程, 企业 ChatGPT 培训, ChatGPT 企业内训, 吉隆坡 ChatGPT 培训",
      },
    },
  },
  "academy/register-interest": {
    // Waiting list, not a booking. Public dates are not open — first intake is
    // September 2026 — so nothing here may read as a confirmed seat or a price.
    title: "Register Interest — Anchor Academy Courses",
    description:
      "Join the waiting list for Claude certification prep or Vibe Engineering. Tell us the course, preferred month, headcount and whether you need the HRD Corp claim filed.",
    keywords:
      "Claude certification course Malaysia, Claude training waiting list, Vibe Engineering course Malaysia, HRD Corp claimable AI course, Claude Certified Associate Malaysia, Claude Certified Architect Malaysia, AI course registration Malaysia, corporate AI training enquiry Malaysia",
    localized: {
      ms: {
        title: "Daftar Minat — Kursus Anchor Academy",
        description:
          "Sertai senarai menunggu untuk persediaan persijilan Claude atau Vibe Engineering. Beritahu kami kursus, bulan pilihan, bilangan peserta dan sama ada anda perlukan tuntutan HRD Corp difailkan.",
        keywords:
          "kursus persijilan Claude Malaysia, senarai menunggu latihan Claude, kursus Vibe Engineering Malaysia, kursus AI boleh dituntut HRD Corp, pendaftaran kursus AI Malaysia, latihan AI korporat Malaysia",
      },
      zh: {
        title: "登记意向 — Anchor Academy 课程",
        description:
          "登记 Claude 认证备考或 Vibe Engineering 课程的候补名单。请告知课程、属意月份、参加人数，以及是否需要我们代办 HRD Corp 补助申请。",
        keywords:
          "Claude 认证课程 马来西亚, Claude 培训候补名单, Vibe Engineering 课程, HRD Corp 可申领 AI 课程, AI 课程报名 马来西亚, 企业 AI 培训",
      },
    },
    ogImage: "/images/academy/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
  },
  "enterprise/enquiry": {
    title: "Enterprise Quotation Enquiry — Software & Hardware in RM",
    description:
      "Pick brands, set quantities in seats or units, tell us the use case — we return an enterprise quotation in RM, usually within two working days.",
    keywords:
      "enterprise software quotation Malaysia, request software quote Malaysia, IT procurement enquiry Malaysia, software licence quote RM",
    ogImage: "/images/enterprise/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Pertanyaan Sebut Harga Enterprise",
        description:
          "Pilih jenama, tetapkan kuantiti dalam kerusi atau unit, nyatakan kes penggunaan — kami balas dengan sebut harga dalam RM, biasanya dalam dua hari bekerja.",
        keywords:
          "sebut harga perisian Malaysia, pertanyaan perolehan IT Malaysia, sebut harga lesen perisian RM",
      },
      zh: {
        title: "企业软硬件询价",
        description:
          "选择品牌、按席位或数量填写、说明用例——我们以令吉回复企业报价，通常两个工作日内。",
        keywords:
          "企业软件询价 马来西亚, IT 采购询价, 软件授权报价 令吉",
      },
    },
  },
  "enterprise/microsoft-malaysia": {
    title: "Authorised Microsoft Reseller Malaysia — Licensing & Renewals",
    description:
      "Authorised Microsoft reseller in Malaysia: Microsoft 365, Windows and server licensing, Azure and GitHub Enterprise, quoted in RM with SST stated.",
    keywords:
      "Microsoft reseller Malaysia, authorised Microsoft reseller Malaysia, buy Microsoft 365 Malaysia, Microsoft licensing Malaysia, Microsoft CSP Malaysia, Azure billing Malaysia, Windows Server licence Malaysia, Microsoft supplier Malaysia, Microsoft Malaysia supplier, buy Microsoft Malaysia, Microsoft distributor Malaysia, Microsoft price Malaysia",
    ogImage: "/images/enterprise/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Penjual Semula Sah Microsoft Malaysia — Pelesenan & Pembaharuan",
        description:
          "Penjual semula sah Microsoft di Malaysia, termasuk GitHub Enterprise — sebut harga dalam RM, SST dan e-invois MyInvois diuruskan.",
        keywords:
          "penjual semula Microsoft Malaysia, penjual semula sah Microsoft Malaysia, beli Microsoft Malaysia, harga Microsoft Malaysia, pembaharuan Microsoft Malaysia",
      },
      zh: {
        title: "Microsoft 马来西亚授权经销商 — 授权、供应与续期",
        description:
          "Microsoft 马来西亚授权经销商，包括 GitHub Enterprise——以令吉报价，SST 与 MyInvois 电子发票一并办理。",
        keywords:
          "Microsoft 马来西亚经销商, Microsoft 马来西亚授权经销商, 马来西亚买 Microsoft, Microsoft 价格 马来西亚, Microsoft 续期",
      },
    },
  },
  "enterprise/dell-technologies-malaysia": {
    title: "Authorised Dell Reseller Malaysia — Servers, GPU Compute & Provisioning",
    description:
      "Authorised Dell reseller in Malaysia: PowerEdge servers, GPU compute, workstations and storage, specified against the workload and quoted in RM with SST stated.",
    // "Dell Technologies distributor Malaysia" was dropped deliberately: we are a
    // partner and reseller, not a distributor, and the sitewide rule keeps those
    // words apart. Anyone searching it wants the distributor, not us.
    keywords:
      "Dell reseller Malaysia, authorised Dell reseller Malaysia, Dell partner Malaysia, Dell Technologies partner Malaysia, buy Dell server Malaysia, PowerEdge price Malaysia, Dell server provisioning Malaysia, Dell GPU server Malaysia, Dell workstation Malaysia, Dell ProSupport Malaysia, Dell Technologies supplier Malaysia",
    ogImage: "/images/enterprise/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Penjual Semula Sah Dell Malaysia — Pelayan, GPU & Penyediaan",
        description:
          "Penjual semula sah Dell di Malaysia — pelayan PowerEdge, komputan GPU, stesen kerja dan storan, disebut harga dalam RM dengan SST dinyatakan.",
        keywords:
          "penjual semula Dell Malaysia, penjual semula sah Dell Malaysia, rakan kongsi Dell Malaysia, pembekal pelayan Dell Malaysia, beli pelayan Dell Malaysia, harga pelayan PowerEdge Malaysia, stesen kerja Dell Malaysia",
      },
      zh: {
        title: "Dell 马来西亚授权经销商 — 服务器、GPU 算力与部署",
        description:
          "Dell 马来西亚授权经销商——PowerEdge 服务器、GPU 算力、工作站与存储，依实际工作负载配置，以令吉报价并注明 SST。",
        keywords:
          "Dell 马来西亚授权经销商, Dell 马来西亚合作伙伴, 马来西亚 Dell 服务器供应商, PowerEdge 服务器 马来西亚, 马来西亚买 Dell 服务器, Dell 工作站 马来西亚",
      },
    },
  },
  "enterprise/red-hat-malaysia": {
    title: "Authorised Red Hat Reseller Malaysia — Licensing & Renewals",
    description:
      "Authorised Red Hat reseller in Malaysia: Red Hat Enterprise Linux, OpenShift and Ansible subscriptions, quoted in RM with SST stated.",
    keywords:
      "Red Hat reseller Malaysia, authorised Red Hat reseller Malaysia, RHEL subscription Malaysia, OpenShift Malaysia, Ansible licence Malaysia, Red Hat renewal Malaysia, Red Hat supplier Malaysia, Red Hat Malaysia supplier, buy Red Hat Malaysia, Red Hat distributor Malaysia, Red Hat price Malaysia",
    ogImage: "/images/enterprise/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Penjual Semula Sah Red Hat Malaysia — Pelesenan & Pembaharuan",
        description:
          "Penjual semula sah Red Hat di Malaysia — sebut harga dalam RM, SST dan e-invois MyInvois diuruskan.",
        keywords:
          "penjual semula Red Hat Malaysia, penjual semula sah Red Hat Malaysia, beli Red Hat Malaysia, harga Red Hat Malaysia, pembaharuan Red Hat Malaysia",
      },
      zh: {
        title: "Red Hat 马来西亚授权经销商 — 授权、供应与续期",
        description:
          "Red Hat 马来西亚授权经销商——以令吉报价，SST 与 MyInvois 电子发票一并办理。",
        keywords:
          "Red Hat 马来西亚经销商, Red Hat 马来西亚授权经销商, 马来西亚买 Red Hat, Red Hat 价格 马来西亚, Red Hat 续期",
      },
    },
  },
  // Tender/RFQ intent, deliberately SEPARATE from the hub (buy intent) and the
  // enquiry form. Keywords target how a procurement officer or an integrator's
  // tender team searches — "tender", "RFQ", "specification", "LOA" — not
  // "buy"/"price", which the hub already owns. The live MY SERP for
  // "dgx spark tender rfq malaysia" already cites Anchor Sprint three times in
  // the AI Overview; this is the page that reader lands on.
  // NO MOF CLAIM anywhere — we are not MOF-registered. See the accuracy gates in
  // content/enterprise/dgx-spark-tender.ts before editing any of this.
  "enterprise/dgx-spark-tender-malaysia": {
    title: "DGX Spark Tender & RFQ Support Malaysia — Spec, LOA, RM Quote",
    description:
      "Tender and RFQ support for NVIDIA DGX Spark in Malaysia. Baseline specification you can lift, every equivalent OEM build, OEM Letter of Authorisation obtained for your tender, quotation in one working day.",
    keywords:
      "DGX Spark tender Malaysia, DGX Spark RFQ Malaysia, NVIDIA DGX Spark tender specification, GB10 tender specification Malaysia, AI supercomputer tender Malaysia, DGX Spark letter of authorisation, DGX Spark LOA Malaysia, DGX Spark quotation tender, GB10 RFQ Malaysia, DGX Spark government procurement Malaysia",
    ogImage: "/images/enterprise/dgx-spark-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1176,
    ogImageHeight: 750,
    localized: {
      ms: {
        title: "Sokongan Tender & RFQ DGX Spark Malaysia — Spesifikasi & LOA",
        description:
          "Sokongan tender dan RFQ untuk NVIDIA DGX Spark di Malaysia. Spesifikasi asas untuk dokumen tender anda, setiap binaan OEM setara, Surat Kebenaran OEM diperoleh untuk tender anda, sebut harga dalam satu hari bekerja.",
        keywords:
          "tender DGX Spark Malaysia, RFQ DGX Spark Malaysia, spesifikasi tender DGX Spark, spesifikasi tender GB10, surat kebenaran OEM DGX Spark, sebut harga tender DGX Spark, perolehan kerajaan DGX Spark",
      },
      zh: {
        title: "DGX Spark 招标与询价支持（马来西亚）— 规格、授权书与报价",
        description:
          "马来西亚 NVIDIA DGX Spark 招标与 RFQ 支持：可直接写入标书的基准规格、全部等效 OEM 机型、按标案取得的 OEM 授权书，报价一个工作天内回覆。",
        keywords:
          "DGX Spark 招标 马来西亚, DGX Spark 询价 马来西亚, DGX Spark 标书规格, GB10 招标规格, DGX Spark 授权书, DGX Spark 政府采购 马来西亚",
      },
    },
  },
  "enterprise/dgx-spark-malaysia/enquiry": {
    title: "DGX Spark Quotation Malaysia — Request a Price in RM",
    description:
      "Request a DGX Spark quotation in Malaysia. Pick the OEM build — NVIDIA, ASUS, Dell, HP, Lenovo, Acer — with quantity and workload. Quoted in RM, SST stated.",
    keywords:
      "DGX Spark quotation Malaysia, DGX Spark price Malaysia, buy DGX Spark Malaysia, DGX Spark quote RM, GB10 quotation Malaysia, ASUS Ascent GX10 price Malaysia, Lenovo ThinkStation PGX price Malaysia, HP ZGX Nano price Malaysia, Dell Pro Max GB10 price Malaysia",
    ogImage: "/images/enterprise/dgx-spark-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1176,
    ogImageHeight: 750,
    localized: {
      ms: {
        title: "Sebut Harga DGX Spark Malaysia — Minta Harga dalam RM",
        description:
          "Minta sebut harga DGX Spark di Malaysia. Pilih model OEM — NVIDIA, ASUS, Dell, HP, Lenovo, Acer — dengan kuantiti dan beban kerja. Sebut harga dalam RM, SST dinyatakan.",
        keywords:
          "sebut harga DGX Spark Malaysia, harga DGX Spark Malaysia, beli DGX Spark Malaysia, GB10 Malaysia, harga ASUS Ascent GX10 Malaysia",
      },
      zh: {
        title: "DGX Spark 报价 马来西亚 — 索取令吉报价",
        description:
          "在马来西亚索取 DGX Spark 报价。选择 OEM 型号——NVIDIA、ASUS、Dell、HP、Lenovo、Acer——填写数量与工作负载。以令吉报价，注明 SST。",
        keywords:
          "DGX Spark 报价 马来西亚, DGX Spark 价格 马来西亚, 马来西亚买 DGX Spark, GB10 马来西亚, ASUS Ascent GX10 价格 马来西亚",
      },
    },
  },
  "enterprise/lenovo-thinkstation-pgx-malaysia": {
    title: "Lenovo ThinkStation PGX Malaysia — GB10 Workstation in RM",
    description:
      "Buy the Lenovo ThinkStation PGX in Malaysia — NVIDIA GB10, 128 GB unified memory, TPM 2.0, Secure Boot and self-encrypting NVMe. Quoted in RM, SST stated.",
    keywords:
      "Lenovo ThinkStation PGX Malaysia, ThinkStation PGX price Malaysia, buy Lenovo ThinkStation PGX Malaysia, ThinkStation PGX supplier Malaysia, Lenovo GB10 Malaysia, Lenovo DGX Spark Malaysia, Lenovo AI workstation Malaysia, ThinkStation PGX quotation",
    ogImage: "/images/enterprise/dgx-lenovo-thinkstation-pgx-card.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Lenovo ThinkStation PGX Malaysia — Stesen Kerja GB10 dalam RM",
        description:
          "Beli Lenovo ThinkStation PGX di Malaysia — NVIDIA GB10, memori bersatu 128 GB, TPM 2.0, Secure Boot dan NVMe menyulit sendiri. Sebut harga dalam RM.",
        keywords:
          "Lenovo ThinkStation PGX Malaysia, ThinkStation PGX price Malaysia, buy Lenovo ThinkStation PGX Malaysia, ThinkStation PGX supplier Malaysia, Lenovo GB10 Malaysia, Lenovo DGX Spark Malaysia, Lenovo AI workstation Malaysia, ThinkStation PGX quotation",
      },
      zh: {
        title: "Lenovo ThinkStation PGX 马来西亚 — GB10 工作站，令吉报价",
        description:
          "在马来西亚购买 Lenovo ThinkStation PGX——NVIDIA GB10、128 GB 统一内存、TPM 2.0、Secure Boot 与自加密 NVMe。以令吉报价，注明 SST。",
        keywords:
          "Lenovo ThinkStation PGX Malaysia, ThinkStation PGX price Malaysia, buy Lenovo ThinkStation PGX Malaysia, ThinkStation PGX supplier Malaysia, Lenovo GB10 Malaysia, Lenovo DGX Spark Malaysia, Lenovo AI workstation Malaysia, ThinkStation PGX quotation",
      },
    },
  },
  "enterprise/hp-zgx-nano-malaysia": {
    title: "HP ZGX Nano Malaysia — GB10 AI Station, Quoted in RM",
    description:
      "Buy the HP ZGX Nano AI Station (G1n) in Malaysia — NVIDIA GB10, 128 GB unified memory, radio-free option and remote management. Quoted in RM, SST stated.",
    keywords:
      "HP ZGX Nano Malaysia, HP ZGX Nano G1n Malaysia, ZGX Nano price Malaysia, buy HP ZGX Nano Malaysia, HP GB10 Malaysia, HP DGX Spark Malaysia, HP AI station Malaysia, radio free AI workstation Malaysia",
    ogImage: "/images/enterprise/dgx-hp-zgx-nano-card.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "HP ZGX Nano Malaysia — Stesen AI GB10, Sebut Harga dalam RM",
        description:
          "Beli HP ZGX Nano AI Station (G1n) di Malaysia — NVIDIA GB10, memori bersatu 128 GB, pilihan tanpa radio dan pengurusan jarak jauh. Sebut harga dalam RM.",
        keywords:
          "HP ZGX Nano Malaysia, HP ZGX Nano G1n Malaysia, ZGX Nano price Malaysia, buy HP ZGX Nano Malaysia, HP GB10 Malaysia, HP DGX Spark Malaysia, HP AI station Malaysia, radio free AI workstation Malaysia",
      },
      zh: {
        title: "HP ZGX Nano 马来西亚 — GB10 AI 工作站，令吉报价",
        description:
          "在马来西亚购买 HP ZGX Nano AI Station (G1n)——NVIDIA GB10、128 GB 统一内存、可选无无线电配置与远程管理。以令吉报价。",
        keywords:
          "HP ZGX Nano Malaysia, HP ZGX Nano G1n Malaysia, ZGX Nano price Malaysia, buy HP ZGX Nano Malaysia, HP GB10 Malaysia, HP DGX Spark Malaysia, HP AI station Malaysia, radio free AI workstation Malaysia",
      },
    },
  },
  "enterprise/asus-ascent-gx10-malaysia": {
    title: "ASUS Ascent GX10 Malaysia — GB10 AI Supercomputer in RM",
    description:
      "ASUS Ascent GX10 128 GB: 1 TB RM38,000, 2 TB RM46,000, 4 TB RM49,000. Request a quote; taxes and delivery extra, subject to availability.",
    keywords:
      "ASUS Ascent GX10 Malaysia, Ascent GX10 price Malaysia, buy ASUS Ascent GX10 Malaysia, ASUS GX10 Malaysia, ASUS GB10 Malaysia, ASUS DGX Spark Malaysia, ASUS AI supercomputer Malaysia, GX10 supplier Malaysia",
    ogImage: "/images/enterprise/dgx-asus-ascent-gx10-card.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "ASUS Ascent GX10 Malaysia — Superkomputer AI GB10 dalam RM",
        description:
          "ASUS Ascent GX10 128 GB: 1 TB RM38,000, 2 TB RM46,000, 4 TB RM49,000. Minta sebut harga; cukai dan penghantaran tambahan, tertakluk pada stok.",
        keywords:
          "ASUS Ascent GX10 Malaysia, Ascent GX10 price Malaysia, buy ASUS Ascent GX10 Malaysia, ASUS GX10 Malaysia, ASUS GB10 Malaysia, ASUS DGX Spark Malaysia, ASUS AI supercomputer Malaysia, GX10 supplier Malaysia",
      },
      zh: {
        title: "ASUS Ascent GX10 马来西亚 — GB10 AI 超级电脑，令吉报价",
        description:
          "ASUS Ascent GX10 128 GB：1 TB RM38,000、2 TB RM46,000、4 TB RM49,000。申请报价；税费及配送另计，以库存情况为准。",
        keywords:
          "ASUS Ascent GX10 Malaysia, Ascent GX10 price Malaysia, buy ASUS Ascent GX10 Malaysia, ASUS GX10 Malaysia, ASUS GB10 Malaysia, ASUS DGX Spark Malaysia, ASUS AI supercomputer Malaysia, GX10 supplier Malaysia",
      },
    },
  },
  "enterprise/dell-pro-max-gb10-malaysia": {
    title: "Dell Pro Max with GB10 Malaysia — Quoted in RM",
    description:
      "Buy the Dell Pro Max with GB10 in Malaysia — NVIDIA GB10, 128 GB unified memory, inside your existing Dell procurement and service contracts. Quoted in RM.",
    keywords:
      "Dell Pro Max GB10 Malaysia, Dell Pro Max with GB10 Malaysia, Dell GB10 price Malaysia, buy Dell Pro Max GB10 Malaysia, Dell DGX Spark Malaysia, Dell AI workstation Malaysia, Dell Pro Max supplier Malaysia",
    ogImage: "/images/enterprise/dgx-dell-pro-max-gb10-card.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Dell Pro Max dengan GB10 Malaysia — Sebut Harga dalam RM",
        description:
          "Beli Dell Pro Max dengan GB10 di Malaysia — NVIDIA GB10, memori bersatu 128 GB, dalam kontrak perolehan dan servis Dell sedia ada anda. Sebut harga dalam RM.",
        keywords:
          "Dell Pro Max GB10 Malaysia, Dell Pro Max with GB10 Malaysia, Dell GB10 price Malaysia, buy Dell Pro Max GB10 Malaysia, Dell DGX Spark Malaysia, Dell AI workstation Malaysia, Dell Pro Max supplier Malaysia",
      },
      zh: {
        title: "Dell Pro Max with GB10 马来西亚 — 令吉报价",
        description:
          "在马来西亚购买 Dell Pro Max with GB10——NVIDIA GB10、128 GB 统一内存，纳入您现有的戴尔采购与服务合同。以令吉报价。",
        keywords:
          "Dell Pro Max GB10 Malaysia, Dell Pro Max with GB10 Malaysia, Dell GB10 price Malaysia, buy Dell Pro Max GB10 Malaysia, Dell DGX Spark Malaysia, Dell AI workstation Malaysia, Dell Pro Max supplier Malaysia",
      },
    },
  },
  "enterprise/acer-veriton-gn100-malaysia": {
    title: "Acer Veriton GN100 Malaysia — GB10 Mini Workstation in RM",
    description:
      "Buy the Acer Veriton GN100 AI Mini Workstation in Malaysia — NVIDIA GB10, 128 GB unified memory. Quoted in RM with SST stated and MyInvois handled.",
    keywords:
      "Acer Veriton GN100 Malaysia, Veriton GN100 price Malaysia, buy Acer Veriton GN100 Malaysia, Acer GB10 Malaysia, Acer DGX Spark Malaysia, Acer AI mini workstation Malaysia",
    ogImage: "/images/enterprise/dgx-acer-veriton-gn100-card.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Acer Veriton GN100 Malaysia — Stesen Kerja Mini GB10 dalam RM",
        description:
          "Beli Acer Veriton GN100 AI Mini Workstation di Malaysia — NVIDIA GB10, memori bersatu 128 GB. Sebut harga dalam RM dengan SST dinyatakan.",
        keywords:
          "Acer Veriton GN100 Malaysia, Veriton GN100 price Malaysia, buy Acer Veriton GN100 Malaysia, Acer GB10 Malaysia, Acer DGX Spark Malaysia, Acer AI mini workstation Malaysia",
      },
      zh: {
        title: "Acer Veriton GN100 马来西亚 — GB10 迷你工作站，令吉报价",
        description:
          "在马来西亚购买 Acer Veriton GN100 AI 迷你工作站——NVIDIA GB10、128 GB 统一内存。以令吉报价，注明 SST。",
        keywords:
          "Acer Veriton GN100 Malaysia, Veriton GN100 price Malaysia, buy Acer Veriton GN100 Malaysia, Acer GB10 Malaysia, Acer DGX Spark Malaysia, Acer AI mini workstation Malaysia",
      },
    },
  },
  "enterprise/dgx-spark-malaysia": {
    title: "DGX Spark Malaysia — NVIDIA & Every OEM Build, Quoted in RM",
    description:
      "Authorised NVIDIA DGX Spark reseller in Malaysia — Founders Edition plus ASUS Ascent GX10, Dell Pro Max GB10, HP ZGX Nano, Lenovo ThinkStation PGX and Acer Veriton GN100.",
    keywords:
      "DGX Spark Malaysia, NVIDIA DGX Spark Malaysia, DGX Spark price Malaysia, buy DGX Spark Malaysia, DGX Spark supplier Malaysia, DGX Spark reseller Malaysia, authorised DGX Spark reseller Malaysia, GB10 Malaysia, Grace Blackwell Malaysia, ASUS Ascent GX10 Malaysia, Dell Pro Max GB10 Malaysia, HP ZGX Nano Malaysia, Lenovo ThinkStation PGX Malaysia, Acer Veriton GN100 Malaysia, Gigabyte AI TOP ATOM Malaysia, MSI EdgeXpert Malaysia, MSI GB10 Malaysia, personal AI supercomputer Malaysia, desktop AI supercomputer Malaysia, local AI workstation Malaysia, AI workstation Malaysia price, RTX Spark vs DGX Spark, DGX Spark vs RTX Spark Malaysia",
    ogImage: "/images/enterprise/dgx-spark-hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1176,
    ogImageHeight: 750,
    localized: {
      ms: {
        title: "DGX Spark Malaysia — NVIDIA & Semua Model OEM, Sebut Harga RM",
        description:
          "Penjual semula sah NVIDIA DGX Spark di Malaysia — Founders Edition serta ASUS Ascent GX10, Dell Pro Max GB10, HP ZGX Nano, Lenovo ThinkStation PGX dan Acer Veriton GN100.",
        keywords:
          "DGX Spark Malaysia, harga DGX Spark Malaysia, beli DGX Spark Malaysia, pembekal DGX Spark Malaysia, GB10 Malaysia, ASUS Ascent GX10 Malaysia, Lenovo ThinkStation PGX Malaysia, superkomputer AI peribadi Malaysia, stesen kerja AI Malaysia",
      },
      zh: {
        title: "DGX Spark 马来西亚 — NVIDIA 与各 OEM 型号，令吉报价",
        description:
          "马来西亚 NVIDIA DGX Spark 授权经销商——Founders Edition 及 ASUS Ascent GX10、Dell Pro Max GB10、HP ZGX Nano、Lenovo ThinkStation PGX、Acer Veriton GN100。",
        keywords:
          "DGX Spark 马来西亚, NVIDIA DGX Spark 马来西亚, DGX Spark 价格 马来西亚, 马来西亚买 DGX Spark, DGX Spark 供应商 马来西亚, GB10 马来西亚, ASUS Ascent GX10 马来西亚, Lenovo ThinkStation PGX 马来西亚, 个人 AI 超级电脑 马来西亚, AI 工作站 马来西亚",
      },
    },
  },
  "enterprise/nvidia-malaysia": {
    title: "NVIDIA Supplier Malaysia — GPUs, AI Enterprise & Systems",
    description:
      "NVIDIA supplier in Malaysia — H100, H200, L40S and RTX GPUs, AI Enterprise licensing and certified systems, quoted in RM with SST and MyInvois handled.",
    keywords:
      "NVIDIA Malaysia supplier, NVIDIA supplier Malaysia, NVIDIA reseller Malaysia, buy NVIDIA GPU Malaysia, NVIDIA distributor Malaysia, H100 Malaysia, H200 Malaysia, L40S Malaysia, RTX workstation Malaysia, NVIDIA AI Enterprise Malaysia, GPU server Malaysia, AI GPU price Malaysia",
    ogImage: "/images/enterprise/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Pembekal NVIDIA Malaysia — GPU, AI Enterprise & Sistem",
        description:
          "Pembekal NVIDIA di Malaysia — GPU H100, H200, L40S dan RTX, pelesenan AI Enterprise dan sistem diperakui, sebut harga dalam RM dengan SST dan MyInvois diuruskan.",
        keywords:
          "pembekal NVIDIA Malaysia, penjual semula NVIDIA Malaysia, beli GPU NVIDIA Malaysia, harga GPU AI Malaysia, H100 Malaysia, pelayan GPU Malaysia",
      },
      zh: {
        title: "NVIDIA 马来西亚供应商 — GPU、AI Enterprise 与系统",
        description:
          "马来西亚 NVIDIA 供应商——H100、H200、L40S 及 RTX GPU、AI Enterprise 授权与认证系统，以令吉报价，含 SST 与 MyInvois。",
        keywords:
          "NVIDIA 马来西亚供应商, NVIDIA 马来西亚经销商, 马来西亚买 NVIDIA GPU, H100 马来西亚, AI GPU 价格 马来西亚, GPU 服务器 马来西亚",
      },
    },
  },
  // The KYC intake for advanced GPUs. Keywords deliberately target the
  // documentation question ("end user certificate", "export licence GPU
  // Malaysia") rather than competing with the brand page above on "buy H200
  // Malaysia" — that would repeat the ai-solutions / digital-transformation
  // self-cannibalisation. See docs/analytics/competitors/.
  "enterprise/nvidia-malaysia/enquiry": {
    title: "NVIDIA GPU Quotation Malaysia — Export Documentation First",
    description:
      "Request an NVIDIA GPU quotation in Malaysia. Data-centre GPUs need signed end-user documentation before pricing — answer the export questions once here.",
    keywords:
      "NVIDIA GPU quotation Malaysia, H200 quotation Malaysia, B300 Malaysia, GB300 Malaysia, HGX Malaysia, buy data centre GPU Malaysia, GPU end user certificate Malaysia, export documentation GPU Malaysia, NVIDIA GPU enquiry Malaysia",
    ogImage: "/images/enterprise/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Sebut Harga GPU NVIDIA Malaysia — Dokumentasi Eksport Dahulu",
        description:
          "Minta sebut harga GPU NVIDIA di Malaysia. GPU pusat data memerlukan dokumentasi pengguna akhir yang ditandatangani sebelum harga dikeluarkan.",
        keywords:
          "sebut harga GPU NVIDIA Malaysia, H200 Malaysia, B300 Malaysia, beli GPU pusat data Malaysia, dokumentasi eksport GPU Malaysia, sijil pengguna akhir GPU Malaysia",
      },
      zh: {
        title: "NVIDIA GPU 马来西亚报价 — 先完成出口文件",
        description:
          "在马来西亚申请 NVIDIA GPU 报价。数据中心 GPU 需先签署最终用户文件才能发出价格，请在此一次性回答出口管制问题。",
        keywords:
          "NVIDIA GPU 马来西亚报价, H200 马来西亚, B300 马来西亚, 马来西亚买数据中心 GPU, GPU 出口文件 马来西亚, 最终用户证明 马来西亚",
      },
    },
  },
  "enterprise/adobe-malaysia": {
    title: "Adobe Reseller Malaysia — Creative Cloud & Acrobat Licensing",
    description:
      "Adobe Certified Reseller in Selangor, Malaysia. Genuine Creative Cloud, Acrobat and VIP licensing for teams — quoted in RM with SST stated and MyInvois handled.",
    keywords:
      "Adobe reseller Malaysia, Adobe Certified Reseller Malaysia, buy Adobe Creative Cloud Malaysia, Adobe VIP licensing Malaysia, Adobe Acrobat licence Malaysia, Adobe renewal Malaysia, Creative Cloud for teams price Malaysia, Adobe supplier Malaysia, Adobe Malaysia supplier, buy Adobe Malaysia, Adobe distributor Malaysia, Adobe price Malaysia",
    ogImage: "/images/enterprise/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    localized: {
      ms: {
        title: "Penjual Semula Adobe Malaysia — Pelesenan Creative Cloud & Acrobat",
        description:
          "Adobe Certified Reseller di Malaysia — pelesenan Creative Cloud individu, pasukan dan enterprise (VIP/ETLA), Acrobat, sokongan pelaksanaan dan pembaharuan, disebut harga dalam RM.",
        keywords:
          "penjual semula Adobe Malaysia, beli Adobe Creative Cloud Malaysia, pelesenan Adobe VIP Malaysia, lesen Adobe Acrobat Malaysia, pembaharuan Adobe Malaysia",
      },
      zh: {
        title: "Adobe 马来西亚经销 — Creative Cloud 与 Acrobat 授权",
        description:
          "马来西亚 Adobe Certified Reseller——Creative Cloud 个人、团队与企业（VIP/ETLA）授权、Acrobat、部署支持与续期，以令吉报价。",
        keywords:
          "Adobe 马来西亚经销商, 马来西亚买 Adobe Creative Cloud, Adobe VIP 授权 马来西亚, Adobe Acrobat 许可证, Adobe 续期 马来西亚",
      },
    },
  },
  "blog/adobe-license-compliance-audit-malaysia-guide": {
    title: "Adobe License Compliance Audits: A Malaysian Business Guide",
    description:
      "62% of companies faced software audits in 2024. How Malaysian businesses can prepare for Adobe compliance reviews and avoid BSA penalties.",
    keywords:
      "Adobe license audit Malaysia, software compliance, BSA audit, Adobe licensing, software asset management Malaysia",
    ogImage:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1200&h=630&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["Adobe", "Compliance", "Malaysia", "Enterprise"],
  },
  "blog/ai-voice-agents-malaysia-business-2026": {
    title: "AI Voice Agents Malaysia: Where They Actually Fit in 2026",
    description:
      "What an AI voice agent is, where it beats chat for a Malaysian business, where WhatsApp still wins, and how to pick a first workflow that actually pays back.",
    keywords:
      "AI voice agent Malaysia, AI voice agents Malaysia, AI call agent Malaysia, AI call automation Malaysia, AI phone agent Malaysia, what is an AI voice agent, voice AI for customer service, multilingual AI voice agent Malaysia, AI receptionist Malaysia, after-hours call handling Malaysia",
    ogImage: "/blog/ai-voice-agents-malaysia-business-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["AI Agents", "Automation", "Malaysia Business", "Insights"],
  },
  "blog/ai-team-members-openclaw-malaysian-business": {
    title:
      "AI Assistants for Malaysian Business | OpenClaw Guide",
    description:
      "Discover how Malaysian businesses deploy AI assistants like OpenClaw that go beyond chatbots — handling real tasks across WhatsApp, Teams, and more. Practical guide.",
    keywords:
      "AI assistant Malaysia, AI automation Malaysian business, OpenClaw AI, AI for business Malaysia, business automation WhatsApp Malaysia, open source AI assistant, AI team member",
    ogImage:
      "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1200&h=630&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["Insights", "AI", "AI Agents", "Malaysia Business", "Open Source"],
  },
  "blog/malaysia-einvoice-ai-automation-sme-playbook-2026": {
    title: "Malaysia e-Invoice Automation Guide for Businesses in 2026",
    description:
      "Prepare your business for Malaysia e-Invoice 2026 with an AI automation playbook covering workflow design, exception handling, and faster compliance rollout today.",
    keywords:
      "Malaysia e-Invoice automation, e-Invoice business Malaysia 2026, AI finance automation Malaysia, LHDN e-Invoice compliance, business process automation",
    ogImage:
      "/blog/malaysia-einvoice-ai-automation-sme-playbook-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1792,
    ogImageHeight: 1024,
    tags: ["AI", "Automation", "Malaysia Business", "Compliance"],
  },
  "blog/malaysia-einvoice-automation-playbook-2026": {
    title: "Malaysia e-Invoice Automation: Business Fixes Before 2027",
    description:
      "Malaysia e-invoice automation for businesses: fix MyInvois workflow gaps, API handoffs, approval queues, and submission delays before 2027. Book a consultation today.",
    keywords:
      "malaysia e-invoice automation, MyInvois workflow, e-Invoice business Malaysia, MyInvois API integration, business digitalisation Malaysia, invoice process automation Malaysia",
    ogImage: "/blog/malaysia-einvoice-automation-playbook-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1792,
    ogImageHeight: 1024,
    tags: ["Automation", "Malaysia Business", "Compliance"],
  },
  "blog/malaysia-einvoice-tin-brn-validation-august-2026": {
    title: "Malaysia e-Invoice Validation: What to Fix Before Aug 2026",
    description:
      "Malaysia e-Invoice validation starts 1 August 2026. Fix TIN, BRN, and source data gaps now so MyInvois submissions pass cleanly. Book a planning call today.",
    keywords:
      "Malaysia e-Invoice validation, MyInvois TIN validation, BRN validation Malaysia, LHDN e-Invoice August 2026, MyInvois API integration",
    ogImage: "/blog/malaysia-einvoice-tin-brn-validation-august-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1792,
    ogImageHeight: 1024,
    tags: ["News", "Compliance", "Malaysia Business", "Automation"],
  },
  "blog/malaysia-einvoice-extension-2026-businesses": {
    title: "Malaysia e-Invoice Extension 2026: Why Businesses Automate",
    description:
      "Malaysia's e-Invoice extension gives businesses more time, not less work. Fix workflows and automate now before deadline pressure returns.",
    keywords:
      "Malaysia e-Invoice extension 2026, e-Invoice Malaysia business, MyInvois automation, e-Invoice implementation timeline, e-Invoice relaxation period 2027, RM1 million e-Invoice exemption",
    ogImage: "/blog/malaysia-einvoice-extension-2026-businesses/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1792,
    ogImageHeight: 1024,
    tags: ["News", "Compliance", "Malaysia Business", "Automation"],
  },
  "blog/premium-web-design-accessible-ai-development": {
    title: "AI-Assisted Web Design Malaysia for Businesses",
    description:
      "See how AI-assisted web design helps Malaysian businesses get premium custom websites faster while preserving strategy, quality, performance, and ownership.",
    keywords:
      "AI-assisted web design Malaysia, AI web development Malaysia, premium web design Malaysia, AI website design for businesses, custom web design workflow Malaysia, web design services Selangor",
    ogImage: "/blog/premium-web-design-accessible-ai-development/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1792,
    ogImageHeight: 1024,
    tags: ["Insights", "AI", "Malaysia"],
  },
  "blog/agentic-ai-workflow-automation-malaysian-business": {
    title: "Agentic AI Malaysia: Automating Workflows for Businesses",
    description:
      "Discover how agentic AI in Malaysia is automating entire business workflows beyond chatbots. Explore practical strategies for businesses and start transforming today.",
    keywords:
      "agentic AI Malaysia, AI workflow automation, AI agents business, agentic AI business, AI automation 2026",
    ogImage: "/blog/agentic-ai-workflow-automation-malaysian-business/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1792,
    ogImageHeight: 1024,
    tags: ["AI Agents", "Automation", "Malaysia Business", "Insights"],
  },
  "blog/ai-pilot-to-production-malaysia-2026": {
    title: "Why AI Pilots Stall in Malaysia (2026)",
    description:
      "Most AI pilots in Malaysia never reach production. Here is why business AI projects stall in 2026, and how agentic AI gets them to scale across real operations.",
    keywords:
      "AI Malaysia, agentic AI Malaysia, AI pilot to production, business AI automation, AI agents for business Malaysia, scaling AI 2026",
    ogImage: "/blog/ai-pilot-to-production-malaysia-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Insights", "AI Agents", "Automation", "Malaysia"],
  },
  "blog/google-io-2026-ai-search-malaysian-businesses": {
    title: "AI Search for Malaysian Businesses After Google I/O 2026",
    description:
      "AI search is changing how Malaysian businesses get found after Google I/O 2026. How to adapt your website, lead funnel, and automation stack.",
    keywords:
      "AI search for Malaysian businesses, Google I/O 2026, AI agents for business, lead generation automation Malaysia, SEO for AI search, Malaysia business AI",
    ogImage: "/blog/google-io-2026-ai-search-malaysian-businesses/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1600,
    ogImageHeight: 1067,
    tags: ["News", "AI", "Malaysia Business", "Automation"],
  },
  "blog/nvidia-gtc-2026-malaysian-businesses": {
    title: "What NVIDIA GTC 2026 Means for Malaysian Businesses",
    description:
      "NVIDIA GTC 2026 revealed major AI breakthroughs for business. Here is what Malaysian business owners need to know and how to act before your competitors do.",
    keywords:
      "AI for Malaysian businesses, AI adoption Malaysia, NVIDIA GTC 2026, agentic AI Malaysia, AI business Malaysia",
    ogImage: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=1792&q=80",
    ogImageType: "image/jpeg",
    ogImageWidth: 1792,
    ogImageHeight: 1024,
    tags: ["Insights", "AI", "Malaysia"],
  },
"blog/whatsapp-ai-agents-malaysia-lead-generation-2026": {
  title: "WhatsApp AI Agents Malaysia for Lead Generation 2026",
  description:
    "WhatsApp AI agents Malaysia can qualify leads, automate follow-up, and speed replies. Learn how your business can deploy them with Anchor Sprint today for growth.",
  keywords:
    "WhatsApp AI agents Malaysia, WhatsApp automation Malaysia, AI lead generation Malaysia, WhatsApp sales automation, customer service AI Malaysia, AI agents for Businesses",
  ogImage: "/blog/whatsapp-ai-agents-malaysia-lead-generation-2026/hero.jpg",
  ogImageType: "image/jpeg",
  ogImageWidth: 1792,
  ogImageHeight: 1024,
  tags: ["WhatsApp", "AI Agents", "Malaysia business", "Automation"],
},
  "blog/microsoft-build-2026-ai-agents-malaysian-businesses": {
  title: "AI Agents for Malaysian Businesses After Microsoft Build",
  description: "Microsoft Build 2026 puts AI agents into everyday tools. Learn what Malaysian businesses should automate in the next 90 days, then book your strategy call today.",
  keywords:
    "AI agents for Malaysian businesses, Microsoft Build 2026, Copilot Studio automation, lead generation automation Malaysia, business process automation Malaysia, Microsoft AI agents",
  ogImage: "/blog/microsoft-build-2026-ai-agents-malaysian-businesses/hero.jpg",
  ogImageType: "image/jpeg",
  ogImageWidth: 1792,
  ogImageHeight: 1024,
  tags: ["News", "AI", "AI Agents", "Malaysia business", "Automation"],
},

  "blog/openai-dots-chatgpt-agents-malaysia": {
    // Published 2026-10-08. Keyword ownership (one keyword, one page): this post owns
    // "OpenAI dots", "ChatGPT dots", "dots agent", "always-on AI agent" and their
    // Malaysia/Singapore variants. It must NOT target "AI agent Malaysia" or "AI agent
    // development Malaysia" (services/ai-agents and blog/ai-agent-development-malaysia-2026
    // own them); it links to services/ai-agents instead. Plan prices are owned by
    // guides/chatgpt-plans-malaysia. Research: docs/research/openai-dots-2026-10-08.md
    // (prices and the first-month usage terms decay: re-check by 2026-11-01).
    title: "OpenAI Dots in Malaysia: Plans, Prices and a PDPA Checklist",
    description:
      "OpenAI dots are always-on ChatGPT agents with their own cloud computer. Which plans include them in Malaysia and Singapore, the controls, and a PDPA checklist.",
    keywords:
      "OpenAI dots, ChatGPT dots, dots agent, always-on AI agent, OpenAI dots Malaysia, ChatGPT dots Singapore, ChatGPT always-on agent, OpenAI dots plans, OpenAI dots PDPA",
    ogImage: "/blog/openai-dots-chatgpt-agents-malaysia/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["AI Agents", "ChatGPT", "Malaysia Business", "Singapore"],
  },

  "blog/malaysia-ai-tax-deduction-automation-roadmap-2026": {
    title: "AI Tax Deduction Malaysia 2026: Business Automation Roadmap",
    description:
      "AI tax deduction Malaysia can cut training costs, but real value comes from turning one workflow into automation. See the business roadmap and book a free consult.",
    keywords:
      "AI tax deduction Malaysia, Malaysia business automation, AI training tax deduction, Budget 2026 AI Malaysia, AI adoption roadmap Malaysia, digital transformation Malaysia business",
    ogImage: "/blog/malaysia-ai-tax-deduction-automation-roadmap-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1792,
    ogImageHeight: 1024,
    tags: ["Automation", "AI", "Malaysia business", "Insights"],
  },

  "blog/malaysia-ai-grants-2026-real-automation": {
    title: "AI Grant Malaysia 2026: From Funding to Automation",
    description:
      "AI grant Malaysia options are opening in 2026. Learn how to turn grants, tax relief, and advisory support into one practical automation project with us today.",
    keywords:
      "AI grant Malaysia, Malaysia AI grants 2026, digitalisation grant Malaysia, AI automation Malaysia, MDAG-AI, Malaysia business automation",
    ogImage: "/blog/malaysia-ai-grants-2026-real-automation/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1792,
    ogImageHeight: 1024,
    tags: ["Automation", "AI", "Malaysia business", "Insights"],
  },
  "blog/malaysia-q4-ai-sales-automation-2026": {
    title: "AI Sales Automation Malaysia for the 11.11 to 12.12 Rush",
    description:
      "AI sales automation Malaysia helps you handle 11.11 to 12.12 WhatsApp lead spikes, speed follow-up, and keep sales moving through Q4. See what to automate now.",
    keywords:
      "AI sales automation Malaysia, Malaysian business automation, WhatsApp AI automation, AI lead follow-up Malaysia, 11.11 sales automation Malaysia",
    ogImage: "/blog/malaysia-q4-ai-sales-automation-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Automation", "AI", "WhatsApp", "Malaysia business"],
  },
  "blog/budget-2027-ai-readiness-malaysian-businesses": {
    title: "Budget 2027 AI Malaysia: Business Readiness Guide",
    description:
      "Budget 2027 backs AI adoption and skills in Malaysia. Use this 10-point readiness check and RM return method to get one AI project ready before scheme terms are published.",
    keywords:
      "Budget 2027 AI Malaysia, AI grants Malaysia 2027, Budget 2027 AI business, AI readiness Malaysia, business automation Malaysia",
    ogImage: "/blog/budget-2027-ai-readiness-malaysian-businesses/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["News", "AI", "Malaysia Business"],
  },
  "blog/malaysia-ai-push-2026-business-automation-priorities": {
    title: "Malaysia's AI Push 2026: What Businesses Should Automate First",
    description:
      "Malaysia's national AI agenda is moving fast. Here is what Malaysian businesses should automate first, where the grants fit, and how to start with one process.",
    keywords:
      "Malaysia AI push 2026, AI automation Malaysia, what to automate first Malaysia, business automation Malaysia, national AI agenda Malaysia, MDAG AI",
    ogImage: "/blog/malaysia-ai-push-2026-business-automation-priorities/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["AI", "Automation", "Malaysia"],
  },
  "blog/malaysia-einvoice-ai-workflow-2026": {
    title: "How Malaysian Businesses Should Automate e-Invoice Workflows",
    description:
      "A practical blueprint for a MyInvois-ready, AI-assisted e-Invoice workflow: capture, validation, submission and the exceptions queue that keeps humans in the loop.",
    keywords:
      "MyInvois automation, e-Invoice automation Malaysia, MyInvois API workflow, e-invoice AI Malaysia, LHDN e-invoice automation, invoice capture Malaysia",
    ogImage: "/blog/malaysia-einvoice-ai-workflow-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["AI", "Automation", "Malaysia"],
  },
  "blog/ai-malaysia-takeover-2026-automation-playbook": {
    title: "AI Automation Malaysia: What Businesses Should Do Before Q4",
    description:
      "AI automation Malaysia is moving fast. Learn what Malaysian businesses should automate before Q4, where MDAG-AI fits, and book your practical strategy call today.",
    keywords:
      "AI automation Malaysia, Malaysian businesses AI, workflow automation Malaysia, AI grants Malaysia, MDAG-AI",
    ogImage: "/blog/ai-malaysia-takeover-2026-automation-playbook/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1792,
    ogImageHeight: 1024,
    tags: ["AI", "Automation", "Malaysia business", "News"],
  },



  "blog/nvidia-rtx-spark-local-ai-malaysian-businesses": {
    title:
      "NVIDIA RTX Spark: Local AI or Cloud for Malaysian Business?",
    description:
      "NVIDIA's RTX Spark puts a petaflop of AI on an office desk. The honest local-vs-cloud decision guide for Malaysian businesses, including costs and PDPA.",
    keywords:
      "NVIDIA RTX Spark Malaysia, local AI for business Malaysia, run AI locally business, on-premise AI Malaysia, AI PC business Malaysia 2026, cloud AI vs local AI cost, PDPA AI Malaysia",
    ogImage: "/blog/nvidia-rtx-spark-local-ai-malaysian-businesses/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["News", "AI", "Malaysia Business"],
  },
  "blog/chatgpt-vs-claude-vs-gemini-malaysia-business-2026": {
    title:
      "ChatGPT vs Claude vs Gemini for Malaysian Businesses: The Difference That Actually Matters",
    description:
      "ChatGPT vs Claude vs Gemini for Malaysian business: the business-lens answer on data/PDPA, ecosystem fit, cost in MYR, and task — not benchmarks.",
    keywords:
      "ChatGPT vs Claude vs Gemini, Claude vs ChatGPT Malaysia, best AI for business Malaysia, ChatGPT vs Gemini Malaysia, which AI for business, AI comparison Malaysia, ChatGPT Claude Gemini difference, enterprise AI Malaysia, AI for Malaysian business, best LLM for business Malaysia",
    ogImage: "/blog/chatgpt-vs-claude-vs-gemini-malaysia-business-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Insights", "AI", "Malaysia"],
  },
  "blog/ai-seo-vs-traditional-seo-malaysia": {
    // Rewritten 2026-09-23 to agree with the repositioned AI SEO service page
    // (Google + AI answers, AI visibility tracking on every plan). The 2026-08-22
    // version argued against paying for AEO/GEO and quoted retired RM699/RM1,499
    // prices. Keyword ownership (one keyword, one page): this post owns the
    // comparison and definition terms ("AI SEO vs traditional SEO", "GEO vs SEO",
    // "AEO vs SEO", "what is GEO/AEO"). "AI SEO Malaysia" / "AI SEO agency|company
    // Malaysia" belong to services/seo-services-malaysia, "AI SEO Kuala Lumpur" / KL to
    // services/ai-seo-kuala-lumpur,
    // "AI SEO agent" to blog/ai-seo-agent-malaysia, price terms to
    // blog/seo-price-malaysia-2026. Research: docs/research/ai-seo-vs-traditional.md.
    title: "AI SEO vs Traditional SEO: What GEO and AEO Change",
    description:
      "AI SEO vs traditional SEO: what GEO and AEO mean, what Google says you don't need, why AI answers can name firms that don't rank, and what to ask a provider.",
    keywords:
      "AI SEO vs traditional SEO, AI SEO vs SEO, GEO vs SEO, AEO vs SEO, what is GEO, what is AEO, generative engine optimisation, answer engine optimisation, GEO vs AEO, traditional SEO vs AI search",
    ogImage: "/blog/ai-seo-vs-traditional-seo-malaysia/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["SEO", "AI", "Malaysia Business"],
  },
  "blog/psg-seo-singapore-edge-grant": {
    // Published 2026-10-07. Facts: docs/research/psg-edge-seo-singapore-2026-10-07.md.
    // Keyword ownership (one keyword, one page): owns the Singapore grant/informational
    // terms ("PSG SEO package", "PSG digital marketing grant", "EDGE grant SEO", "EDGE
    // grant digital marketing"). It must NOT target the SG commercial or price terms
    // ("AI SEO Singapore", "SEO agency Singapore", "AI SEO Singapore cost/price"), which
    // singapore/ai-seo owns and this post links to. No prices on the page. Never claim we
    // qualify for PSG, EDGE or any grant.
    title: "PSG SEO Package Ended: Does EDGE Cover SEO? (Singapore 2026)",
    description:
      "PSG ceased on 29 Sep 2026. What that means for PSG SEO packages, what the new EDGE Grant does and does not cover, and what to ask an SEO provider now.",
    keywords:
      "PSG SEO package, PSG SEO, PSG digital marketing grant, PSG digital marketing package, PSG ended, EDGE grant SEO, EDGE grant digital marketing, EDGE grant Singapore, Productivity Solutions Grant SEO",
    ogImage: "/blog/psg-seo-singapore-edge-grant/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
    tags: ["SEO", "Singapore", "News"],
  },
  "blog/get-recommended-by-chatgpt-google-ai-overviews": {
    // Published 2026-09-24 (owner override of the 90 gate; last scored A 85 / B 77).
    // Keyword ownership (one keyword, one page): this post owns the how-to terms for
    // being named in AI answers ("how to get recommended by ChatGPT", "how to rank on
    // ChatGPT", "get into Google AI Overviews", "rank in AI Overview", "ChatGPT
    // recommend my business"). It must NOT target the Singapore commercial terms
    // ("AI SEO Singapore", "ChatGPT SEO Singapore", "Google AI Overviews SEO
    // Singapore", "AI visibility Singapore"), which singapore/ai-seo owns and this
    // post links to (2026-10-07), nor "what is GEO/AEO"
    // or "GEO vs SEO" (blog/ai-seo-vs-traditional-seo-malaysia), "AI SEO Malaysia" /
    // "AI SEO agency Malaysia" (services/seo-services-malaysia), "AI SEO agent"
    // (blog/ai-seo-agent-malaysia) or SEO price terms (blog/seo-price-malaysia-2026).
    // Research: docs/research/ai-answer-recommendation-how-to.md.
    // Title/description rewritten 2026-10-04 for CTR (p6-8, ~15 impr/day, 0 clicks
    // 2026-09-20 to 10-04): leads with the "ranks on Google, missing from AI
    // answers" problem buyers type. Was "How to Get Recommended by ChatGPT & AI
    // Overviews (MY & SG)". The on-page H1 (defineBlogPost title) is unchanged.
    title: "Rank on Google but Not in ChatGPT? How to Get Recommended",
    description:
      "Ranking on Google but missing from ChatGPT and AI Overviews? Six steps for businesses in Malaysia and Singapore, and a monthly check of who AI names.",
    keywords:
      "how to get recommended by ChatGPT, how to rank on ChatGPT, ChatGPT recommend my business, how to get into Google AI Overviews, rank in AI Overview, get mentioned by ChatGPT, OAI-SearchBot robots.txt",
    ogImage: "/blog/get-recommended-by-chatgpt-google-ai-overviews/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["SEO", "AI", "Malaysia Business", "Singapore"],
  },
  "blog/ai-quality-inspection-malaysia": {
    // SERP check 2026-08-27 (gl=my, logged out): "ai quality inspection
    // manufacturing malaysia" has one government result, NO AI Overview, and
    // explainer content ranking — but page one is VISION-SYSTEM AND CAMERA
    // SUPPLIERS. So the article deliberately does not compete on hardware; it
    // competes on the nonconformity paperwork after the camera fires, which is
    // integration and document work and is what we actually build.
    title: "AI Quality Inspection Malaysia: The Camera Is the Easy Part",
    description:
      "Most factories solve defect detection and leave the paperwork manual. What a nonconformity record must contain, why they fail audits, and where AI genuinely helps.",
    keywords:
      "AI quality inspection Malaysia, quality inspection automation Malaysia, nonconformity report NCR, defect tracking manufacturing Malaysia, ISO 9001 nonconformity record, corrective action manufacturing Malaysia, machine vision inspection Malaysia, quality management factory Malaysia, audit trail manufacturing defects",
    ogImage: "/blog/ai-quality-inspection-malaysia/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Manufacturing", "AI", "Malaysia Business"],
  },
  "blog/predictive-maintenance-malaysia": {
    // Targets the WORKFLOW term, not the umbrella term. SERP check 2026-08-23
    // (gl=my, logged out): "predictive maintenance malaysia" has ZERO government
    // results, NO AI Overview, and blog content ranking on page one — while
    // "smart manufacturing malaysia" is SIRIM/MIDA/MIMOS/Smart4wrd/MSMA and is
    // not winnable. See docs/analytics/articles/2026-W34-use-case-guide-plan.md.
    // Also supplies /services/smart-manufacturing/ with an in-content inbound
    // link, which it had none of from the blog.
    title: "Predictive Maintenance Malaysia: Real Savings, Real Costs (2026)",
    description:
      "What predictive maintenance actually saves against preventive maintenance, the costs vendors leave out, and how to pick the first machines — with sourced figures, not vendor claims.",
    keywords:
      "predictive maintenance Malaysia, predictive maintenance cost, condition based maintenance Malaysia, factory downtime Malaysia, machine condition monitoring Malaysia, IoT predictive maintenance Malaysia, preventive vs predictive maintenance, smart manufacturing Malaysia, reliability centred maintenance, unplanned downtime manufacturing",
    ogImage: "/blog/predictive-maintenance-malaysia/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Manufacturing", "AI", "Malaysia Business"],
  },
  "blog/ai-agent-cost-malaysia": {
    // Two jobs. (1) Catch the price-shaped agent queries where we already sit
    // p5-8 but with tiny volume: "claude agent price" p8.5, "claude ai agent
    // price" p7.5, "how much is claude ai agent" p5.0, "ai agents costing
    // malaysia" p23.0. (2) Give the Claude PRICING cluster (~3,000 impressions
    // at p1-7) a commercial destination — that cluster produced 147 sessions
    // and ZERO conversions for three weeks running.
    title: "What an AI Agent Costs to Build and Run in Malaysia (2026)",
    description:
      "Real numbers for the three costs of an AI agent in Malaysia — the token bill, the build, and the running cost nobody quotes. With a worked example in Ringgit.",
    keywords:
      "AI agent cost Malaysia, AI agents costing Malaysia, Claude AI agent price, Claude agent price, how much is Claude AI agent, AI agent price Malaysia, AI agent development cost Malaysia, Claude API cost per million tokens, prompt caching cost, Batch API 50% off, AI agent running cost, AI agent maintenance cost Malaysia",
    ogImage: "/blog/ai-agent-cost-malaysia/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["AI Agents", "Insights", "Malaysia Business"],
  },
  "blog/how-to-choose-ai-agency-malaysia": {
    // MY agency cluster: 424 impressions across 27 queries, ZERO clicks.
    // "ai agency malaysia" 171i p19.9 is the anchor; "ai automation agency
    // malaysia" 59i p11.3 is the striking-distance term. The long-tail shows
    // what buyers actually worry about — lock-in contracts, whether the agency
    // also trains the team, integration — so the article is built on those.
    // KEYWORD OWNERSHIP (2026-09-28): "AI agency Malaysia" and "AI automation
    // agency Malaysia" removed — both owned by /services/ai-solutions/. This
    // post keeps the decision-stage question/long-tail forms instead.
    title:
      "How to Choose an AI Agency in Malaysia: 7 Questions Before You Sign",
    description:
      "How to evaluate an AI agency in Malaysia — ask for the Claude Certified Architect credential, then the six other questions that reveal who can actually deliver.",
    keywords:
      "how to choose an AI agency in Malaysia, AI automation consultant Malaysia, AI consulting services Malaysia, AI implementation partner Malaysia, choose AI agency Malaysia, best AI agency Malaysia, AI integration agency Malaysia, AI agency without lock-in contract, questions to ask an AI agency, hire AI agency Kuala Lumpur, AI consultancy Malaysia, AI development company Malaysia, Claude Certified Architect Malaysia, Claude Partner Network Malaysia, certified AI agent developer Malaysia",
    ogImage: "/blog/how-to-choose-ai-agency-malaysia/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["AI", "Insights", "Malaysia Business"],
  },
  "blog/claude-ai-training-malaysia-cost-guide": {
    // The MY training cluster is 240 impressions across 51 queries at ZERO
    // clicks. /services/training-education/ already owns the "approved
    // provider" intent (147i); this post deliberately targets the DECISION
    // intent it does not serve — what does it cost, who goes first, how much
    // does the levy pay back — so the two do not cannibalise each other.
    // "ai corporate training malaysia" sits at p73.5, a plainly commercial
    // term we rank nowhere for.
    title:
      "Claude AI Training Malaysia: Cost, Who to Train First & HRD Corp Claim",
    description:
      "What Claude AI training costs a Malaysian team, which department to send first, and how much of it the HRD Corp levy pays back — from an HRD Corp approved provider.",
    keywords:
      "Claude AI training Malaysia, AI corporate training Malaysia, HRD Corp claimable AI training, HRD Corp claimable AI productivity workshop Malaysia, AI training for finance teams Malaysia, AI training Kuala Lumpur, AI training Petaling Jaya, AI training Penang, corporate AI training Malaysia cost, HRDC claimable AI training provider, Claude training Malaysia price, in-house AI training Malaysia",
    ogImage: "/blog/claude-ai-training-malaysia-cost-guide/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["AI", "Claude", "Malaysia Business"],
  },
  "blog/ai-training-didnt-work-malaysia": {
    // KEYWORD DECISION (2026-09-01) — this post has NO head term available, and
    // that is deliberate rather than a gap. Every adjacent head term was checked
    // on a live logged-out MY SERP and rejected:
    //   - "why ai training fails" — 2 impressions in 90 days, AI-Overview-capped,
    //     SERP owned by HR Dive / CIO / eLearning Industry. No MY intent.
    //   - "ai adoption malaysia" — AI-Overview-capped AND held by government
    //     domains (National AI Office, MDEC, Kementerian Digital). It is a
    //     statistics/policy query, not a buyer query.
    //   - the digital-transformation cluster (2,045 impr) — ALREADY OURS via
    //     /services/digital-transformation/, which is the #1 organic result for
    //     "digital transformation consultants kl", above EY. Retitling this post
    //     at that cluster would repeat the "claude partner malaysia" self-
    //     cannibalisation. Do not.
    //   - "ai adoption" — already ours via /blog/ai-adoption-roadmap-malaysian-smes/
    //     at p7.5.
    // So this targets the POST-FAILURE LONG TAIL instead, which nothing of ours
    // serves and no AI Overview answers: conversational "we trained and nothing
    // changed" phrasing. That matches where this domain actually wins — our
    // question-shaped queries sit at p2-8 ("ai implementation partner in
    // malaysia that also trains our team" p5.7). Its second job is as an
    // internal-link asset pointing at /services/digital-transformation/.
    //
    // GEO RULE (Jazz, 2026-09-01): every keyword here carries Malaysia intent.
    // A geo-LESS long tail puts us straight back against HR Dive and CIO on
    // their own turf, which is the same trap that cost us "claude partner
    // malaysia" — geo-modified queries are a different result set, and the
    // unmodified variant is not a cheaper route into it. The one term
    // deliberately EXCLUDED is "digital transformation Malaysia": that belongs
    // to /services/digital-transformation/ and we link to it instead.
    title: "AI Training Didn't Work? A 5-Symptom Diagnosis for Malaysia",
    description:
      "Your team did the AI training and nothing changed. A Malaysian business diagnosis: five symptoms, five different causes, and why a second course rarely fixes it.",
    keywords:
      "AI training Malaysia not working, AI adoption failure Malaysia, why AI projects fail Malaysia, AI training didn't work Malaysia, we trained our team on AI but nothing changed Malaysia, AI adoption stalled Malaysia, AI pilot failed Malaysia, HRD Corp AI training outcome, workflow redesign Malaysia business, AI change management Malaysia, measuring AI adoption Malaysia, AI implementation Malaysia business, what to do after AI training Malaysia",
    ogImage: "/blog/ai-training-didnt-work-malaysia/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Insights", "AI", "Malaysia Business"],
  },
  "blog/claude-ai-pricing-malaysia-2026": {
    // This cluster is 4,638 impressions at 0.8% CTR — the AI Overview answers
    // "how much does Claude cost" before anyone scrolls. The title was 107
    // characters and promised information; it now promises the calculator,
    // which is the one thing an AI Overview cannot deliver in the SERP.
    title: "Claude Pricing Malaysia 2026: RM Costs + Cost Calculator",
    description:
      "Claude pricing in Malaysia 2026 — every plan in RM and SGD, API token rates, and a free calculator that works out your team's real 3-year cost as an Excel.",
    keywords:
      "Claude pricing Malaysia, Claude AI price Malaysia, Claude Malaysia price, Claude price in Malaysia, Claude API cost Malaysia, Anthropic Claude Malaysia pricing, Claude enterprise Malaysia, is Claude available in Malaysia, does Claude work in Malaysia, Claude plan Malaysia, Claude pricing Singapore, Claude AI price Singapore, Claude cost Singapore SGD",
    ogImage: "/blog/claude-ai-pricing-malaysia-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Insights", "AI", "Malaysia Business"],
    // Per-locale researched terms, not a translation of the English list —
    // Malay searchers type "harga", Chinese searchers type "价格"/"收费".
    localized: {
      ms: {
        title: "Harga Claude AI Malaysia 2026: Kos RM + Kalkulator Kos",
        description:
          "Harga Claude AI di Malaysia 2026 — setiap pelan dalam RM dan SGD, kadar token API, dan kalkulator percuma yang mengira kos tiga tahun sebenar pasukan anda.",
        keywords:
          "harga Claude AI Malaysia, harga Claude Malaysia, kos Claude Malaysia, harga langganan Claude, berapa harga Claude, Claude Pro harga Malaysia, harga API Claude, kalkulator kos Claude, harga Claude Team, Claude Enterprise harga Malaysia",
      },
      zh: {
        title: "Claude 价格马来西亚 2026：令吉成本与成本计算器",
        description:
          "2026 年 Claude 在马来西亚与新加坡的价格——各档方案的 RM 与 SGD 金额、API token 费率，以及一个算出你团队三年真实成本的免费计算器。",
        keywords:
          "Claude 价格 马来西亚, Claude 收费, Claude 马来西亚 价格, Claude 订阅价格, Claude Pro 价格, Claude API 价格, Claude 企业版 价格, Claude 成本计算器, Claude 多少钱, Claude 新加坡 价格",
      },
    },
  },
  "blog/credit-evaluation-agent-malaysia": {
    title:
      "Credit Evaluation Agent: How AI Automates Loan Document Review for Malaysian Lenders (2026)",
    description:
      "What a credit evaluation agent is, how it reads and cross-checks loan documents, where the human stays in control, and how Malaysian lenders deploy one compliantly.",
    keywords:
      "credit evaluation agent, credit evaluation automation Malaysia, loan document review AI, AI credit assessment Malaysia, automated document verification lending, credit underwriting automation Malaysia, AI agent for lenders, document matching AI Malaysia",
    ogImage: "/blog/credit-evaluation-agent-malaysia/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Insights", "AI", "Malaysia"],
  },
  "blog/ai-agent-development-malaysia-2026": {
    // KEYWORD OWNERSHIP (2026-09-28): "AI agent Malaysia" and "AI automation
    // agency Malaysia" removed — both owned by /services/ai-agents/. This post
    // keeps its cost/how-to long-tail instead.
    title:
      "AI Agent Development in Malaysia: What It Is, What It Costs & How to Get One Built (2026)",
    description:
      "A plain-English guide to custom AI agent development in Malaysia: what agents do, when you need one, real Ringgit costs, and how a document-matching agent works.",
    keywords:
      "AI agent development Malaysia, AI agent solution Malaysia, custom AI agent Malaysia, AI agent provider Malaysia, build AI agent Malaysia, document comparison AI",
    ogImage: "/blog/ai-agent-development-malaysia-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Insights", "AI", "Malaysia"],
  },
  "blog/ai-corporate-communications-consistency": {
    title:
      "AI for Corporate Communications: Keeping One Message Consistent Across Languages",
    description:
      "Where AI for corporate communications helps: keeping one message on-brand and fact-consistent across English, Malay and Chinese, for listed multi-region firms.",
    keywords:
      "AI corporate communications, multilingual investor relations, brand consistency across languages, AI translation consistency, IR disclosure consistency Malaysia, corporate communications AI Singapore, multilingual press release AI, Bursa Malaysia disclosure, SGX disclosure, corporate communications agent",
    ogImage: "/blog/ai-corporate-communications-consistency/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Insights", "AI", "Malaysia"],
  },
  "blog/shipping-fast-shipping-your-keys-malaysia": {
    title: "Hardcoded API Keys in Malaysia: What PDPA Actually Requires",
    description:
      "One operation decompiled 1.8 million Android apps hunting hardcoded secrets. Malaysia's breach-notification duty started 1 June 2025; the guideline sets 72 hours.",
    keywords:
      "hardcoded API key Malaysia, exposed API key data breach, PDPA data breach notification, PDPA 72 hours, AI generated code security Malaysia, leaked credentials Malaysia, secrets in code, Section 12B PDPA",
    ogImage: "/blog/shipping-fast-shipping-your-keys-malaysia/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Insights", "Security", "Malaysia"],
  },
  "blog/ai-agent-rescue-fix-broken-agent-malaysia": {
    title: "Fix a Broken AI Agent: How to Tell If It's Salvageable",
    description:
      "A field guide to fixing a broken AI agent: why they fail after the demo, the rectify-vs-rebuild decision, and how to tell if your agent can be saved.",
    keywords:
      "fix AI agent, AI agent not working, broken AI agent, rescue AI agent, AI agent rectify or rebuild, failed AI agent project Malaysia, AI agent audit, fix my AI agent",
    ogImage: "/blog/ai-agent-rescue-fix-broken-agent-malaysia/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Insights", "AI", "Malaysia"],
  },
  "blog/claude-opus-4-8-malaysian-businesses": {
    title: "Claude Opus 4.8 for Malaysian Businesses: Is It Worth Paying For?",
    description:
      "Claude Opus 4.8 is better and cheaper than before, but not for every task. Here is how Malaysian businesses should decide when premium AI is worth paying for.",
    keywords:
      "Claude Opus 4.8 Malaysia, premium AI model Malaysia, Anthropic Claude Malaysia, AI model pricing Malaysia, AI solutions Malaysia business",
    ogImage: "/blog/claude-opus-4-8-malaysian-businesses/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1792,
    ogImageHeight: 1024,
    tags: ["News", "AI", "Malaysia Business"],
  },
  "blog/claude-opus-5-malaysia-business": {
    title: "Claude Opus 5 Is Here: What It Changes for Malaysian Businesses",
    description:
      "Claude Opus 5 launched 24 July 2026 at Opus 4.8 prices — the real story is cost per completed task. What Malaysian and Singapore teams should test first.",
    keywords:
      "Claude Opus 5, Claude Opus 5 Malaysia, Claude Opus 5 release, Claude Opus 5 pricing, Claude Opus 5 vs Opus 4.8, Claude Opus 5 cost Ringgit, Claude Opus 5 benchmarks, Claude Opus 5 agents, Claude Opus 5 computer use, what is Claude Opus 5, Claude Opus 5 for business, AI for business Malaysia, AI for business Singapore, Anthropic Claude Malaysia, Claude API Malaysia, agentic AI Malaysia, AI agent Malaysia, premium AI model Malaysia",
    ogImage: "/blog/claude-opus-5-malaysia-business/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["News", "AI", "Malaysia Business"],
  },
  "blog/claude-sonnet-5-malaysia-business": {
    title:
      "Claude Sonnet 5 for Malaysian Businesses: Cost, Deployment & Agentic Use Cases",
    description:
      "Claude Sonnet 5 brings near-Opus reasoning at Sonnet prices. What it means for Malaysian businesses: RM cost, deployment options, and agentic AI use cases.",
    keywords:
      "Claude Sonnet 5, Claude Sonnet 5 Malaysia, Claude Sonnet 5 release, Claude Sonnet 5 pricing, Claude Sonnet 5 cost Ringgit, Claude Sonnet 5 benchmarks, Claude Sonnet 5 vs Opus 4.8, Claude Sonnet 5 deployment, what is Claude Sonnet 5, Claude Sonnet 5 agentic AI, agentic AI Malaysia, AI agent Malaysia, Anthropic Claude Malaysia, Claude API Malaysia, Claude Sonnet 5 Bedrock Vertex Foundry",
    ogImage: "/blog/claude-sonnet-5-malaysia-business/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    tags: ["Insights", "AI", "Malaysia Business"],
    localized: {
      ms: {
        title: "Claude Sonnet 5 untuk Perniagaan Malaysia",
        description:
          "Sonnet 5 pada US$2 / US$10 setiap juta token — kini kadar standard, dan lebih murah daripada Sonnet 4.6. Apa maknanya untuk beban kerja perniagaan Malaysia dan Singapura.",
        keywords:
          "Claude Sonnet 5 Malaysia, harga Claude Sonnet 5, Sonnet 5 perniagaan, kos API Claude Malaysia, model AI untuk perniagaan Malaysia",
      },
      zh: {
        title: "面向马来西亚企业的 Claude Sonnet 5",
        description:
          "Sonnet 5 每百万 token 收费 2/10 美元，现已成为标准价，且比 Sonnet 4.6 更便宜。这对马来西亚与新加坡企业的实际工作负载意味着什么。",
        keywords:
          "Claude Sonnet 5 马来西亚, Claude Sonnet 5 价格, Sonnet 5 企业, Claude API 成本 马来西亚, 马来西亚企业 AI 模型",
      },
    },
  },
  "blog/malaysia-ai-action-plan-business-guide-2026": {
    title: "Malaysia AI Action Plan 2026-2030: Business Action Guide",
    description:
      "Malaysia AI Action Plan 2026-2030 gives businesses a clear direction. Learn 90-day steps for governed AI adoption, measurable ROI and safer rollout.",
    keywords:
      "Malaysia AI Action Plan 2026-2030, AI for Malaysian businesses, Malaysia AI Nation 2030, AI adoption Malaysia business, AI governance Malaysia, business AI automation",
    ogImage: "/blog/malaysia-ai-action-plan-business-guide-2026/hero.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1792,
    ogImageHeight: 1024,
    tags: ["News", "AI", "Malaysia Business", "Automation"],
  },
};

// AI Governance Kit template previews: one English-only page per public/free
// document, generated from src/content/ai-governance/kit.ts.
for (const doc of agkPreviewDocs) {
  metaConfigs[`ai-governance/templates/${doc.slug}`] = {
    title: doc.seoTitle,
    description: doc.seoDescription,
    keywords: doc.keywords.join(", "),
    ogImage: "/images/ai-governance/og.jpg",
    ogImageType: "image/jpeg",
    ogImageWidth: 1200,
    ogImageHeight: 630,
  };
}

// Careers role pages: one English-only page per open role, generated from
// src/content/careers.ts.
for (const role of careerRoles) {
  metaConfigs[`careers/${role.slug}`] = {
    title: role.seo.title,
    description: role.seo.description,
    keywords: role.seo.keywords,
    ogImage: role.image,
    ogImageType: "image/jpeg",
    ogImageWidth: 1024,
    ogImageHeight: 1024,
  };
}

// Helper function to get meta config for a specific route
export const getMetaConfig = (
  route: string,
  locale: Locale = defaultLocale,
): MetaConfig => {
  // Remove leading slash if present
  const cleanRoute = route.startsWith("/") ? route.slice(1) : route;

  // Return the meta config for the route, or fallback to home if not found
  const metaConfig = metaConfigs[cleanRoute] || metaConfigs.home;
  const localizedConfig = metaConfig.localized?.[locale];

  return localizedConfig
    ? {
        ...metaConfig,
        ...localizedConfig,
      }
    : metaConfig;
};

// Returns true if the given route has a localized meta config for the given locale.
// Used by generatePageMetadata to determine whether a blog post has a translated variant
// (so we can serve localized metadata + locale-specific canonical) or whether it's
// English-only at this URL (in which case we keep canonical pointing to the EN URL
// to avoid duplicate-content SEO issues).
export const hasLocalizedMetaConfig = (
  route: string,
  locale: Locale,
): boolean => {
  const cleanRoute = route.startsWith("/") ? route.slice(1) : route;
  const metaConfig = metaConfigs[cleanRoute];
  return Boolean(metaConfig?.localized?.[locale]);


};

// Returns only the route's `localized[locale]` block (no English fallback), or
// undefined when the route has none. Used to localize WebPage JSON-LD name and
// description on /ms/ and /zh/ — absent fields stay English there.
export const getLocalizedMetaBlock = (
  route: string,
  locale: Locale,
): LocalizedMetaConfig | undefined => {
  const cleanRoute = route.replace(/^\/+|\/+$/g, "") || "home";
  return metaConfigs[cleanRoute]?.localized?.[locale];
};
